import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Download,
  Eye,
  X,
  Check,
  Ban,
  Send,
  DollarSign,
  Building2,
  Calendar,
  Percent,
  TrendingUp,
  Shield,
  FileCheck,
  AlertTriangle,
  Edit3,
  Search,
  Activity,
  Zap,
  Sparkles,
  Bot,
  BookOpen,
  Database,
  Ticket,
  Plus,
  Trash2,
  FileSearch
} from 'lucide-react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { agreementReviewPath } from '../utils/loanNavigation';
import { REVIEWABLE_DOCUMENT_TYPES, documentWord } from '../features/credit/agreementReview/agreementReviewModel';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { type CatalogEntry, getCatalog } from '../features/documentTemplates/documentTemplatesApi';
import TemplateReadBanner, { type TemplateValidation } from '../features/documentTemplates/TemplateReadBanner';
import { useIsFeatureEnabled } from '../contexts/TenantConfigContext';
import { AxiomButton, AxiomMetricCard, AxiomMetricGrid } from './shared';
import { leveragedLoanTicketPath, loan360Path, securityMasterPath } from '../utils/loanNavigation';
import { getFacilityVersionWorkQueue } from '../features/abf/api/abfApi';
import { useToast } from './shared/Toast';
import { useConfirmation } from './shared/ConfirmationModal';
import {
  ImportOutcome, ImportOutcomePanel, PortfolioChoice, PortfolioPickerDialog, ReasonDialog,
} from './documentProcessing/ImportDialogs';
import { getTenantToday } from '../utils/dateUtils';
import { payingAgentStatementChecks } from '../features/abf/documents/payingAgentChecks';
import { landedCollectionsPeriod, rememberCollectionsPeriod } from '../features/abf/collections/collectionsPeriodMemory';

// ============== Animations ==============
const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// Styled Components
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const Card = styled(GlassCard)`
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #e2e8f0;
  margin-bottom: 0.5rem;
`;

const FileInputWrapper = styled.div<{ $dragging?: boolean; $hasFiles?: boolean }>`
  border: 2px dashed ${props => props.$dragging
    ? 'rgba(56, 189, 248, 0.9)'
    : props.$hasFiles
      ? 'rgba(16, 185, 129, 0.46)'
      : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 12px;
  padding: 1.35rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$dragging
    ? 'rgba(14, 116, 144, 0.16)'
    : props.$hasFiles
      ? 'rgba(16, 185, 129, 0.06)'
      : 'rgba(15, 23, 42, 0.24)'};

  &:hover {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.05);
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  cursor: pointer;
  color: #3b82f6;
  font-weight: 500;

  &:hover {
    color: #60a5fa;
  }
`;

const FileInfo = styled.p`
  margin-top: 0.5rem;
  color: #22c55e;
  font-size: 0.875rem;
`;

const SelectedFilesList = styled.div`
  margin-top: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-height: 210px;
  overflow-y: auto;
  padding-right: 0.15rem;
`;

const SelectedFileRow = styled.div`
  border: 1px solid rgba(16, 185, 129, 0.24);
  background: rgba(15, 23, 42, 0.64);
  border-radius: 10px;
  padding: 0.58rem 0.65rem;
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.55rem;
  text-align: left;
`;

const SelectedFileName = styled.div`
  color: #e2e8f0;
  font-size: 0.76rem;
  font-weight: 750;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectedFileMeta = styled.div`
  color: #64748b;
  font-size: 0.66rem;
  margin-top: 0.12rem;
`;

const SelectedFileTypeSelect = styled.select`
  width: 100%;
  margin-top: 0.45rem;
  padding: 0.38rem 0.5rem;
  border: 1px solid rgba(96, 165, 250, 0.32);
  border-radius: 6px;
  background: rgba(2, 6, 23, 0.72);
  color: #dbeafe;
  font-size: 0.69rem;
  outline: none;

  &:focus {
    border-color: #3b82f6;
  }
`;

const RemoveFileButton = styled.button`
  width: 26px;
  height: 26px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.45);
  color: #94a3b8;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.16s ease;

  &:hover {
    color: #fecaca;
    border-color: rgba(248, 113, 113, 0.42);
    background: rgba(127, 29, 29, 0.2);
  }
`;

const UploadHint = styled.div`
  color: #64748b;
  font-size: 0.72rem;
  line-height: 1.45;
  margin-top: 0.45rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ModeButton = styled.button<{ active: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${props => props.active ? '#3b82f6' : 'rgba(255, 255, 255, 0.2)'};
  background: ${props => props.active ? 'rgba(59, 130, 246, 0.2)' : 'transparent'};
  color: ${props => props.active ? '#ffffff' : '#94a3b8'};

  &:hover {
    border-color: #3b82f6;
    color: #ffffff;
  }
`;

const UploadButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  background: ${props => props.disabled ? '#475569' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'};
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }
`;

const TemplatePickerShell = styled.div`
  position: relative;
`;

const TemplateTrigger = styled.button`
  width: 100%;
  border: 1px solid rgba(59, 130, 246, 0.28);
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.14), transparent 34%),
    rgba(15, 23, 42, 0.78);
  border-radius: 12px;
  color: #e2e8f0;
  padding: 0.85rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.16s ease;

  &:hover {
    border-color: rgba(59, 130, 246, 0.58);
    background: rgba(15, 23, 42, 0.92);
  }
`;

const TemplateCategory = styled.div`
  color: #38bdf8;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const TemplateName = styled.div`
  color: #f8fafc;
  margin-top: 0.25rem;
  font-size: 0.9rem;
  font-weight: 800;
`;

const TemplateDescription = styled.div`
  color: #94a3b8;
  margin-top: 0.25rem;
  font-size: 0.72rem;
  line-height: 1.45;
`;

const TemplateBadge = styled.div`
  flex: 0 0 auto;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(2, 6, 23, 0.38);
  border-radius: 999px;
  color: #94a3b8;
  padding: 0.28rem 0.5rem;
  font-size: 0.65rem;
  font-weight: 800;
`;

const TemplatePickerPanel = styled.div`
  margin-top: 0.65rem;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.96);
  box-shadow: 0 20px 60px rgba(2, 6, 23, 0.38);
  overflow: hidden;
`;

const TemplateSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.75rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(2, 6, 23, 0.34);

  svg {
    color: #64748b;
    flex: 0 0 auto;
  }
`;

const TemplateSearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #f8fafc;
  font-size: 0.82rem;

  &::placeholder {
    color: #64748b;
  }
`;

const TemplateCategoryRow = styled.div`
  display: flex;
  gap: 0.4rem;
  overflow-x: auto;
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
`;

const TemplateChip = styled.button<{ $active?: boolean }>`
  border: 1px solid ${p => p.$active ? 'rgba(59, 130, 246, 0.58)' : 'rgba(148, 163, 184, 0.18)'};
  background: ${p => p.$active ? 'rgba(59, 130, 246, 0.2)' : 'rgba(15, 23, 42, 0.58)'};
  color: ${p => p.$active ? '#bfdbfe' : '#94a3b8'};
  border-radius: 999px;
  padding: 0.34rem 0.62rem;
  font-size: 0.68rem;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
`;

const TemplateList = styled.div`
  max-height: 360px;
  overflow-y: auto;
  padding: 0.45rem;
`;

const TemplateOption = styled.button<{ $active?: boolean }>`
  width: 100%;
  border: 1px solid ${p => p.$active ? 'rgba(16, 185, 129, 0.48)' : 'transparent'};
  background: ${p => p.$active ? 'rgba(16, 185, 129, 0.12)' : 'transparent'};
  color: #e2e8f0;
  border-radius: 10px;
  padding: 0.7rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.7rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: rgba(59, 130, 246, 0.1);
  }
`;

const TemplateOptionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: #64748b;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const TemplateEmpty = styled.div`
  color: #64748b;
  padding: 1rem;
  text-align: center;
  font-size: 0.78rem;
`;

const Table = styled.table`
  width: 100%;
  /* Wide enough that the four stage chips fit their column and never run under the row actions. */
  min-width: 1160px;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.8rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #94a3b8;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Td = styled.td`
  padding: 0.78rem 0.75rem;
  font-size: 0.875rem;
  color: #e2e8f0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  vertical-align: middle;
`;

/* The row actions stay in view while the wider table scrolls beneath them. */
const pinnedActions = `
  position: sticky;
  /* The scroller keeps 0.5rem of padding on its right; sit over it so nothing shows past the actions. */
  right: -0.5rem;
  z-index: 1;
  background: #10172a;
  box-shadow: -10px 0 12px -10px rgba(0, 0, 0, 0.6);
`;
const ActionsTh = styled(Th)`${pinnedActions} text-align: right;`;
const ActionsTd = styled(Td)`${pinnedActions}`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  max-width: 100%;
  background: ${props => {
    switch (props.status) {
      case 'COMPLETED': return 'rgba(34, 197, 94, 0.2)';
      case 'FAILED': return 'rgba(239, 68, 68, 0.2)';
      case 'PROCESSING': return 'rgba(59, 130, 246, 0.2)';
      case 'PENDING': return 'rgba(234, 179, 8, 0.2)';
      default: return 'rgba(148, 163, 184, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'COMPLETED': return '#22c55e';
      case 'FAILED': return '#ef4444';
      case 'PROCESSING': return '#3b82f6';
      case 'PENDING': return '#eab308';
      default: return '#94a3b8';
    }
  }};
`;

const ProgressBar = styled.div`
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  flex-shrink: 0;
  width: 100%;
`;

const ProgressFill = styled.div<{ percent: number }>`
  height: 100%;
  width: ${props => Math.min(100, Math.max(0, props.percent || 0))}%;
  background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%);
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const PipelineStepper = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(88px, 1fr));
  gap: 0.35rem;

  @media (max-width: 1350px) {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }
`;

const PipelineStep = styled.div<{ state: 'done' | 'active' | 'pending' | 'failed' }>`
  display: flex;
  align-items: center;
  gap: 0.28rem;
  min-height: 26px;
  min-width: 0;
  padding: 0.28rem 0.45rem;
  border-radius: 6px;
  border: 1px solid ${props => {
    if (props.state === 'done') return 'rgba(16, 185, 129, 0.35)';
    if (props.state === 'active') return 'rgba(59, 130, 246, 0.45)';
    if (props.state === 'failed') return 'rgba(239, 68, 68, 0.45)';
    return 'rgba(148, 163, 184, 0.18)';
  }};
  background: ${props => {
    if (props.state === 'done') return 'rgba(16, 185, 129, 0.10)';
    if (props.state === 'active') return 'rgba(59, 130, 246, 0.14)';
    if (props.state === 'failed') return 'rgba(239, 68, 68, 0.12)';
    return 'rgba(15, 23, 42, 0.35)';
  }};
  color: ${props => {
    if (props.state === 'done') return '#34d399';
    if (props.state === 'active') return '#60a5fa';
    if (props.state === 'failed') return '#f87171';
    return '#64748b';
  }};
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  svg {
    flex: 0 0 auto;
  }
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  min-height: 32px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${props => props.active ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)'};
  background: ${props => props.active ? 'rgba(59, 130, 246, 0.2)' : 'transparent'};
  color: ${props => props.active ? '#ffffff' : '#94a3b8'};

  &:hover {
    border-color: #3b82f6;
  }
`;

const TableScroller = styled.div`
  overflow-x: auto;
  margin: 0 -0.5rem;
  padding: 0 0.5rem;
`;

const StatusStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
`;

const HoldBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: rgba(239, 68, 68, 0.15);
  color: #fca5a5;
  border: 1px solid rgba(239, 68, 68, 0.3);
`;

const DocumentNameCell = styled.div`
  min-width: 0;
  color: #f8fafc;
  font-size: 0.86rem;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DocumentMeta = styled.div`
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProgressCell = styled.div`
  display: grid;
  grid-template-columns: minmax(64px, 1fr) 42px;
  align-items: center;
  gap: 0.55rem;
`;

const ProgressValue = styled.span`
  color: #94a3b8;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.67rem;
  font-weight: 800;
`;

const StageLabel = styled.div`
  margin-top: 0.4rem;
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OperationsCell = styled.div`
  display: flex;
  gap: 0.35rem;
  justify-content: flex-end;
  align-items: center;
  /* Wrap inside the column instead of spilling over the stage chips beside it. */
  flex-wrap: wrap;

  button {
    width: 30px;
    height: 30px;
    display: inline-grid;
    place-items: center;
    flex: 0 0 auto;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #64748b;
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1e293b;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 700px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    color: #ffffff;
  }
`;

const CodeBlock = styled.pre`
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  overflow-x: auto;
  font-size: 0.75rem;
  color: #e2e8f0;
  font-family: 'Monaco', 'Menlo', monospace;
`;

// New styled components for extracted data display
const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
`;

const DataSection = styled.div`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #94a3b8;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.375rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const DataLabel = styled.span`
  font-size: 0.75rem;
  color: #64748b;
`;

const DataValue = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #e2e8f0;
`;

const DataValueHighlight = styled(DataValue)`
  color: #22c55e;
  font-size: 1rem;
`;

/** Inline editor for extracted-loan modal (edit-before-approve) */
const EditableInput = styled.input`
  width: 55%;
  min-width: 120px;
  padding: 0.35rem 0.5rem;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(59, 130, 246, 0.45);
  border-radius: 6px;
  color: #e2e8f0;
  font-size: 0.8125rem;
  font-weight: 500;
  text-align: right;

  &:focus {
    outline: none;
    border-color: #60a5fa;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const ButtonOutline = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(59, 130, 246, 0.55);
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const CovenantCard = styled.div<{ status: string }>`
  background: ${props => props.status === 'COMPLIANT' ? 'rgba(34, 197, 94, 0.1)' :
               props.status === 'BREACH' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(234, 179, 8, 0.1)'};
  border: 1px solid ${props => props.status === 'COMPLIANT' ? 'rgba(34, 197, 94, 0.3)' :
                     props.status === 'BREACH' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(234, 179, 8, 0.3)'};
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
`;

const CovenantName = styled.div`
  font-weight: 600;
  color: #ffffff;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

const CovenantDetail = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
`;

const DecisionBadge = styled.span<{ decision: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${props => {
    switch (props.decision) {
      case 'APPROVE_FOR_EXPORT': return 'rgba(34, 197, 94, 0.2)';
      case 'NEEDS_HUMAN_REVIEW': return 'rgba(234, 179, 8, 0.2)';
      case 'REJECT': return 'rgba(239, 68, 68, 0.2)';
      case 'APPROVED': return 'rgba(34, 197, 94, 0.2)';
      case 'REJECTED': return 'rgba(239, 68, 68, 0.2)';
      default: return 'rgba(148, 163, 184, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.decision) {
      case 'APPROVE_FOR_EXPORT': return '#22c55e';
      case 'NEEDS_HUMAN_REVIEW': return '#eab308';
      case 'REJECT': return '#ef4444';
      case 'APPROVED': return '#22c55e';
      case 'REJECTED': return '#ef4444';
      default: return '#94a3b8';
    }
  }};
`;

const ApprovalBanner = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 8px;
  background: ${props => props.status === 'APPROVED' ? 'rgba(34, 197, 94, 0.15)' :
               props.status === 'REJECTED' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(234, 179, 8, 0.15)'};
  border: 1px solid ${props => props.status === 'APPROVED' ? 'rgba(34, 197, 94, 0.3)' :
                     props.status === 'REJECTED' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(234, 179, 8, 0.3)'};
`;

const ButtonPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0.625rem 1rem;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonDanger = styled(ButtonPrimary)`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);

  &:hover {
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  }
`;

const ButtonSecondary = styled(ButtonPrimary)`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);

  &:hover {
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.625rem 0.875rem;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  &::placeholder {
    color: #64748b;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#3b82f6' : 'transparent'};
  color: ${props => props.active ? '#ffffff' : '#94a3b8'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #ffffff;
  }
`;

const LargeModal = styled(ModalContent)`
  max-width: 900px;
`;

/** A row's document type; a general loan notice is named by the event it carries ("DRAWDOWN NOTICE"). */
const jobTypeLabel = (job: { document_type?: string; result?: any }): string => {
  const type = String(job.document_type || '');
  const event = job.result?.result?.extracted_loan_data?.notice_event?.event_type ?? job.result?.extracted_loan_data?.notice_event?.event_type;
  return (type === 'LOAN_NOTICE' || type === 'AGENT_BANK_NOTICE') && event
    ? documentWord(type, String(event)).toUpperCase()
    : type.replace(/_/g, ' ');
};

// Types
interface Job {
  job_id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  document_name: string;
  document_type?: string;
  document_hash?: string;
  created_at: string;
  completed_at?: string;
  progress_percentage: number;
  current_stage?: string;
  result?: any;
  error?: string;
  approval_status?: 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED';
  oms_application_status?: 'APPLIED' | 'FAILED';
  oms_import_status?: 'SUCCESS' | 'FAILED';
  oms_finalization_status?: 'COMPLETED' | 'FAILED';
  oms_reconciliation_status?: 'COMPLETED' | 'FAILED';
  linked_security_id?: number;
  linked_loan_record_id?: number;
  linked_credit_deal_id?: number;
  oms_import_result?: LLDealImportResult;
  linked_commitment_id?: number;
  linked_capital_call_id?: number;
  linked_distribution_id?: number;
  linked_portfolio_id?: number;
  linked_portfolio_code?: string;
  seeded_position_id?: number;
  oms_application_result?: any;
  oms_finalization_result?: any;
  // Booking pipeline (post-extraction posting into IBOR).
  // Distinct from `status` (parse pipeline). A doc can be status=COMPLETED
  // but booking_status=BOOKING_FAILED if an audit gate rejected it.
  booking_status?: 'UNBOOKED' | 'POSTED' | 'STAGED' | 'BOOKING_FAILED' | 'EXTRACTION_FAILED' | 'PYTHON_HANDLES';
  last_booking_error?: string;
  retry_count?: number;
  next_retry_at?: string;
}

interface PcDocumentPackageSummary {
  packageId: string;
  title: string;
  purpose: string;
  state: string;
  expectedBorrower?: string;
  currentResolutionVersion?: number;
  preparer: string;
  checker?: string;
  documentCount: number;
  extractionFailures: number;
  updatedAt: string;
}

interface PcPackageMember {
  jobId: string;
  documentName: string;
  documentType: string;
  contentHash: string;
  extractionStatus: string;
  currentExtractionStatus: string;
  proposedRole?: string;
  approvedRole?: string;
  executionStatus: string;
  completenessStatus: string;
  inclusionState: string;
  clusterKey?: string;
}

interface PcPackageWorkspace extends PcDocumentPackageSummary {
  members: PcPackageMember[];
  counts: { documents: number; extractionFailures: number; unresolvedMembers: number };
  lockVersion: number;
}

interface PcPackageResolution {
  id: number;
  versionNumber: number;
  status: string;
  lockVersion: number;
  proposalHash: string;
  promotionManifestHash?: string;
  package: PcPackageWorkspace;
  dryRun: {
    referenceCreates?: number;
    facilityCreates?: number;
    blockingConflicts?: number;
    tradeCommands?: number;
    lifecycleCommands?: number;
    immediatePositionImpact?: number;
    immediateCashImpact?: number;
    message?: string;
  };
  entities: Array<{
    id: number;
    proposalKey: string;
    clusterKey: string;
    entityType: string;
    decision: string;
    confidenceScore?: number;
    evidenceFactors?: Array<{ factor: string; weight: number }>;
    blockingReview: boolean;
    reviewerDecision?: string;
    proposedPayload: Record<string, any>;
    promotedEntityId?: number;
  }>;
  relationships: Array<{
    id: number;
    relationshipType: string;
    fromProposalKey?: string;
    toProposalKey?: string;
    jobId?: string;
    proposedPayload: Record<string, any>;
    confidenceScore?: number;
    evidenceFactors?: Array<{ factor: string; weight: number }>;
    blockingReview: boolean;
    reviewerDecision?: string;
  }>;
  conflicts: Array<{
    id: number;
    conflictClass: string;
    severity: string;
    blocking: boolean;
    description: string;
    involvedAssertionIds?: number[];
    resolutionDecision?: string;
    resolutionReason?: string;
  }>;
  assertions: Array<{
    id: number;
    jobId: string;
    assertionType: string;
    proposedValue: any;
    targetScope?: string;
    documentRole?: string;
    authorityRank?: number;
    reviewStatus: string;
  }>;
  commands: Array<{ id: number; commandType: string; status: string; resultEntityIds?: Record<string, number> }>;
}

interface ExtractedLoanData {
  deal_code?: string;
  deal_name?: string;
  governing_law?: string;
  cov_lite_acknowledged?: boolean;
  event_amount?: number | string;
  amount?: number | string;
  paydown_amount?: number | string;
  draw_amount?: number | string;
  borrower?: {
    legal_name?: string;
    lei_code?: string;
    jurisdiction?: string;
    industry_sector?: string;
    sub_industry?: string;
    credit_rating?: string;
    sp_rating?: string;
    moodys_rating?: string;
    fitch_rating?: string;
  };
  sponsor_info?: {
    sponsor_name?: string;
    sponsor_type?: string;
    fund_name?: string;
  };
  loan_identifiers?: {
    loan_id?: string;
    facility_name?: string;
    cusip?: string;
    isin?: string;
  };
  facilities?: Array<{
    tranche_code?: string;
    facility_type?: string;
    facility_name?: string;
    loan_id?: string;
    cusip?: string;
    isin?: string;
    lsta_id?: string;
    loanx_id?: string;
    wso_loan_id?: string;
    original_size?: number;
    currency?: string;
    base_rate?: string;
    spread_bps?: number;
    csa_bps?: number;
    floor_rate?: number;
    floor_basis?: string;
    oid_price?: number;
    commitment_fee_bps?: number;
    ticking_fee_bps?: number;
    closing_date?: string;
    maturity_date?: string;
    availability_end_date?: string;
    day_count_convention?: string;
    payment_frequency?: string;
    annual_amortization_pct?: number;
    quarterly_amortization_pct?: number;
    amortization_start_date?: string;
    amortization_frequency?: string;
    balloon_at_maturity?: boolean;
    page_reference?: string;
    evidence_text?: string;
  }>;
  loan_terms?: {
    original_principal?: number;
    current_principal?: number;
    currency?: string;
    origination_date?: string;
    maturity_date?: string;
    loan_status?: string;
  };
  interest_rate?: {
    base_type?: string;
    base_rate?: number;
    spread_bps?: number;
    csa_bps?: number;
    floor_pct?: number;
    all_in_rate_pct?: number;
    step_down_spread_bps?: number;
    step_down_trigger_leverage?: number;
    oid_price?: number;
    upfront_fee_bps?: number;
  };
  lender_register?: Array<{
    lender_name?: string;
    lender_role?: string;
    commitment_amount?: number;
    pro_rata_pct?: number;
    facility_breakdown?: Record<string, number>;
  }>;
  payment_terms?: {
    payment_type?: string;
    payment_frequency?: string;
    day_count_convention?: string;
    next_payment_date?: string;
    next_payment_amount?: number;
  };
  pik_toggle?: {
    has_pik_option?: boolean;
    pik_premium_bps?: number;
    current_election?: string;
    cash_percentage?: number;
    pik_percentage?: number;
  };
  financial_covenants?: Array<{
    covenant_name?: string;
    covenant_type?: string;
    covenant_category?: string;
    metric_type?: string;
    covenant_direction?: string;
    threshold?: string;
    threshold_value?: string | number;
    current_value?: number;
    status?: string;
    current_compliance_status?: string;
    test_frequency?: string;
    testing_frequency?: string;
    measurement_frequency?: string;
    measurement_period?: string;
    next_test_date?: string;
    step_schedule?: Array<{ date?: string; value?: number }>;
    has_equity_cure?: boolean;
    max_equity_cures?: number;
    equity_cure_amount_cap?: number;
    cure_period_days?: number;
  }>;
  key_dates?: {
    document_date?: string;
    notice_effective_date?: string;
    interest_period_start?: string;
    interest_period_end?: string;
  };
  notice_event?: {
    event_type?: string;
    event_amount?: number;
    effective_date?: string;
    notice_date?: string;
    confidence?: number;
    rationale?: string;
  };
  trade_settlement_details?: {
    trade_date?: string;
    settlement_date?: string;
    settlement_convention?: string;
    par_amount?: number;
    transfer_price?: number;
    principal_consideration?: number;
    accrued_interest?: number;
    accrued_days?: number;
    accrued_period_start?: string;
    accrued_period_end?: string;
    assignment_fee?: number;
    total_settlement_amount?: number;
    assignor?: string;
    assignee?: string;
    trade_type?: string;
    lsta_standard_terms?: string;
  };
  capital_activity?: {
    activity_type?: string;
    fund_name?: string;
    investor_amount?: number;
    due_date?: string;
    payment_date?: string;
    commitment_amount?: number;
    unfunded_commitment?: number;
    return_of_capital?: number;
    realized_gain?: number;
    carried_interest?: number;
    waterfall_tier?: string;
  };
  _extraction_note?: string;
}

interface ExtractedDataResponse {
  /** The document template this job was read with, and what failed its rules (absent: built-in instructions). */
  template_validation?: TemplateValidation | null;
  job_id: string;
  document_name: string;
  document_type?: string;
  processing_time: number;
  manager_decision: string;
  data_quality_score: number;
  extracted_data: ExtractedLoanData;
  /** Present when server returned previously approved edits */
  edited_loan_data?: ExtractedLoanData;
  raw_output?: string;
  approval_status: string;
  /** F-S6-025: which record the ABF modal shows — the approved record once a maker approved, else the extraction. */
  abf_record_source?: 'APPROVED' | 'EXTRACTION';
  booking_status?: 'UNBOOKED' | 'POSTED' | 'STAGED' | 'BOOKING_FAILED' | 'EXTRACTION_FAILED' | 'PYTHON_HANDLES';
  last_booking_error?: string;
  /**
   * Interest-payment audit verification block — set by CrewAI's auditor agent
   * for quarterly LOAN_NOTICE documents. When `status === 'FAIL'` the modal
   * surfaces a discrepancy banner and offers "Override & Post" alongside the
   * normal Edit/Approve flow.
   */
  interest_verification?: {
    status?: 'PASS' | 'FAIL' | 'INSUFFICIENT_DATA' | string;
    stated_interest_amount?: number | string;
    computed_interest_amount?: number | string;
    delta_bps?: number | string;
    tolerance_bps?: number | string;
    reason?: string;
    day_count_base?: number | string;
    days?: number | string;
    all_in_rate?: number | string;
  };
  /** Audit stamp from a prior Override & Post — set after PM accepts FAIL with justification. */
  manual_override?: {
    by?: string;
    at?: string;
    reason?: string;
  };
  booking_precheck?: {
    status?: string;
    extraction_contract_validated?: boolean;
    missing_contract_fields?: string[];
    buyer_mapping_status?: string;
    human_approval_required?: boolean;
    ibor_ready?: boolean;
    message?: string;
  };
  linked_security_id?: number;
  linked_loan_record_id?: number;
  linked_credit_deal_id?: number;
  oms_import_result?: LLDealImportResult;
  linked_commitment_id?: number;
  linked_capital_call_id?: number;
  linked_distribution_id?: number;
  linked_portfolio_id?: number;
  linked_portfolio_code?: string;
  seeded_position_id?: number;
  oms_import_status?: string;
  oms_application_status?: string;
  oms_application_result?: any;
  oms_finalization_status?: string;
  oms_finalization_result?: any;
  oms_reconciliation_status?: string;
  oms_reconciliation_result?: any;
}

interface LLDealImportResult {
  dealId?: number;
  dealCode?: string;
  dealName?: string;
  referenceOnly?: boolean;
  message?: string;
  covenantsCreated?: number;
  lendersCreated?: number;
  tranches?: Array<{
    loanRecordId: number;
    loanId?: string;
    trancheCode?: string;
    facilityType?: string;
    securityId?: number;
    cusip?: string;
    lstaId?: string;
    lxId?: string;
    wsoLoanId?: string;
    facilityState?: string;
    created?: boolean;
  }>;
}

interface FacilityWorkspace {
  loanRecordId: number;
  loanId: string;
  loanName: string;
  borrowerName: string;
  cusip?: string;
  isin?: string;
  securityId?: number;
  securityName?: string;
  currentCapacity?: number | null;
  globalOutstanding?: number | null;
  globalAvailability?: number | null;
  totalDrawnAcrossPortfolios: number;
  totalUnfundedAcrossPortfolios: number;
  positions: Array<{
    portfolioId: number;
    portfolioCode?: string;
    portfolioName: string;
    drawnAmount: number;
    unfundedCommitment: number;
    parAmount: number;
    positionDate?: string;
  }>;
  recentEvents: Array<{
    eventId: number;
    eventType: string;
    status: string;
    eventDate: string;
    eventAmount?: number;
    sourceDocumentName?: string;
    message?: string;
  }>;
}

interface CommitmentWorkspace {
  commitmentId: number;
  fundId?: number;
  fundName?: string;
  fundType?: string;
  portfolioId: number;
  portfolioName?: string;
  portfolioCode?: string;
  commitmentAmount: number;
  calledAmount: number;
  unfundedCommitment: number;
  distributedAmount: number;
  recallableAmount: number;
  status: string;
  capitalCalls: Array<{
    callId: number;
    callNumber?: number;
    callAmount: number;
    dueDate?: string;
    status: string;
    paymentReference?: string;
  }>;
  distributions: Array<{
    distributionId: number;
    distributionNumber?: number;
    grossDistribution: number;
    distributionDate?: string;
    status: string;
    returnOfCapital?: number;
    capitalGains?: number;
  }>;
  recentEvents: Array<{
    eventId: number;
    eventType: string;
    status: string;
    eventDate?: string;
    eventAmount?: number;
    message?: string;
  }>;
}

function cloneExtractedLoanData(data: ExtractedLoanData | undefined | null): ExtractedLoanData {
  return JSON.parse(JSON.stringify(data ?? {}));
}

function parseOptionalNumber(raw: string): number | undefined {
  const t = raw.replace(/,/g, '').trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

type DocumentTemplate = {
  value: string;
  label: string;
  description: string;
};

type DocumentTemplateGroup = {
  category: string;
  templates: DocumentTemplate[];
};

const DOCUMENT_TEMPLATE_GROUPS: DocumentTemplateGroup[] = [
  {
    category: 'Private Credit',
    templates: [
      { value: 'LOAN_NOTICE', label: 'Loan Notice', description: 'Drawdown, PIK election, payment or interest notice for bilateral loans.' },
      { value: 'PAYDOWN_NOTICE', label: 'Paydown Notice', description: 'Principal repayment and funded-balance reduction.' },
      { value: 'CREDIT_AGREEMENT', label: 'Credit Agreement', description: 'Origination terms, facility setup and covenant extraction.' },
      { value: 'RATE_RESET_NOTICE', label: 'Rate Reset Notice', description: 'SOFR fixing, floor, spread and CSA used to recompute the governed all-in rate.' },
      { value: 'AMENDMENT', label: 'Amendment', description: 'Pricing, covenant, maturity or term changes.' },
      { value: 'COMPLIANCE_CERTIFICATE', label: 'Compliance Certificate (bilateral loan)', description: 'Period-end covenant calculations on a private credit loan. For an ABF facility use ABF Compliance Certificate.' },
    ],
  },
  {
    category: 'Syndicated Loans',
    templates: [
      { value: 'SYNDICATED_TERM_SHEET', label: 'LL Credit Agreement / Term Sheet', description: 'One governed syndicated deal with distinct TLB, revolver, DDTL and other facility terms.' },
      { value: 'AGENT_BANK_NOTICE', label: 'Agent Bank Notice', description: 'Agent bank lifecycle notice and payment instruction.' },
      { value: 'LSTA_TRADE_CONFIRM', label: 'LSTA Trade Confirmation', description: 'Trade confirmation and settlement terms.' },
      { value: 'LSTA_DRAWDOWN_NOTICE', label: 'LSTA Drawdown Notice', description: 'Syndicated facility drawdown notice.' },
      { value: 'LSTA_PAYDOWN_NOTICE', label: 'LSTA Paydown Notice', description: 'Syndicated principal repayment notice.' },
      { value: 'LSTA_RATE_RESET', label: 'LSTA Rate Reset Notice', description: 'Base-rate reset and spread details.' },
      { value: 'LSTA_AMENDMENT', label: 'LSTA Amendment Notice', description: 'Syndicated amendment and consent terms.' },
      { value: 'COMPLIANCE_CERTIFICATE', label: 'Compliance Certificate (syndicated loan)', description: 'Borrower certificate with tested covenant calculations on a syndicated loan. For an ABF facility use ABF Compliance Certificate.' },
    ],
  },
  {
    category: 'Real Estate',
    templates: [
      { value: 'REAL_ESTATE_ACQUISITION', label: 'Property Acquisition', description: 'RE equity acquisition and property setup.' },
      { value: 'RE_APPRAISAL', label: 'Appraisal / Re-Valuation', description: 'Updated property valuation and appraisal evidence.' },
      { value: 'REAL_ESTATE_LEASE', label: 'Lease Agreement', description: 'Tenant lease economics and occupancy terms.' },
      { value: 'REAL_ESTATE_DISPOSITION', label: 'Disposition / Sale', description: 'Property sale or disposal workflow.' },
      { value: 'RE_LOAN_AGREEMENT', label: 'CRE Loan Agreement', description: 'RE debt origination for bridge, mezz, construction or whole-loan deals.' },
      { value: 'RE_LOAN_PAYDOWN', label: 'CRE Loan Paydown Notice', description: 'RE debt repayment notice.' },
      { value: 'RE_LOAN_RATE_RESET', label: 'CRE Loan Rate Reset Notice', description: 'RE debt rate reset and index/spread terms.' },
      { value: 'RE_LOAN_AMENDMENT', label: 'CRE Loan Amendment', description: 'RE debt amendment and covenant changes.' },
      { value: 'RE_LOAN_PAYOFF', label: 'CRE Loan Payoff Notice', description: 'Full payoff or maturity event.' },
      { value: 'RE_LOAN_CONSTRUCTION_DRAW', label: 'CRE Construction Draw', description: 'Construction loan requisition or draw request.' },
    ],
  },
  {
    category: 'Private Equity',
    templates: [
      { value: 'PE_SUBSCRIPTION_AGREEMENT', label: 'Subscription Agreement', description: 'LP commitment setup and fund subscription terms.' },
      { value: 'FUND_PROSPECTUS', label: 'Fund Prospectus / PPM', description: 'Fund metadata, strategy, fees and offering terms.' },
      { value: 'CAPITAL_CALL', label: 'Capital Call Notice', description: 'Capital call, due date and unfunded commitment update.' },
      { value: 'DISTRIBUTION_NOTICE', label: 'Distribution Notice', description: 'Distribution, return of capital and gain allocation.' },
      { value: 'PE_NAV', label: 'Fund Valuation / NAV Mark', description: 'Quarterly NAV or fair-value mark update.' },
      { value: 'PORTCO_FINANCIALS', label: 'Portco Quarterly Financials', description: 'Portfolio-company KPIs, EBITDA, revenue and debt metrics.' },
      { value: 'IC_MEMO', label: 'Investment Committee Memo', description: 'Investment thesis, valuation and committee memo extraction.' },
      { value: 'PE_NAV_STATEMENT', label: 'PE NAV Statement', description: 'Look-through NAV statement and underlying exposure.' },
      { value: 'PORTCO_DEBT_STATEMENT', label: 'Portco Debt Statement', description: 'Sponsor finance and portfolio-company debt details.' },
      { value: 'COVENANT_CERTIFICATE_PORTCO', label: 'Portco Covenant Certificate', description: 'Portfolio-company covenant compliance certificate.' },
    ],
  },
  {
    category: 'Structured Credit',
    templates: [
      { value: 'CLO_OFFERING_MEMO', label: 'CLO Offering Memo', description: 'CLO equity acquisition and offering memo extraction.' },
      { value: 'CLO_OFFERING_CIRCULAR', label: 'CLO Offering Circular', description: 'Offering circular terms and tranche metadata.' },
      { value: 'CLO_INDENTURE', label: 'CLO Indenture', description: 'Deal terms, waterfall provisions and test definitions.' },
      { value: 'CLO_TRUSTEE_REPORT', label: 'CLO Trustee Report', description: 'Trustee report, collateral stats and test results.' },
      { value: 'CLO_MONTHLY_REPORT', label: 'CLO Monthly Report', description: 'Monthly CLO reporting package and surveillance metrics.' },
    ],
  },
  {
    category: 'Asset-Based Finance',
    templates: [
      { value: 'ABF_CREDIT_AGREEMENT', label: 'ABF Credit Agreement', description: 'Facility origination, borrowing base, eligibility and covenants.' },
      { value: 'ABF_AMENDMENT', label: 'ABF Amendment', description: 'Effective-dated changes to an existing verified facility.' },
      { value: 'ABF_DRAWDOWN_NOTICE', label: 'ABF Drawdown Notice', description: 'Borrowing-base draw request and availability impact.' },
      { value: 'ABF_REPAYMENT_NOTICE', label: 'ABF Repayment Notice', description: 'Scheduled repayment, prepayment or payoff staged as a governed repayment request.' },
      { value: 'ABF_COMPLIANCE_CERTIFICATE', label: 'ABF Compliance Certificate', description: "The borrower's covenant figures, compared with what AxiomFlow computed." },
      { value: 'ABF_BORROWING_BASE_CERTIFICATE', label: 'ABF Borrowing-Base Certificate', description: 'Eligible collateral, advance rate, reserve and test refresh.' },
      { value: 'ABF_SERVICER_REPORT', label: 'ABF Servicer Report', description: 'Pool surveillance, delinquency, loss and servicer reporting.' },
      { value: 'ABF_PAYING_AGENT_REPORT', label: 'ABF Paying Agent Statement', description: "The paying agent's distribution-date statement: how the waterfall was applied, compared node by node with the agreement." },
      { value: 'FUND_ADMIN_ACKNOWLEDGEMENT', label: 'Fund Administrator Acknowledgement', description: "The administrator's load confirmation or break report for a delivered ABF report; lands on the delivery as its acknowledgement." },
    ],
  },
];

const DOCUMENT_TEMPLATES = DOCUMENT_TEMPLATE_GROUPS.flatMap(group =>
  group.templates.map(template => ({ ...template, category: group.category }))
);

const getDocumentTemplate = (value: string) =>
  DOCUMENT_TEMPLATES.find(template => template.value === value) || DOCUMENT_TEMPLATES[0];

const inferRelatedDocumentType = (fileName: string, fallback: string) => {
  const name = fileName.toLowerCase().replace(/[\s-]+/g, '_');
  const isAbf = name.includes('abf');
  const isLsta = name.includes('lsta');

  // The ABF test has to come first. A file called Ridgeline_ABF_Compliance_Certificate.pdf
  // normalises to a name containing 'compliance_certificate', so the generic branch matched
  // it and returned the private-credit type before the ABF branch below was ever reached.
  // Every other family here already decides inside its own branch; this one did not.
  if (name.includes('compliance_certificate') || (name.includes('compliance') && name.includes('certificate'))) {
    return isAbf ? 'ABF_COMPLIANCE_CERTIFICATE' : 'COMPLIANCE_CERTIFICATE';
  }
  // The only borrowing-base / servicer templates are the ABF ones.
  if (name.includes('borrowing_base') || name.includes('borrowingbase') || /(^|_)bbc(_|\.|$)/.test(name)) {
    return 'ABF_BORROWING_BASE_CERTIFICATE';
  }
  if (isAbf && name.includes('servicer') && name.includes('report')) {
    return 'ABF_SERVICER_REPORT';
  }
  if (name.includes('paying_agent') || name.includes('payingagent') || (isAbf && name.includes('distribution') && name.includes('statement'))) {
    return 'ABF_PAYING_AGENT_REPORT';
  }
  if (name.includes('acknowledg') || (name.includes('fundadmin') && (name.includes('confirmation') || name.includes('break')))) {
    return 'FUND_ADMIN_ACKNOWLEDGEMENT';
  }
  if (name.includes('amendment')) {
    if (isAbf) return 'ABF_AMENDMENT';
    if (isLsta) return 'LSTA_AMENDMENT';
    return 'AMENDMENT';
  }
  if (name.includes('credit_agreement') || name.includes('facility_agreement')) {
    if (isAbf) return 'ABF_CREDIT_AGREEMENT';
    if (isLsta) return 'SYNDICATED_TERM_SHEET';
    return 'CREDIT_AGREEMENT';
  }
  if (name.includes('rate_reset')) return isLsta ? 'LSTA_RATE_RESET' : 'RATE_RESET_NOTICE';
  if (name.includes('paydown') || name.includes('repayment') || name.includes('prepayment') || name.includes('payoff')) {
    if (isAbf) return 'ABF_REPAYMENT_NOTICE';
    return isLsta ? 'LSTA_PAYDOWN_NOTICE' : 'PAYDOWN_NOTICE';
  }
  if (name.includes('drawdown') || name.includes('funding_notice')) {
    if (isAbf) return 'ABF_DRAWDOWN_NOTICE';
    if (isLsta) return 'LSTA_DRAWDOWN_NOTICE';
    return 'LOAN_NOTICE';
  }
  return fallback;
};

const ContractReviewHandoff = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.85rem;
  padding: 0.8rem 0.9rem;
  border: 1px solid rgba(56, 189, 248, 0.28);
  border-radius: 10px;
  background: rgba(14, 165, 233, 0.08);
  div { display: grid; gap: 0.2rem; }
  strong { color: #e2e8f0; font-size: 0.78rem; }
  span { max-width: 42rem; color: #94a3b8; font-size: 0.69rem; line-height: 1.5; }
  button {
    flex-shrink: 0;
    padding: 0.5rem 0.75rem;
    border: 1px solid rgba(56, 189, 248, 0.45);
    border-radius: 8px;
    background: rgba(14, 165, 233, 0.16);
    color: #bae6fd;
    font-family: inherit;
    font-size: 0.7rem;
    font-weight: 800;
    cursor: pointer;
  }
  button:hover { background: rgba(14, 165, 233, 0.26); }
  @media (max-width: 700px) { flex-direction: column; align-items: flex-start; }
`;

const DocumentProcessingScreen: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  // The provider's value object changes on every toast; its functions do not.
  const toastError = toast.error;
  const { confirm } = useConfirmation();
  const [portfolioPick, setPortfolioPick] = useState<{
    docType?: string; documentName?: string; resolve: (choice: PortfolioChoice) => void;
  } | null>(null);
  const [reasonAsk, setReasonAsk] = useState<{
    title: string; description?: string; confirmLabel?: string; resolve: (reason: string | null) => void;
  } | null>(null);
  const [importOutcome, setImportOutcome] = useState<ImportOutcome | null>(null);
  const [businessDate, setBusinessDate] = useState<string | null>(null);
  const pickPortfolio = (docType?: string, documentName?: string) =>
    new Promise<PortfolioChoice>(resolve => setPortfolioPick({ docType, documentName, resolve }));
  const askReason = (title: string, description?: string, confirmLabel?: string) =>
    new Promise<string | null>(resolve => setReasonAsk({ title, description, confirmLabel, resolve }));
  const navigate = useNavigate();

  const relatedPackagesEnabled = useIsFeatureEnabled('PC_MULTI_DOCUMENT_RESOLUTION');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFileTypes, setSelectedFileTypes] = useState<string[]>([]);
  const [uploadMode, setUploadMode] = useState<'single' | 'batch' | 'related'>('single');
  const [packageTitle, setPackageTitle] = useState('');
  const [packagePurpose, setPackagePurpose] = useState('NEW_FINANCING');
  const [expectedArrangementId, setExpectedArrangementId] = useState('');
  const [pcArrangements, setPcArrangements] = useState<Array<{
    id: number; arrangementCode: string; arrangementName: string; borrowerName: string;
  }>>([]);
  const [documentPackages, setDocumentPackages] = useState<PcDocumentPackageSummary[]>([]);
  const [packageQueueError, setPackageQueueError] = useState<string | null>(null);
  const [packageWorkspace, setPackageWorkspace] = useState<PcPackageWorkspace | null>(null);
  const [packageResolution, setPackageResolution] = useState<PcPackageResolution | null>(null);
  const [packageBusy, setPackageBusy] = useState(false);
  const [isFileDropActive, setIsFileDropActive] = useState(false);
  // Portfolios are loaded for the post-extraction allocate-later picker (V284:
  // doc-ingested origination never prompts for a portfolio at upload time).
  const [portfolios, setPortfolios] = useState<Array<{id: number, portfolioCode: string, portfolioName: string}>>([]);
  const [documentType, setDocumentType] = useState('LOAN_NOTICE');
  const [templateNotice, setTemplateNotice] = useState<string | null>(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategory, setTemplateCategory] = useState('ALL');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [reprocessingJobId, setReprocessingJobId] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('ALL');

  // New state for extracted data modal
  const [extractedDataModal, setExtractedDataModal] = useState<ExtractedDataResponse | null>(null);
  // The contract version this agreement produced, if any: its review is on the
  // facility, not in this read-only extraction view (F-WR-015).
  const [abfReviewVersionId, setAbfReviewVersionId] = useState<number | null>(null);

  useEffect(() => {
    const jobId = extractedDataModal?.job_id;
    const abf = ['ABF_CREDIT_AGREEMENT', 'ABF_AMENDMENT']
      .includes(extractedDataModal?.document_type || '');
    if (!jobId || !abf) { setAbfReviewVersionId(null); return; }
    let live = true;
    (async () => {
      try {
        const versions = await getFacilityVersionWorkQueue();
        const match = versions.find(version => String(version.source_document_job_id) === String(jobId));
        if (live) setAbfReviewVersionId(match ? match.id : null);
      } catch {
        if (live) setAbfReviewVersionId(null);
      }
    })();
    return () => { live = false; };
  }, [extractedDataModal?.job_id, extractedDataModal?.document_type]);
  const [extractDraftData, setExtractDraftData] = useState<ExtractedLoanData | null>(null);
  const [extractDraftBaselineJson, setExtractDraftBaselineJson] = useState('');
  const [extractEditMode, setExtractEditMode] = useState(false);
  const [modalTab, setModalTab] = useState<'data' | 'workspace' | 'raw'>('data');
  const [approvalComments, setApprovalComments] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [llDealImportResult, setLlDealImportResult] = useState<LLDealImportResult | null>(null);
  const [facilityWorkspace, setFacilityWorkspace] = useState<FacilityWorkspace | null>(null);
  const [facilityWorkspaceLoading, setFacilityWorkspaceLoading] = useState(false);
  const [commitmentWorkspace, setCommitmentWorkspace] = useState<CommitmentWorkspace | null>(null);
  const [commitmentWorkspaceLoading, setCommitmentWorkspaceLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  // The tenant's business date, for the "booked to a future date" note on an
  // import result. Tenants on their own business date (e.g. 2026-12-01 while the
  // wall clock says September) would otherwise see it on every document.
  useEffect(() => {
    axios.get(`${API_URL}/tenant-config/business-date`)
      .then(response => setBusinessDate(response.data?.businessDate || null))
      .catch(() => setBusinessDate(null));
  }, [API_URL]);
  // Route CrewAI calls through Java backend proxy to avoid HTTPS→HTTP mixed content
  // The proxy at /api/v1/* forwards to the Python CrewAI service internally
  const AXIOMFLOW_API_URL = '';
  // An ACTIVE document template names its type on upload; the list above is the fallback.
  const [templateCatalog, setTemplateCatalog] = useState<Record<string, CatalogEntry>>({});
  useEffect(() => {
    getCatalog()
      .then(entries => setTemplateCatalog(Object.fromEntries(entries.map(entry => [entry.documentType, entry]))))
      .catch(() => setTemplateCatalog({}));
  }, []);
  const pickerTemplates = useMemo(() => DOCUMENT_TEMPLATES.map(template => {
    const active = templateCatalog[template.value];
    return active
      ? { ...template, label: active.label || template.label, description: active.description || template.description }
      : template;
  }), [templateCatalog]);
  const selectedTemplate = useMemo(
    () => pickerTemplates.find(template => template.value === documentType) || getDocumentTemplate(documentType),
    [documentType, pickerTemplates]);
  const templateCategories = useMemo(() => ['ALL', ...DOCUMENT_TEMPLATE_GROUPS.map(group => group.category)], []);
  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    return pickerTemplates.filter(template => {
      const categoryMatches = templateCategory === 'ALL' || template.category === templateCategory;
      const queryMatches = !q ||
        template.label.toLowerCase().includes(q) ||
        template.description.toLowerCase().includes(q) ||
        template.category.toLowerCase().includes(q) ||
        template.value.toLowerCase().includes(q);
      return categoryMatches && queryMatches;
    });
  }, [templateCategory, templateSearch, pickerTemplates]);

  // Fetch jobs from AxiomFlow API
  const fetchJobs = useCallback(async () => {
    // 'BOOKING_HOLD' is a client-side filter on the booking pipeline, not the
    // parse pipeline — the server's status param doesn't know about it. Pull
    // everything, then filter for jobs the audit gate rejected.
    const isBookingHoldView = filter === 'BOOKING_HOLD';
    try {
      const status = (filter === 'ALL' || isBookingHoldView) ? undefined : filter;
      const response = await axios.get(`${AXIOMFLOW_API_URL}/api/v1/jobs`, {
        params: { status, limit: 50, offset: 0 }
      });
      const raw: Job[] = response.data.jobs || [];
      setJobs(isBookingHoldView ? raw.filter(j => j.booking_status === 'BOOKING_FAILED') : raw);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      // Fallback: try the backend API
      try {
        const status = (filter === 'ALL' || isBookingHoldView) ? '' : filter;
        const fallbackResponse = await axios.get(`${API_URL}/documents/jobs`, {
          params: { status, limit: 50, offset: 0 }
        });
        const raw: Job[] = fallbackResponse.data.jobs || [];
        setJobs(isBookingHoldView ? raw.filter(j => j.booking_status === 'BOOKING_FAILED') : raw);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    }
  }, [AXIOMFLOW_API_URL, API_URL, filter]);

  const fetchDocumentPackages = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/v1/pc/document-packages`);
      setDocumentPackages(Array.isArray(response.data) ? response.data : []);
      setPackageQueueError(null);
    } catch (error) {
      console.error('Error fetching private-credit document packages:', error);
      setPackageQueueError('Package queue could not be loaded. Retry after checking the document service.');
    }
  }, [API_URL]);

  const openPackageWorkspace = useCallback(async (packageId: string, version?: number) => {
    setPackageBusy(true);
    try {
      const packageResponse = await axios.get(`${API_URL}/v1/pc/document-packages/${packageId}`);
      const workspace = packageResponse.data as PcPackageWorkspace;
      setPackageWorkspace(workspace);
      const selectedVersion = version || workspace.currentResolutionVersion;
      if (selectedVersion) {
        const versionResponse = await axios.get(
          `${API_URL}/v1/pc/document-packages/${packageId}/versions/${selectedVersion}`
        );
        setPackageResolution(versionResponse.data);
      } else {
        setPackageResolution(null);
      }
    } catch (error: any) {
      console.error('Error opening document package:', error);
      toastError('Package load failed', `${error.response?.data?.message || error.message}`);
    } finally {
      setPackageBusy(false);
    }
  }, [API_URL, toastError]);

  const fetchFacilityWorkspace = useCallback(async (loanRecordId: number) => {
    setFacilityWorkspaceLoading(true);
    try {
      const response = await axios.get(`${API_URL}/credit/loans/${loanRecordId}/workspace`);
      setFacilityWorkspace(response.data);
    } catch (error) {
      console.error('Error fetching facility workspace:', error);
      setFacilityWorkspace(null);
    } finally {
      setFacilityWorkspaceLoading(false);
    }
  }, [API_URL]);

  const fetchCommitmentWorkspace = useCallback(async (commitmentId: number) => {
    setCommitmentWorkspaceLoading(true);
    try {
      const response = await axios.get(`${API_URL}/alternatives/commitments/${commitmentId}/workspace`);
      setCommitmentWorkspace(response.data);
    } catch (error) {
      console.error('Error fetching commitment workspace:', error);
      setCommitmentWorkspace(null);
    } finally {
      setCommitmentWorkspaceLoading(false);
    }
  }, [API_URL]);

  const closeExtractedModal = useCallback(() => {
    setExtractedDataModal(null);
    setExtractDraftData(null);
    setExtractDraftBaselineJson('');
    setExtractEditMode(false);
    setApprovalComments('');
  }, []);

  const openExtractedModal = useCallback((payload: ExtractedDataResponse) => {
    const draft = cloneExtractedLoanData(payload.extracted_data);
    setExtractedDataModal(payload);
    setExtractDraftData(draft);
    setExtractDraftBaselineJson(JSON.stringify(draft));
    setExtractEditMode(false);
    setModalTab('data');
    setApprovalComments('');
  }, []);

  const updateExtractDraft = useCallback((mutate: (draft: ExtractedLoanData) => void) => {
    setExtractDraftData((prev) => {
      if (!prev) return prev;
      const next = cloneExtractedLoanData(prev);
      mutate(next);
      return next;
    });
  }, []);

  const hasActiveJobs = jobs.some((job) => job.status === 'PROCESSING' || job.status === 'PENDING');

  useEffect(() => {
    fetchJobs();
    if (relatedPackagesEnabled) fetchDocumentPackages();
    const interval = setInterval(fetchJobs, hasActiveJobs ? 1500 : 5000);
    return () => clearInterval(interval);
  }, [fetchJobs, fetchDocumentPackages, hasActiveJobs, relatedPackagesEnabled]);

  // Load tenant portfolios for Fund Context dropdown
  useEffect(() => {
    axios.get('/api/dimensions/portfolios')
      .then(res => {
        const data = res.data;
        const list = Array.isArray(data) ? data : (data.portfolios || data.content || []);
        // Only include portfolios that have a real portfolioCode — null-code entries
        // would render as value="" and silently behave like "no portfolio selected"
        const withCode = list
          .map((p: any) => ({
            id: p.id,
            portfolioCode: p.portfolioCode || p.portfolio_code || '',
            portfolioName: p.portfolioName || p.portfolio_name || p.name || ''
          }))
          .filter((p: any) => p.portfolioCode !== '');
        setPortfolios(withCode);
      })
      .catch(() => {}); // silently ignore — field remains optional
  }, []);

  useEffect(() => {
    if (uploadMode !== 'related') return;
    axios.get(`${API_URL}/private-credit/arrangements`)
      .then(response => setPcArrangements(Array.isArray(response.data) ? response.data : []))
      .catch(error => console.error('Error loading private-credit arrangements:', error));
  }, [API_URL, uploadMode]);

  const normalizeFilesForMode = useCallback((files: File[]) => {
    const pdfFiles = files.filter(file =>
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );
    return uploadMode === 'single' ? pdfFiles.slice(0, 1) : pdfFiles;
  }, [uploadMode]);

  const handleSelectedFiles = useCallback((files: File[]) => {
    const normalized = normalizeFilesForMode(files);
    setSelectedFiles(normalized);
    // N-LR-015: a single document whose name says what it is (…_ABF_Compliance_Certificate…)
    // switches the template to match, and says so. A Larkspur ABF certificate was sent
    // under the private credit template of the same name and failed at import.
    let effectiveType = documentType;
    if (uploadMode !== 'related' && normalized.length === 1) {
      const inferred = inferRelatedDocumentType(normalized[0].name, documentType);
      if (inferred !== documentType) {
        effectiveType = inferred;
        setDocumentType(inferred);
        setTemplateNotice(`Template set to ${getDocumentTemplate(inferred).label} from the file name. Change it if that is wrong.`);
      } else {
        setTemplateNotice(null);
      }
    }
    setSelectedFileTypes(normalized.map(file =>
      uploadMode === 'related'
        ? inferRelatedDocumentType(file.name, documentType)
        : effectiveType
    ));
  }, [documentType, normalizeFilesForMode, uploadMode]);

  const formatFileSize = (bytes: number) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${bytes} B`;
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleSelectedFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDropActive(false);
    handleSelectedFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeSelectedFile = (fileName: string, index: number) => {
    setSelectedFiles(prev => prev.filter((file, i) => !(file.name === fileName && i === index)));
    setSelectedFileTypes(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadModeChange = (mode: 'single' | 'batch' | 'related') => {
    setUploadMode(mode);
    setTemplatePickerOpen(false);
    if (mode === 'single') {
      setSelectedFiles(prev => prev.slice(0, 1));
      setSelectedFileTypes(prev => prev.slice(0, 1));
    } else if (mode === 'related') {
      setSelectedFileTypes(selectedFiles.map(file =>
        inferRelatedDocumentType(file.name, documentType)
      ));
    }
  };

  const createRelatedPackageWhenJobsAreDurable = async (payload: Record<string, any>) => {
    let lastError: any;
    for (let attempt = 0; attempt < 20; attempt += 1) {
      try {
        return await axios.post(`${API_URL}/v1/pc/document-packages`, payload);
      } catch (error: any) {
        lastError = error;
        const message = error.response?.data?.message || error.response?.data?.error || '';
        if (!String(message).includes('Document job not found')) throw error;
        await new Promise(resolve => window.setTimeout(resolve, 500));
      }
    }
    throw lastError || new Error('Document jobs were not persisted before the package timeout');
  };

  // Upload document(s)
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.warning('Select at least one file to upload');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();

      let uploadResponse: any = null;
      if (uploadMode === 'single') {
        formData.append('file', selectedFiles[0]);
        formData.append('document_type', documentType);
        formData.append('process_async', 'true');

        // Upload to AxiomFlow API (port 8000)
        const resp = await axios.post(`${AXIOMFLOW_API_URL}/api/v1/documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadResponse = resp.data;
      } else {
        selectedFiles.forEach((file, index) => {
          formData.append('files', file);
          if (uploadMode === 'related') {
            formData.append('document_types', selectedFileTypes[index]
              || inferRelatedDocumentType(file.name, documentType));
          }
        });
        formData.append('document_type', documentType);

        // Upload to AxiomFlow API (port 8000)
        const response = await axios.post(`${AXIOMFLOW_API_URL}/api/v1/documents/batch`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadResponse = response.data;

        if (uploadMode === 'related') {
          const jobIds: string[] = uploadResponse?.job_ids || [];
          if (jobIds.length === 0) throw new Error('Extraction service returned no durable document job IDs');
          const title = packageTitle.trim() || selectedFiles[0]?.name.replace(/\.pdf$/i, '') || 'Related financing package';
          const packageResponse = await createRelatedPackageWhenJobsAreDurable({
            title,
            purpose: packagePurpose,
            expectedArrangementId: expectedArrangementId ? Number(expectedArrangementId) : undefined,
            expectedBorrower: expectedArrangementId
              ? pcArrangements.find(arrangement => String(arrangement.id) === expectedArrangementId)?.borrowerName
              : undefined,
            idempotencyKey: `related-batch:${uploadResponse.batch_id}`,
            members: jobIds.map((jobId, index) => ({
              jobId,
              uploadSequence: index + 1,
              executionStatus: 'UNKNOWN',
              completenessStatus: 'UNKNOWN',
              inclusionState: 'INCLUDED',
              accessClassification: 'INTERNAL'
            }))
          });
          setPackageTitle('');
          setExpectedArrangementId('');
          await fetchDocumentPackages();
          await openPackageWorkspace(packageResponse.data.packageId);
        }
      }

      setSelectedFiles([]);
      setSelectedFileTypes([]);
      await fetchJobs();
      // Distinguish a genuine new upload from a deduplicated return
      const isDuplicate = uploadResponse?.duplicate_of != null;
      if (isDuplicate) {
        toast.info('Already processed', `This file was processed before (job ${uploadResponse.job_id?.slice(0,8)}). Find it in the pipeline below.`);
      } else {
        toast.success(uploadMode === 'single'
          ? 'Document uploaded'
          : uploadMode === 'related'
            ? 'Related financing package created. Extraction runs per document; booking remains disabled until package approval.'
            : 'Independent batch uploaded successfully!');
      }
    } catch (error: any) {
      console.error('Error uploading document:', error);
      toast.error('Upload failed', `${error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const refreshPackageWorkspace = async () => {
    if (!packageWorkspace) return;
    await fetchDocumentPackages();
    await openPackageWorkspace(packageWorkspace.packageId, packageWorkspace.currentResolutionVersion);
  };

  const resolvePackage = async () => {
    if (!packageWorkspace) return;
    setPackageBusy(true);
    try {
      const response = await axios.post(
        `${API_URL}/v1/pc/document-packages/${packageWorkspace.packageId}/resolve`,
        { reason: 'Initial deterministic package resolution' }
      );
      const updatedResolution = response.data as PcPackageResolution;
      setPackageWorkspace(updatedResolution.package);
      setPackageResolution(updatedResolution);
      await fetchDocumentPackages();
      await openPackageWorkspace(packageWorkspace.packageId, response.data.versionNumber);
    } catch (error: any) {
      const apiError = error.response?.data;
      const supportReference = [
        apiError?.errorCode,
        apiError?.traceId ? `trace ${apiError.traceId}` : null
      ].filter(Boolean).join(' / ');
      toast.error('Resolution failed', `${apiError?.message || error.message}${supportReference ? ` (${supportReference})` : ''}`);
    } finally {
      setPackageBusy(false);
    }
  };

  const savePackageDecision = async (
    entityDecisions: Array<{ proposalKey: string; decision: string; reason: string }> = [],
    conflictDecisions: Array<{ conflictId: number; decision: string; selectedAuthorityId?: number; reason: string }> = [],
    memberDecisions: Array<{
      jobId: string;
      approvedRole?: string;
      executionStatus?: string;
      completenessStatus?: string;
      inclusionState?: string;
      clusterKey?: string;
      reason: string;
    }> = [],
    relationshipDecisions: Array<{ relationshipId: number; decision: string; reason: string }> = []
  ) => {
    if (!packageWorkspace || !packageResolution) return;
    setPackageBusy(true);
    try {
      const response = await axios.patch(
        `${API_URL}/v1/pc/document-packages/${packageWorkspace.packageId}/versions/${packageResolution.versionNumber}/decisions`,
        {
          lockVersion: packageResolution.lockVersion,
          reason: 'Package review decision',
          entityDecisions,
          relationshipDecisions,
          conflictDecisions,
          memberDecisions
        }
      );
      const updatedResolution = response.data as PcPackageResolution;
      setPackageWorkspace(updatedResolution.package);
      setPackageResolution(updatedResolution);
    } catch (error: any) {
      toast.error('Decision failed', `${error.response?.data?.message || error.message}`);
      await openPackageWorkspace(packageWorkspace.packageId, packageResolution.versionNumber);
    } finally {
      setPackageBusy(false);
    }
  };

  const submitPackage = async () => {
    if (!packageWorkspace || !packageResolution) return;
    setPackageBusy(true);
    try {
      const response = await axios.post(
        `${API_URL}/v1/pc/document-packages/${packageWorkspace.packageId}/versions/${packageResolution.versionNumber}/submit`,
        { reason: 'Structure and evidence reviewed by preparer' }
      );
      setPackageResolution(response.data);
      await refreshPackageWorkspace();
    } catch (error: any) {
      toast.error('Submit failed', `${error.response?.data?.message || error.message}`);
    } finally {
      setPackageBusy(false);
    }
  };

  const approvePackage = async () => {
    if (!packageWorkspace || !packageResolution) return;
    setPackageBusy(true);
    try {
      const response = await axios.post(
        `${API_URL}/v1/pc/document-packages/${packageWorkspace.packageId}/versions/${packageResolution.versionNumber}/approve`,
        { reason: 'Independent checker approval' }
      );
      setPackageResolution(response.data);
      await refreshPackageWorkspace();
    } catch (error: any) {
      toast.error('Approval failed', `${error.response?.data?.message || error.message}`);
    } finally {
      setPackageBusy(false);
    }
  };

  const promotePackage = async () => {
    if (!packageWorkspace || !packageResolution) return;
    setPackageBusy(true);
    try {
      await axios.post(
        `${API_URL}/v1/pc/document-packages/${packageWorkspace.packageId}/versions/${packageResolution.versionNumber}/promote`
      );
      await refreshPackageWorkspace();
    } catch (error: any) {
      toast.error('Promotion failed', `${error.response?.data?.message || error.message}`);
    } finally {
      setPackageBusy(false);
    }
  };

  const startPackageCorrection = async () => {
    if (!packageWorkspace) return;
    const reason = await askReason('Reason for the new resolution version',
      'Recorded on the resolution history.', 'Create version');
    if (!reason) return;
    setPackageBusy(true);
    try {
      const response = await axios.post(`${API_URL}/v1/pc/document-packages/${packageWorkspace.packageId}/versions`, {
        reason: reason.trim()
      });
      setPackageWorkspace(response.data);
      setPackageResolution(null);
      await fetchDocumentPackages();
    } catch (error: any) {
      toast.error('Correction failed', `${error.response?.data?.message || error.message}`);
    } finally {
      setPackageBusy(false);
    }
  };

  const handleReprocessJob = async (job: Job) => {
    const nextAttempt = (job.retry_count || 0) + 1;
    if (!(await confirm({
      type: 'warning',
      title: `Reprocess ${job.document_name}?`,
      message: `This reruns extraction on the original document and job record as attempt ${nextAttempt}. It does not create a duplicate job.`,
      confirmLabel: 'Reprocess',
    }))) return;

    setReprocessingJobId(job.job_id);
    try {
      const response = await axios.post(
        `${AXIOMFLOW_API_URL}/api/v1/jobs/${job.job_id}/reprocess`,
        {
          requested_by: user?.fullName || user?.username || 'Document Processing UI',
          reason: 'Operator requested retry after failed document extraction'
        }
      );
      await fetchJobs();
      toast.info('Reprocessing queued', response.data?.message || 'The failed extraction is queued for reprocessing.');
    } catch (error: any) {
      console.error('Error reprocessing document:', error);
      toast.error('Reprocess failed', `${error.response?.data?.detail || error.response?.data?.error || error.message}`);
    } finally {
      setReprocessingJobId(null);
    }
  };

  // Import to OMS
  const handleImportToOms = async (jobId: string, docType?: string) => {
    // V284 origination block-trade pattern: acquisition documents for
    // position-anchored asset classes create a DRAFT block trade. An LL credit
    // agreement is different: it is deal/facility reference data only. Other
    // acquisition documents create a DRAFT block trade with portfolio_id=NULL; the user assigns a
    // portfolio in the pre-clear ticket UI (BaseTicketScreen amber banner)
    // before submitting for compliance review. No portfolio prompt at import.
    const V284_BLOCK_ORIGINATION = new Set([
      'CREDIT_AGREEMENT',           // PC bilateral origination
      'LSTA_TRADE_CONFIRM',         // LL secondary purchase
      'RE_LOAN_AGREEMENT',          // RE Debt mortgage origination
      'REAL_ESTATE_ACQUISITION',    // RE Equity acquisition
      'PE_SUBSCRIPTION_AGREEMENT',  // PE fund subscription
      'CLO_OFFERING_MEMO',          // CLO equity acquisition (Path B)
      'ABF_CREDIT_AGREEMENT',       // ABF facility origination
    ]);
    const isV284BlockOrigination = !!docType && V284_BLOCK_ORIGINATION.has(docType);

    // Document types that auto-resolve portfolio from existing DB records — no user prompt needed.
    // RE follow-on events (appraisal, lease, disposition) resolve from the acquisition's position.
    // PE follow-on events (valuation) resolve via commitment → portfolio linkage.
    const AUTO_RESOLVE_TYPES = new Set([
      'AMENDMENT', 'LSTA_AMENDMENT',
      'COMPLIANCE_CERTIFICATE',   // resolves the borrower's PC loan server-side; never originates
      'LOAN_NOTICE',              // PC follow-on: DRAWDOWN reuses prior portfolio, INTEREST_PAYMENT needs no portfolio
      'PAYDOWN_NOTICE', 'LSTA_PAYDOWN_NOTICE',
      'LSTA_RATE_RESET', 'LSTA_DRAWDOWN_NOTICE',
      'AGENT_BANK_NOTICE',        // informational only
      'SYNDICATED_TERM_SHEET',    // one deal/many tranche reference definition; no portfolio or trade
      'RE_APPRAISAL',             // resolves via fact_re_equity_position
      'REAL_ESTATE_LEASE',        // resolves via fact_re_equity_position
      'REAL_ESTATE_DISPOSITION',  // resolves via fact_re_equity_position
      'RE_LOAN_PAYDOWN',          // resolves via real_estate_loan
      'RE_LOAN_RATE_RESET',       // resolves via real_estate_loan
      'RE_LOAN_AMENDMENT',        // resolves via real_estate_loan
      'RE_LOAN_COVENANT_CERT',    // resolves via real_estate_loan
      'RE_LOAN_PAYOFF',           // resolves via real_estate_loan
      'RE_LOAN_CONSTRUCTION_DRAW',// resolves via real_estate_loan (is_construction=true)
      'PE_VALUATION',             // resolves via dim_private_fund + fact_commitment
      'PE_NAV',                   // PE-BUG-11: resolves via fund_name → fact_commitment
      'DISTRIBUTION_NOTICE',      // resolves via AlternativeDocumentLinkingService
      'CAPITAL_CALL',             // resolves via fund_name → fact_commitment
      // CLO follow-on / reference-data docs — never require a portfolio prompt.
      // Indenture & Offering Circular are deal-level reference data (no trade).
      // Trustee & Monthly Reports resolve portfolio via existing CLO equity position.
      'CLO_INDENTURE',
      'CLO_OFFERING_CIRCULAR',
      'CLO_TRUSTEE_REPORT',
      'CLO_MONTHLY_REPORT',
      // ABF follow-on docs resolve against existing ABF facility records.
      'ABF_AMENDMENT',
      'ABF_DRAWDOWN_NOTICE',
      'ABF_REPAYMENT_NOTICE',
      'ABF_COMPLIANCE_CERTIFICATE',
      'ABF_BORROWING_BASE_CERTIFICATE',
      'ABF_SERVICER_REPORT',
      'ABF_PAYING_AGENT_REPORT',
      // Slice 7 walkthrough F-S7-011: an acknowledgement attaches to a report the facility already
      // owns (report reference → report → facility → IBOR portfolio); it books no position.
      'FUND_ADMIN_ACKNOWLEDGEMENT',
    ]);
    // Skip prompt for V284 origination (always block) AND auto-resolve types.
    const needsPortfolioPick = !isV284BlockOrigination
      && (!docType || !AUTO_RESOLVE_TYPES.has(docType));

    let portfolioParam = '';
    if (needsPortfolioPick && portfolios.length > 0) {
      const job = jobs.find(j => j.job_id === jobId);
      const choice = await pickPortfolio(docType, job?.document_name);
      if (!choice) return; // cancelled
      if (choice.kind === 'portfolio') portfolioParam = choice.code;
    }
    try {
      const url = portfolioParam
        ? `${API_URL}/documents/import/${jobId}?portfolio_code=${encodeURIComponent(portfolioParam)}`
        : `${API_URL}/documents/import/${jobId}`;
      const response = await axios.post(url);
      const result = response.data || {};
      if (result.import_status === 'FAILED' || result.booking_status === 'BOOKING_FAILED') {
        const reason = result.error
          || result.last_booking_error
          || result.message
          || 'Document booking failed. Review the Booking Hold row for details.';
        setImportOutcome({ jobId, documentName: jobs.find(j => j.job_id === jobId)?.document_name, tone: 'error', title: 'Import failed', lines: [reason] });
        await fetchJobs();
        return;
      }
      if (result.referenceOnly && Array.isArray(result.tranches)) {
        setLlDealImportResult(result);
        await fetchJobs();
        return;
      }
      const details = [
        result.trade_id ? `Trade ID: ${result.trade_id}${result.trade_status ? ` (${result.trade_status})` : ''}` : null,
        result.tradeId ? `Trade ID: ${result.tradeId}${result.tradeStatus ? ` (${result.tradeStatus})` : ''}` : null,
        result.security_id ? `Security ID: ${result.security_id}` : null,
        result.securityId ? `Security ID: ${result.securityId}` : null,
        result.cusip ? `CUSIP: ${result.cusip}` : null,
        result.loan_record_id ? `Loan Record ID: ${result.loan_record_id}` : null,
        result.loanRecordId ? `Loan Record ID: ${result.loanRecordId}` : null,
        result.commitment_id ? `Commitment ID: ${result.commitment_id}` : null,
        result.capital_call_id ? `Capital Call ID: ${result.capital_call_id}` : null,
        result.distribution_id ? `Distribution ID: ${result.distribution_id}` : null,
        result.portfolio_code ? `Portfolio: ${result.portfolio_code}` : null,
        result.portfolioCode ? `Portfolio: ${result.portfolioCode}` : null,
        result.seeded_position_id ? `Seeded Loan Position: ${result.seeded_position_id}` : null
      ].filter(Boolean) as string[];

      // For DRAFT-lifecycle imports (e.g. RE acquisitions), prompt the user to
      // pick up the next step in the trade blotter rather than expecting an
      // immediate IBOR position.
      const isLateBookingDraft = result.trade_status === 'DRAFT'
        && (result.import_status === 'LATE_BOOKING_DRAFT'
          || String(result.message || '').toLowerCase().includes('late-booking'));
      const nextStep = result.trade_status === 'DRAFT'
        ? isLateBookingDraft
          ? 'Open this ticket in the Trade Blotter and record the post-trade Compliance and Operations approvals; do not submit it for retroactive preclear. Release unlocks after both approvals, then Execute and Post normally.'
          : 'Pre-clear and approve this DRAFT ticket in the Trade Blotter. The position appears in IBOR once it is POSTED.'
        : undefined;

      // Note a trade booked well after the business date (e.g. a paydown effective
      // months away): the blotter's default date range will not show it.
      const effectiveDateStr: string | undefined = result.effectiveDate || result.effective_date || result.event_date;
      const referenceDate = businessDate || getTenantToday();
      let futureDateWarning: string | undefined;
      if (effectiveDateStr) {
        const daysAhead = Math.round(
          (Date.parse(String(effectiveDateStr).slice(0, 10)) - Date.parse(referenceDate)) / 86400000);
        if (daysAhead > 7) {
          futureDateWarning = `Booked to ${String(effectiveDateStr).slice(0, 10)}, ${daysAhead} days after the business date ${referenceDate}. Extend the blotter's Date To filter past it to see the trade.`;
        }
      }
      // F-MK-076: an agent's statement or servicer report lands on a collection period; Collections
      // reopens on that period, and the outcome links straight to it.
      const landed = docType === 'ABF_PAYING_AGENT_REPORT' || docType === 'ABF_SERVICER_REPORT' ? landedCollectionsPeriod(result) : null;
      if (landed) rememberCollectionsPeriod(landed.facilityId, landed.periodId);
      setImportOutcome({
        jobId,
        documentName: jobs.find(j => j.job_id === jobId)?.document_name,
        tone: futureDateWarning ? 'warning' : 'success',
        title: result.message || 'Document imported to OMS',
        lines: details,
        nextStep,
        openBlotter: result.trade_status === 'DRAFT',
        warning: futureDateWarning,
        openLink: landed ? { label: 'Open Collections at the period it landed on', to: landed.path } : undefined,
      });
      await fetchJobs();
      if (result.loan_record_id) {
        fetchFacilityWorkspace(result.loan_record_id);
      }
      if (result.commitment_id) {
        fetchCommitmentWorkspace(result.commitment_id);
      }
    } catch (error: any) {
      console.error('Error importing to OMS:', error);
      setImportOutcome({
        jobId, documentName: jobs.find(j => j.job_id === jobId)?.document_name, tone: 'error', title: 'Import failed',
        lines: [error.response?.data?.error || error.message],
      });
    }
  };

  const handleApplyToOms = async (jobId: string) => {
    try {
      const response = await axios.post(`${API_URL}/documents/apply/${jobId}`);
      const result = response.data || {};
      const details = [
        result.event_type ? `Event: ${result.event_type}` : null,
        result.applied_amount ? `Applied Amount: ${formatCurrency(result.applied_amount)}` : null,
        result.commitment_id ? `Commitment ID: ${result.commitment_id}` : null,
        result.capital_call_id ? `Capital Call ID: ${result.capital_call_id}` : null,
        result.distribution_id ? `Distribution ID: ${result.distribution_id}` : null,
        result.next_required_action ? `Next Step: ${result.next_required_action}` : null,
        result.portfolio_code ? `Portfolio: ${result.portfolio_code}` : null,
        result.loan_record_id ? `Loan Record ID: ${result.loan_record_id}` : null
      ].filter(Boolean).join('\n');

      setImportOutcome({ jobId, documentName: jobs.find(j => j.job_id === jobId)?.document_name, tone: 'success', title: result.message || 'Loan notice applied to OMS successfully', lines: details ? details.split('\n') : [] });
      await fetchJobs();
      if (result.loan_record_id) {
        fetchFacilityWorkspace(result.loan_record_id);
      }
      if (result.commitment_id) {
        fetchCommitmentWorkspace(result.commitment_id);
      }
    } catch (error: any) {
      console.error('Error applying OMS workflow:', error);
      toast.error('Apply failed', `${error.response?.data?.error || error.message}`);
    }
  };

  const handleFinalizeToOms = async (jobId: string) => {
    try {
      const response = await axios.post(`${API_URL}/documents/finalize/${jobId}`);
      const result = response.data || {};
      const details = [
        result.cash_control_action ? `Action: ${result.cash_control_action}` : null,
        result.confirmed_amount ? `Confirmed Amount: ${formatCurrency(result.confirmed_amount)}` : null,
        result.commitment_id ? `Commitment ID: ${result.commitment_id}` : null,
        result.capital_call_id ? `Capital Call ID: ${result.capital_call_id}` : null,
        result.distribution_id ? `Distribution ID: ${result.distribution_id}` : null,
        result.portfolio_code ? `Portfolio: ${result.portfolio_code}` : null
      ].filter(Boolean).join('\n');

      setImportOutcome({ jobId, documentName: jobs.find(j => j.job_id === jobId)?.document_name, tone: 'success', title: result.message || 'OMS workflow finalized successfully', lines: details ? details.split('\n') : [] });
      await fetchJobs();
      if (result.commitment_id) {
        fetchCommitmentWorkspace(result.commitment_id);
      }
    } catch (error: any) {
      console.error('Error finalizing OMS workflow:', error);
      toast.error('Finalize failed', `${error.response?.data?.error || error.message}`);
    }
  };

  const handleReconcileToOms = async (jobId: string) => {
    try {
      const response = await axios.post(`${API_URL}/documents/reconcile/${jobId}`);
      const result = response.data || {};
      const details = [
        result.reconciled_action ? `Action: ${result.reconciled_action}` : null,
        result.commitment_id ? `Commitment ID: ${result.commitment_id}` : null,
        result.capital_call_id ? `Capital Call ID: ${result.capital_call_id}` : null,
        result.distribution_id ? `Distribution ID: ${result.distribution_id}` : null,
        result.portfolio_code ? `Portfolio: ${result.portfolio_code}` : null
      ].filter(Boolean).join('\n');

      setImportOutcome({ jobId, documentName: jobs.find(j => j.job_id === jobId)?.document_name, tone: 'success', title: result.message || 'OMS workflow reconciled successfully', lines: details ? details.split('\n') : [] });
      await fetchJobs();
      if (result.commitment_id) {
        fetchCommitmentWorkspace(result.commitment_id);
      }
    } catch (error: any) {
      console.error('Error reconciling OMS workflow:', error);
      toast.error('Reconcile failed', `${error.response?.data?.error || error.message}`);
    }
  };

  // View job details (raw JSON)
  const viewJobDetails = async (jobId: string) => {
    try {
      const response = await axios.get(`${API_URL}/documents/jobs/${jobId}`);
      setSelectedJob(response.data);
    } catch (error) {
      console.error('Error fetching job details:', error);
    }
  };

  // View extracted loan data
  const viewExtractedData = async (jobId: string) => {
    try {
      // Try AxiomFlow API first
      const response = await axios.get(`${AXIOMFLOW_API_URL}/api/v1/jobs/${jobId}/extracted-data`);
      openExtractedModal({
        ...response.data,
        abf_record_source: response.data?.approval_status === 'APPROVED' ? 'APPROVED' : 'EXTRACTION',
      });
      if (response.data?.linked_loan_record_id) {
        fetchFacilityWorkspace(response.data.linked_loan_record_id);
      }
      if (response.data?.linked_commitment_id) {
        fetchCommitmentWorkspace(response.data.linked_commitment_id);
      }
    } catch (error: any) {
      // Fallback to backend API
      try {
        const response = await axios.get(`${API_URL}/documents/jobs/${jobId}`);
        const job = response.data;
        const resultRoot = job.result || {};
        const r = resultRoot?.result || resultRoot;
        // Different doc types ship different extracted-data shapes; the legacy
        // loan extractor returns blanks for non-loan docs. Branch by doc type.
        const reDocTypes = ['REAL_ESTATE_ACQUISITION', 'RE_APPRAISAL', 'REAL_ESTATE_LEASE', 'REAL_ESTATE_DISPOSITION'];
        const reDebtDocTypes = ['RE_LOAN_AGREEMENT', 'RE_LOAN_PAYDOWN', 'RE_LOAN_RATE_RESET', 'RE_LOAN_AMENDMENT', 'RE_LOAN_PAYOFF', 'RE_LOAN_CONSTRUCTION_DRAW'];
        const peDocTypes = ['PE_SUBSCRIPTION_AGREEMENT', 'PE_CAPITAL_CALL', 'PE_DISTRIBUTION_NOTICE', 'PE_NAV_STATEMENT', 'PE_SIDE_LETTER', 'PE_FUND_TERM_SHEET'];
        const cloDocTypes = ['CLO_OFFERING_MEMO', 'CLO_OFFERING_CIRCULAR', 'CLO_INDENTURE', 'CLO_TRUSTEE_REPORT', 'CLO_MONTHLY_REPORT'];
        const abfDocTypes = ['ABF_CREDIT_AGREEMENT', 'ABF_AMENDMENT', 'ABF_DRAWDOWN_NOTICE', 'ABF_REPAYMENT_NOTICE', 'ABF_BORROWING_BASE_CERTIFICATE', 'ABF_SERVICER_REPORT', 'ABF_COMPLIANCE_CERTIFICATE', 'ABF_PAYING_AGENT_REPORT'];
        const docType = job.document_type || '';
        const isReDoc = reDocTypes.includes(docType);
        const isReDebtDoc = reDebtDocTypes.includes(docType);
        const isPeDoc = peDocTypes.includes(docType);
        const isCloDoc = cloDocTypes.includes(docType);
        const isAbfDoc = abfDocTypes.includes(docType);
        const nonEmptyObject = (value: any) => value && typeof value === 'object' && Object.keys(value).length > 0 ? value : undefined;
        const isMeaningfulValue = (value: any): boolean => {
          if (value == null) return false;
          if (typeof value === 'string') {
            return !['', 'N/A', 'NA', 'TBD', 'NONE', 'UNKNOWN', 'NOT EXTRACTED'].includes(value.trim().toUpperCase());
          }
          if (Array.isArray(value)) return value.length > 0;
          if (typeof value === 'object') return Object.keys(value).length > 0;
          return true;
        };
        const mergeMeaningful = (base: any, supplemental: any): any => {
          const merged = base && typeof base === 'object' && !Array.isArray(base) ? { ...base } : {};
          if (!supplemental || typeof supplemental !== 'object' || Array.isArray(supplemental)) return merged;
          Object.entries(supplemental).forEach(([key, value]) => {
            if (!isMeaningfulValue(value)) return;
            const existing = merged[key];
            if (existing && typeof existing === 'object' && !Array.isArray(existing)
              && value && typeof value === 'object' && !Array.isArray(value)
            ) {
              merged[key] = mergeMeaningful(existing, value);
            } else if (!isMeaningfulValue(existing)) {
              merged[key] = value;
            }
          });
          return merged;
        };
        const bookingAbf = nonEmptyObject(job.booking_payload?.extracted_abf_data)
          || nonEmptyObject(job.bookingPayload?.extracted_abf_data)
          || {};
        // F-S6-025: once approved, the approved record is what books; show it, not the raw extraction.
        const approvedAbf = nonEmptyObject(resultRoot?.approval_review?.approved_abf_data)
          || nonEmptyObject(r?.approval_review?.approved_abf_data);
        const extractedAbf = approvedAbf
          || nonEmptyObject(r?.edited_abf_data)
          || nonEmptyObject(r?.extracted_abf_data)
          || {};
        const extracted = isReDoc
          ? (nonEmptyObject(r?.edited_re_data) || nonEmptyObject(r?.extracted_re_data) || {})
          : isReDebtDoc
          ? (nonEmptyObject(r?.edited_re_debt_data) || nonEmptyObject(r?.extracted_re_debt_data) || {})
          : isPeDoc
          ? (nonEmptyObject(r?.edited_pe_data) || nonEmptyObject(r?.extracted_pe_data) || {})
          : isCloDoc
          ? (nonEmptyObject(r?.edited_clo_data) || nonEmptyObject(r?.extracted_clo_data) || {})
          : isAbfDoc
          ? mergeMeaningful(bookingAbf, extractedAbf)
          : (nonEmptyObject(r?.edited_loan_data) || nonEmptyObject(r?.extracted_loan_data) || {});
        const managerDecision = r?.manager_decision?.decision || 'NEEDS_HUMAN_REVIEW';
        const dqScore = r?.manager_decision?.data_quality_score ?? 0;
        openExtractedModal({
          job_id: jobId,
          document_name: job.document_name,
          processing_time: job.completed_at ? (new Date(job.completed_at).getTime() - new Date(job.created_at).getTime()) / 1000 : 0,
          manager_decision: managerDecision,
          data_quality_score: dqScore,
          extracted_data: extracted,
          raw_output: r?.raw_output || JSON.stringify(job.result, null, 2),
          approval_status: job.approval_status || r?.approval_status || 'PENDING_REVIEW',
          abf_record_source: isAbfDoc && approvedAbf ? 'APPROVED' : 'EXTRACTION',
          document_type: job.document_type,
          linked_security_id: job.linked_security_id,
          linked_loan_record_id: job.linked_loan_record_id,
          linked_credit_deal_id: job.linked_credit_deal_id,
          oms_import_result: job.oms_import_result,
          linked_commitment_id: job.linked_commitment_id,
          linked_capital_call_id: job.linked_capital_call_id,
          linked_distribution_id: job.linked_distribution_id,
          linked_portfolio_id: job.linked_portfolio_id,
          linked_portfolio_code: job.linked_portfolio_code,
          seeded_position_id: job.seeded_position_id,
          oms_import_status: job.oms_import_status,
          oms_application_status: job.oms_application_status,
          oms_application_result: job.oms_application_result,
          oms_finalization_status: job.oms_finalization_status,
          oms_finalization_result: job.oms_finalization_result,
          oms_reconciliation_status: job.oms_reconciliation_status,
          oms_reconciliation_result: job.oms_reconciliation_result
        });
        if (job.linked_loan_record_id) {
          fetchFacilityWorkspace(job.linked_loan_record_id);
        }
        if (job.linked_commitment_id) {
          fetchCommitmentWorkspace(job.linked_commitment_id);
        }
      } catch (fallbackError) {
        console.error('Error fetching extracted data:', error);
        toast.error('Could not load the extracted data');
      }
    }
  };

  // A link from the agreement review opens the job's extraction here, where it is approved.
  const [linkParams] = useSearchParams();
  const linkedReviewJob = linkParams.get('reviewJob');
  useEffect(() => {
    if (linkedReviewJob) viewExtractedData(linkedReviewJob);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedReviewJob]);
  // An entry lane (the private credit home's "upload the agreement") arrives with the type chosen.
  const linkedDocumentType = linkParams.get('documentType');
  useEffect(() => {
    if (linkedDocumentType && DOCUMENT_TEMPLATES.some(t => t.value === linkedDocumentType)) setDocumentType(linkedDocumentType);
  }, [linkedDocumentType]);

  // Approve extracted data — optional override for verification-FAIL flows
  const handleApprove = async (overrideVerification: boolean = false) => {
    if (!extractedDataModal) return;

    if (overrideVerification && !approvalComments.trim()) {
      toast.warning('Justification required', 'Give a reason to override a verification discrepancy.');
      return;
    }
    // F-PCP-039 / F-PCP-003: a read the template's rules reject is not approved as it stands. Correct the read, re-read the
    // document, or approve with an override and a reason — never silently.
    const templateVerdict = extractedDataModal.template_validation as TemplateValidation | undefined;
    if (!overrideVerification && templateVerdict && templateVerdict.status === 'FAIL') {
      const failing = Object.keys(templateVerdict.errors || {}).length || templateVerdict.error_count || 0;
      toast.warning('Cannot approve yet', `${failing} value(s) fail the template's rules (see "Read with template" above). Correct the read or re-read the document; to approve anyway, use the override with a reason.`);
      return;
    }

    setIsApproving(true);
    try {
      const draft = extractDraftData ?? cloneExtractedLoanData(extractedDataModal.extracted_data);
      if (extractedDataModal.document_type === 'SYNDICATED_TERM_SHEET') {
        const facilities = draft.facilities || [];
        if (facilities.length === 0) {
          toast.warning('Cannot approve yet', 'Add at least one reviewed facility before approving this leveraged-loan agreement.');
          return;
        }
        const codes = facilities.map(row => String(row.tranche_code || '').trim().toUpperCase());
        if (codes.some(code => !code) || new Set(codes).size !== codes.length) {
          toast.warning('Cannot approve yet', 'Every facility needs a unique tranche code.');
          return;
        }
        if (facilities.some(row => !row.original_size || Number(row.original_size) <= 0)) {
          toast.warning('Cannot approve yet', 'Every facility needs a positive facility-global original size.');
          return;
        }
        if ((!draft.financial_covenants || draft.financial_covenants.length === 0)
          && !draft.cov_lite_acknowledged) {
          toast.warning('Cannot approve yet', 'Add the extracted covenant package, or acknowledge a cov-lite agreement.');
          return;
        }
        const covenantNames = (draft.financial_covenants || [])
          .map(row => String(row.covenant_name || '').trim().replace(/\s+/g, ' ').toUpperCase());
        if (covenantNames.some(name => !name) || new Set(covenantNames).size !== covenantNames.length) {
          toast.warning('Cannot approve yet', 'Every covenant needs a unique name.');
          return;
        }
      }
      const approver = user?.fullName || user?.username;
      if (!approver) {
        toast.error('Approver identity unavailable', 'Sign in again before approving.');
        return;
      }
      const body: {
        approved_by: string;
        comments: string;
        edited_data?: ExtractedLoanData;
        override_verification?: boolean;
        override_reason?: string;
      } = {
        approved_by: approver,
        comments: approvalComments
      };
      if (extractDraftBaselineJson && JSON.stringify(draft) !== extractDraftBaselineJson) {
        body.edited_data = draft;
      }
      if (overrideVerification) {
        body.override_verification = true;
        body.override_reason = approvalComments.trim();
      }
      await axios.post(`${AXIOMFLOW_API_URL}/api/v1/jobs/${extractedDataModal.job_id}/approve`, body);
      toast.success(overrideVerification ? 'Override recorded' : 'Extraction approved', overrideVerification
        ? 'The override is on the audit trail. Click "Import to OMS" in the pipeline row to book the payment.'
        : 'Import to OMS from the pipeline row when ready.');
      closeExtractedModal();
      await fetchJobs();
    } catch (error: any) {
      console.error('Error approving data:', error);
      toast.error('Approval failed', `${error.response?.data?.detail || error.message}`);
    } finally {
      setIsApproving(false);
    }
  };

  // Reject extracted data
  const handleReject = async () => {
    if (!extractedDataModal) return;

    if (!approvalComments.trim()) {
      toast.warning('Give a reason for the rejection');
      return;
    }

    setIsApproving(true);
    try {
      await axios.post(`${AXIOMFLOW_API_URL}/api/v1/jobs/${extractedDataModal.job_id}/reject`, {
        approved_by: user?.fullName || user?.username || 'Unknown User',
        comments: approvalComments
      });
      toast.info('Extraction rejected');
      closeExtractedModal();
      await fetchJobs();
    } catch (error: any) {
      console.error('Error rejecting data:', error);
      toast.error('Rejection failed', `${error.response?.data?.detail || error.message}`);
    } finally {
      setIsApproving(false);
    }
  };

  // Export approved data
  const handleExport = async () => {
    if (!extractedDataModal) return;

    setIsApproving(true);
    try {
      const response = await axios.post(`${AXIOMFLOW_API_URL}/api/v1/jobs/${extractedDataModal.job_id}/export`, {
        export_to_aladdin: true,
        export_to_snowflake: true,
        export_to_oms: true
      });
      const results = response.data.exports.map((e: any) => `${e.destination}: ${e.status}`).join('\n');
      toast.success('Export completed', results);
      closeExtractedModal();
      await fetchJobs();
    } catch (error: any) {
      console.error('Error exporting data:', error);
      toast.error('Export failed', `${error.response?.data?.detail || error.message}`);
    } finally {
      setIsApproving(false);
    }
  };

  // Format currency
  const formatCurrency = (value?: number | string) => {
    if (value === undefined || value === null) return '-';
    const num = typeof value === 'string' ? Number(value.replace(/,/g, '').replace(/\$/g, '').trim()) : value;
    if (!Number.isFinite(num)) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);
  };

  // Format percentage
  const formatPercent = (value?: number | string) => {
    if (value === undefined || value === null) return '-';
    const num = Number(value);
    if (isNaN(num)) return '-';
    return `${num.toFixed(2)}%`;
  };

  const pipelineStages = [
    { label: 'Ingest', aliases: ['document ingestion', 'ingestion', 'queued'] },
    { label: 'Extract', aliases: ['term extraction', 'extraction', 'interpretation', 'legal interpretation'] },
    { label: 'Validate', aliases: ['financial validation', 'validation', 'audit', 'auditing'] },
    { label: 'Review', aliases: ['manager review', 'structuring results', 'complete'] }
  ];

  const getPipelineStageIndex = (job: Job) => {
    if (job.status === 'COMPLETED') return pipelineStages.length;
    if (job.status === 'FAILED') return Math.max(0, pipelineStages.findIndex((stage) =>
      stage.aliases.some((alias) => (job.current_stage || '').toLowerCase().includes(alias))
    ));

    const stageText = (job.current_stage || '').toLowerCase();
    const byName = pipelineStages.findIndex((stage) =>
      stage.aliases.some((alias) => stageText.includes(alias))
    );
    if (byName >= 0) return byName;

    const progress = job.progress_percentage || 0;
    if (progress >= 75) return 3;
    if (progress >= 50) return 2;
    if (progress >= 25) return 1;
    return 0;
  };

  const renderPipelineSteps = (job: Job) => {
    const activeIndex = getPipelineStageIndex(job);
    return (
      <PipelineStepper aria-label={`Processing stage: ${job.current_stage || job.status}`}>
        {pipelineStages.map((stage, index) => {
          const state =
            job.status === 'FAILED' && index === activeIndex ? 'failed' :
            job.status === 'COMPLETED' || index < activeIndex ? 'done' :
            index === activeIndex && job.status !== 'PENDING' ? 'active' :
            'pending';
          return (
            <PipelineStep key={stage.label} state={state}>
              {state === 'done' && <CheckCircle size={12} />}
              {state === 'active' && <RefreshCw size={12} className="animate-spin" />}
              {state === 'failed' && <XCircle size={12} />}
              {state === 'pending' && <Clock size={12} />}
              {stage.label}
            </PipelineStep>
          );
        })}
      </PipelineStepper>
    );
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle size={14} />;
      case 'FAILED':
        return <XCircle size={14} />;
      case 'PROCESSING':
        return <RefreshCw size={14} className="animate-spin" />;
      case 'PENDING':
        return <Clock size={14} />;
      default:
        return <AlertCircle size={14} />;
    }
  };

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <Sparkles className="w-8 h-8 text-blue-500" />
                Intelligent Document Processing
              </h1>
              <p className="text-slate-500 font-medium mt-1">Governed extraction for private credit, leveraged loan and structured credit documents</p>
            </div>
            <div className="flex gap-3">
              {user?.role === 'PLATFORM_ADMIN' && (
                <AxiomButton
                  type="button"
                  $variant="secondary"
                  onClick={() => navigate('/document-templates')}
                  title="What each document type is read into (platform team)"
                >
                  <FileText className="w-4 h-4" />
                  Templates
                </AxiomButton>
              )}
              <AxiomButton
                type="button"
                $variant="secondary"
                onClick={fetchJobs}
              >
                <RefreshCw className={cn("w-4 h-4", isUploading && "animate-spin")} />
                Refresh Pipeline
              </AxiomButton>
            </div>
          </div>

          {importOutcome && (
            <ImportOutcomePanel
              outcome={importOutcome}
              onDismiss={() => setImportOutcome(null)}
              onOpenBlotter={() => navigate('/bond-transactions')}
              onOpenLink={to => navigate(to)}
            />
          )}

          <AxiomMetricGrid>
            <AxiomMetricCard label="Total Jobs" value={jobs.length} icon={<FileText />} tone="cyan" />
            <AxiomMetricCard label="Completed" value={jobs.filter(j => j.status === 'COMPLETED' && j.booking_status !== 'EXTRACTION_FAILED').length} icon={<CheckCircle />} tone="emerald" />
            <AxiomMetricCard label="In Progress" value={jobs.filter(j => j.status === 'PROCESSING' || j.status === 'PENDING').length} icon={<Clock />} tone="amber" />
            <AxiomMetricCard label="Needs Review" value={jobs.filter(j => j.status === 'COMPLETED' && j.booking_status !== 'EXTRACTION_FAILED' && j.approval_status === 'PENDING_REVIEW').length} icon={<AlertCircle />} tone="red" />
            {/*
              Booking Holds: jobs where parsing succeeded but the deterministic
              audit gate (e.g. interest-payment verification) rejected the post
              into IBOR. Distinct from "Needs Review" — those are extraction
              quality issues; this is a math/business-rule violation. Clicking
              filters the table to just these jobs so PMs can inspect the
              computed-vs-stated mismatch before deciding to override.
            */}
            <AxiomMetricCard
              label="Booking Holds"
              value={jobs.filter(j => j.booking_status === 'BOOKING_FAILED').length}
              icon={<AlertTriangle />}
              tone="red"
              onClick={() => setFilter(filter === 'BOOKING_HOLD' ? 'ALL' : 'BOOKING_HOLD')}
              subtext={filter === 'BOOKING_HOLD' ? 'Filter active — click to clear' : 'Audit gate rejections'}
            />
          </AxiomMetricGrid>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Upload Section */}
          <Card className="lg:col-span-1">
            <CardTitle>
              <Upload size={20} className="text-blue-400" />
              Ingestion Gateway
            </CardTitle>

            {/* Upload Mode */}
            <FormGroup>
              <Label>Upload Strategy</Label>
              <ButtonGroup>
                <ModeButton
                  active={uploadMode === 'single'}
                  onClick={() => handleUploadModeChange('single')}
                >
                  Single Document
                </ModeButton>
                <ModeButton
                  active={uploadMode === 'batch'}
                  onClick={() => handleUploadModeChange('batch')}
                >
                  Independent Batch
                </ModeButton>
                {relatedPackagesEnabled && (
                  <ModeButton
                    active={uploadMode === 'related'}
                    onClick={() => handleUploadModeChange('related')}
                  >
                    Related Package
                  </ModeButton>
                )}
              </ButtonGroup>
            </FormGroup>

            {uploadMode === 'related' && (
              <div className="grid grid-cols-1 gap-3 mb-4 rounded-md border border-blue-500/20 bg-blue-500/[0.05] p-3">
                <div>
                  <Label htmlFor="pc-package-title">Package title</Label>
                  <input
                    id="pc-package-title"
                    value={packageTitle}
                    onChange={(event) => setPackageTitle(event.target.value)}
                    placeholder="Borrower or financing name"
                    className="w-full rounded-md border border-slate-700 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <Label htmlFor="pc-package-purpose">Purpose</Label>
                  <select
                    id="pc-package-purpose"
                    value={packagePurpose}
                    onChange={(event) => setPackagePurpose(event.target.value)}
                    className="w-full rounded-md border border-slate-700 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500"
                  >
                    <option value="NEW_FINANCING">New financing</option>
                    <option value="FOLLOW_ON_DOCUMENTS">Follow-on documents</option>
                    <option value="AMENDMENT_PACKAGE">Amendment package</option>
                    <option value="ONGOING_REPORTING">Ongoing reporting</option>
                    <option value="MIXED_OR_UNKNOWN">Mixed or unknown</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="pc-expected-arrangement">Existing arrangement</Label>
                  <select
                    id="pc-expected-arrangement"
                    value={expectedArrangementId}
                    onChange={(event) => setExpectedArrangementId(event.target.value)}
                    className="w-full rounded-md border border-slate-700 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 outline-none focus:border-blue-500"
                  >
                    <option value="">New or unresolved arrangement</option>
                    {pcArrangements.map((arrangement) => (
                      <option key={arrangement.id} value={arrangement.id}>
                        {arrangement.arrangementCode} · {arrangement.borrowerName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* File Input */}
            <FormGroup>
              <Label>Select PDF Document(s)</Label>
              <FileInputWrapper
                $dragging={isFileDropActive}
                $hasFiles={selectedFiles.length > 0}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsFileDropActive(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsFileDropActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.currentTarget === e.target) {
                    setIsFileDropActive(false);
                  }
                }}
                onDrop={handleFileDrop}
              >
                <FileInput
                  type="file"
                  id="file-input"
                  data-testid="doc-upload-input"
                  accept=".pdf"
                  multiple={uploadMode !== 'single'}
                  onChange={handleFileChange}
                />
                <FileText size={32} color="#64748b" style={{ marginBottom: '0.5rem', margin: '0 auto' }} />
                <div className="mt-2">
                  <FileInputLabel htmlFor="file-input">
                    Click to select PDF file(s)
                  </FileInputLabel>
                  <UploadHint>
                    {uploadMode === 'single'
                      ? 'Drop one PDF here, or choose a file from your machine.'
                      : uploadMode === 'related'
                        ? 'Documents are extracted independently and resolved together under one approval manifest.'
                        : 'Several unrelated jobs; no relationship inference.'}
                  </UploadHint>
                </div>
                {selectedFiles.length > 0 && (
                  <>
                    <FileInfo className="bg-emerald-500/10 border border-emerald-500/20 p-2 rounded-lg mt-3">
                      {selectedFiles.length} PDF{selectedFiles.length === 1 ? '' : 's'} ready for extraction
                    </FileInfo>
                    <SelectedFilesList>
                      {selectedFiles.map((file, index) => (
                        <SelectedFileRow key={`${file.name}-${file.lastModified}-${index}`}>
                          <FileCheck size={16} color="#34d399" />
                          <div>
                            <SelectedFileName title={file.name}>{file.name}</SelectedFileName>
                            <SelectedFileMeta>{formatFileSize(file.size)}</SelectedFileMeta>
                            {uploadMode === 'related' && (
                              <SelectedFileTypeSelect
                                aria-label={`Extraction template for ${file.name}`}
                                value={selectedFileTypes[index] || documentType}
                                onChange={(event) => setSelectedFileTypes(prev =>
                                  prev.map((value, typeIndex) =>
                                    typeIndex === index ? event.target.value : value
                                  )
                                )}
                              >
                                {DOCUMENT_TEMPLATE_GROUPS.map(group => (
                                  <optgroup key={group.category} label={group.category}>
                                    {group.templates.map(template => (
                                      <option key={template.value} value={template.value}>{template.label}</option>
                                    ))}
                                  </optgroup>
                                ))}
                              </SelectedFileTypeSelect>
                            )}
                          </div>
                          <RemoveFileButton
                            type="button"
                            aria-label={`Remove ${file.name}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeSelectedFile(file.name, index);
                            }}
                          >
                            <X size={14} />
                          </RemoveFileButton>
                        </SelectedFileRow>
                      ))}
                    </SelectedFilesList>
                  </>
                )}
              </FileInputWrapper>
            </FormGroup>

            {/* Related packages use the per-file selectors above as the processing authority. */}
            {uploadMode !== 'related' && (
              <FormGroup>
                <Label>Extraction Template</Label>
                <TemplatePickerShell>
                <TemplateTrigger
                  type="button"
                  data-testid="doc-template-trigger"
                  aria-expanded={templatePickerOpen}
                  onClick={() => setTemplatePickerOpen(open => !open)}
                >
                  <div>
                    <TemplateCategory>{selectedTemplate.category}</TemplateCategory>
                    <TemplateName>{selectedTemplate.label}</TemplateName>
                    <TemplateDescription>{selectedTemplate.description}</TemplateDescription>
                  </div>
                  <TemplateBadge>Change</TemplateBadge>
                </TemplateTrigger>
                {templateNotice && (
                  <div role="status" style={{ marginTop: 6, fontSize: 12, color: '#fcd34d' }}>{templateNotice}</div>
                )}

                <AnimatePresence>
                  {templatePickerOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.14 }}
                    >
                      <TemplatePickerPanel>
                        <TemplateSearchRow>
                          <Search size={16} />
                          <TemplateSearchInput
                            value={templateSearch}
                            onChange={(e) => setTemplateSearch(e.target.value)}
                            placeholder="Search by asset class, notice type, or document code..."
                          />
                        </TemplateSearchRow>
                        <TemplateCategoryRow>
                          {templateCategories.map(category => (
                            <TemplateChip
                              key={category}
                              type="button"
                              $active={templateCategory === category}
                              onClick={() => setTemplateCategory(category)}
                            >
                              {category === 'ALL' ? 'All' : category}
                            </TemplateChip>
                          ))}
                        </TemplateCategoryRow>
                        <TemplateList>
                          {filteredTemplates.length === 0 && (
                            <TemplateEmpty>No templates match this search.</TemplateEmpty>
                          )}
                          {filteredTemplates.map(template => (
                            <TemplateOption
                              key={template.value}
                              type="button"
                              data-testid="doc-template-option"
                              data-template-label={template.label}
                              $active={documentType === template.value}
                              onClick={() => {
                                setDocumentType(template.value);
                                setTemplateNotice(null);
                                setTemplatePickerOpen(false);
                                setTemplateSearch('');
                              }}
                            >
                              <div>
                                <TemplateOptionMeta>{template.category}</TemplateOptionMeta>
                                <TemplateName>{template.label}</TemplateName>
                                <TemplateDescription>{template.description}</TemplateDescription>
                              </div>
                              {documentType === template.value && (
                                <CheckCircle size={18} color="#34d399" />
                              )}
                            </TemplateOption>
                          ))}
                        </TemplateList>
                      </TemplatePickerPanel>
                    </motion.div>
                  )}
                </AnimatePresence>
                </TemplatePickerShell>
              </FormGroup>
            )}

            {/* Upload Button */}
            <UploadButton
              data-testid="doc-upload-start"
              onClick={handleUpload}
              disabled={isUploading || selectedFiles.length === 0}
              className="mt-4"
            >
              {isUploading ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Bot size={18} />
                  {uploadMode === 'single'
                    ? 'Start AI Extraction'
                    : uploadMode === 'related'
                      ? 'Create Package & Extract'
                      : 'Start Independent Batch'}
                </>
              )}
            </UploadButton>
          </Card>

          {/* Jobs List */}
          <Card className="lg:col-span-2">
            <div className="flex justify-between items-center mb-6">
              <CardTitle style={{ margin: 0 }}>
                <Activity size={20} className="text-emerald-400" />
                Active Processing Pipeline
              </CardTitle>
              <div className="flex flex-wrap gap-2 justify-end">
                {['ALL', 'PROCESSING', 'COMPLETED', 'FAILED', 'BOOKING_HOLD'].map((status) => (
                  <FilterButton
                    key={status}
                    active={filter === status}
                    onClick={() => setFilter(status)}
                  >
                    {status === 'BOOKING_HOLD' ? 'HOLDS' : status}
                  </FilterButton>
                ))}
              </div>
            </div>

            {/* Jobs Table */}
            {jobs.length > 0 ? (
              <TableScroller>
                <Table>
                  <colgroup>
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '300px' }} />
                    <col style={{ width: '150px' }} />
                    <col />
                    <col style={{ width: '176px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <Th>Pipeline Status</Th>
                      <Th>Document Entity</Th>
                      <Th>Progress</Th>
                      <Th>CrewAI Steps</Th>
                      <ActionsTh>Operations</ActionsTh>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <React.Fragment key={job.job_id}>
                      <tr className="group hover:bg-white/5 transition-colors">
                        <Td>
                          <StatusStack>
                            <StatusBadge status={job.status} data-testid="doc-job-status">
                              {getStatusIcon(job.status)}
                              {job.status}
                            </StatusBadge>
                            {job.booking_status === 'BOOKING_FAILED' && (
                              <HoldBadge
                                title="Booking rejected by audit gate — see error row below"
                              >
                                <AlertTriangle size={10} /> Hold
                              </HoldBadge>
                            )}
                            {job.booking_status === 'EXTRACTION_FAILED' && (
                              <HoldBadge title="Document extraction failed — use Reprocess to retry the original job">
                                <XCircle size={10} /> Extraction Failed
                              </HoldBadge>
                            )}
                          </StatusStack>
                        </Td>
                        <Td>
                          <DocumentNameCell title={job.document_name}>
                            {job.document_name}
                          </DocumentNameCell>
                          {job.document_type && (
                            <DocumentMeta>{jobTypeLabel(job)}</DocumentMeta>
                          )}
                        </Td>
                        <Td>
                          <ProgressCell>
                            <ProgressBar>
                              <ProgressFill percent={job.progress_percentage} />
                            </ProgressBar>
                            <ProgressValue>
                              {job.progress_percentage}%
                            </ProgressValue>
                          </ProgressCell>
                        </Td>
                        <Td>
                          {renderPipelineSteps(job)}
                          <StageLabel title={job.current_stage || undefined}>
                            {job.current_stage || '-'}
                          </StageLabel>
                        </Td>
                        <ActionsTd>
                          <OperationsCell>
                            {job.status === 'COMPLETED' && job.booking_status !== 'EXTRACTION_FAILED' && (
                              <button
                                onClick={() => viewExtractedData(job.job_id)}
                                title="View AI Insights"
                                className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 transition-all"
                              >
                                <Sparkles size={14} />
                              </button>
                            )}
                            {job.status === 'COMPLETED' && job.booking_status !== 'EXTRACTION_FAILED' && REVIEWABLE_DOCUMENT_TYPES.has(job.document_type || '') && (
                              <button
                                onClick={() => navigate(agreementReviewPath(job.job_id))}
                                title="Review every term against the document"
                                className="p-1.5 rounded-lg bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 border border-sky-500/20 transition-all"
                              >
                                <FileSearch size={14} />
                              </button>
                            )}
                            {(job.status === 'FAILED' || job.booking_status === 'EXTRACTION_FAILED'
                              || (job.status === 'COMPLETED' && job.approval_status === 'REJECTED'
                                && (!job.booking_status || job.booking_status === 'UNBOOKED'))
                              // F-DT-004: a booking hold booked nothing; read the document again.
                              || (job.status === 'COMPLETED' && job.booking_status === 'BOOKING_FAILED')) && (
                              <button
                                onClick={() => handleReprocessJob(job)}
                                disabled={reprocessingJobId === job.job_id}
                                title={job.booking_status === 'BOOKING_FAILED'
                                  ? 'Read the document again: its booking was refused and nothing was booked (no duplicate upload)'
                                  : job.approval_status === 'REJECTED'
                                    ? 'Reprocess the rejected extraction on the original job (no duplicate upload)'
                                    : 'Reprocess the original failed job (no duplicate upload)'}
                                className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 transition-all disabled:opacity-50 disabled:cursor-wait"
                              >
                                <RefreshCw
                                  size={14}
                                  className={reprocessingJobId === job.job_id ? 'animate-spin' : undefined}
                                />
                              </button>
                            )}
                            <button
                              onClick={() => viewJobDetails(job.job_id)}
                              title="Debug Trace"
                              className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white border border-white/5 transition-all"
                            >
                              <Eye size={14} />
                            </button>
                            {job.status === 'COMPLETED' && job.approval_status === 'APPROVED' && ['CAPITAL_CALL', 'DISTRIBUTION_NOTICE'].includes(job.document_type || '') && (
                              <button
                                onClick={() => handleApplyToOms(job.job_id)}
                                title={job.oms_application_status === 'APPLIED'
                                  ? 'Already advanced in OMS workflow'
                                  : job.document_type === 'AMENDMENT'
                                    ? 'Apply Amendment (portfolio resolved from existing loan)'
                                    : (job.document_type === 'LOAN_NOTICE' || job.document_type === 'PAYDOWN_NOTICE')
                                      ? 'Apply Notice to Portfolio'
                                      : 'Create and advance commitment workflow'}
                                className={`p-1.5 rounded-lg border transition-all ${
                                  job.oms_application_status === 'APPLIED'
                                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                    : 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border-blue-500/20'
                                }`}
                              >
                                <Download size={14} />
                              </button>
                            )}
                            {job.status === 'COMPLETED'
                              && ['CAPITAL_CALL', 'DISTRIBUTION_NOTICE'].includes(job.document_type || '')
                              && job.oms_application_status === 'APPLIED'
                              && job.oms_application_result?.next_required_action
                              && (
                              <button
                                onClick={() => handleFinalizeToOms(job.job_id)}
                                title={job.oms_finalization_status === 'COMPLETED'
                                  ? 'Cash confirmation already completed'
                                  : job.oms_application_result?.next_required_action === 'CONFIRM_FUNDING'
                                    ? 'Confirm Funding'
                                    : 'Confirm Receipt'}
                                className={`p-1.5 rounded-lg border transition-all ${
                                  job.oms_finalization_status === 'COMPLETED'
                                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                    : 'bg-violet-500/10 text-violet-400 hover:bg-violet-500/20 border-violet-500/20'
                                }`}
                              >
                                <CheckCircle size={14} />
                              </button>
                            )}
                            {job.status === 'COMPLETED'
                              && ['CAPITAL_CALL', 'DISTRIBUTION_NOTICE'].includes(job.document_type || '')
                              && job.oms_finalization_status === 'COMPLETED'
                              && (
                              <button
                                onClick={() => handleReconcileToOms(job.job_id)}
                                title={job.oms_reconciliation_status === 'COMPLETED'
                                  ? 'Reconciliation already completed'
                                  : 'Reconcile Workflow'}
                                className={`p-1.5 rounded-lg border transition-all ${
                                  job.oms_reconciliation_status === 'COMPLETED'
                                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                    : 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border-amber-500/20'
                                }`}
                              >
                                <RefreshCw size={14} />
                              </button>
                            )}
                            {job.status === 'COMPLETED' && job.booking_status !== 'EXTRACTION_FAILED' && job.approval_status === 'APPROVED' && !['CAPITAL_CALL', 'DISTRIBUTION_NOTICE'].includes(job.document_type || '') && (
                              <button
                                data-testid="doc-import-oms"
                                onClick={() => handleImportToOms(job.job_id, job.document_type)}
                                title={
                                  ['LOAN_NOTICE', 'PAYDOWN_NOTICE', 'LSTA_DRAWDOWN_NOTICE', 'LSTA_PAYDOWN_NOTICE', 'LSTA_RATE_RESET'].includes(job.document_type || '')
                                    ? 'Book Notice to IBOR'
                                    : ['AMENDMENT', 'LSTA_AMENDMENT'].includes(job.document_type || '')
                                      ? 'Apply Amendment to OMS'
                                      : 'Import to OMS'
                                }
                                className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/20 transition-all"
                              >
                                <Download size={14} />
                              </button>
                            )}
                          </OperationsCell>
                        </ActionsTd>
                      </tr>
                      {job.booking_status === 'BOOKING_FAILED' && job.last_booking_error && (
                        <tr className="bg-red-500/[0.04] border-l-2 border-l-red-500/60">
                          <td colSpan={5} className="px-4 py-3">
                            <div className="flex items-start gap-3">
                              <AlertTriangle size={14} className="text-red-400 mt-0.5 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-red-300 mb-1">
                                  Booking Hold — Audit Gate Rejection
                                </div>
                                <div className="text-xs text-slate-300 font-mono leading-relaxed break-words">
                                  {job.last_booking_error}
                                </div>
                                {(job.retry_count !== undefined || job.next_retry_at) && (
                                  <div className="text-[10px] text-slate-500 mt-1.5 font-medium">
                                    {job.retry_count !== undefined && <>Retry attempts: <span className="text-slate-300">{job.retry_count}</span></>}
                                    {job.next_retry_at && <> · Next retry: <span className="text-slate-300">{new Date(job.next_retry_at).toLocaleString()}</span></>}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {job.booking_status === 'EXTRACTION_FAILED' && (
                        <tr className="bg-red-500/[0.04] border-l-2 border-l-red-500/60">
                          <td colSpan={5} className="px-4 py-3">
                            <div className="flex items-start gap-3">
                              <XCircle size={14} className="text-red-400 mt-0.5 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-red-300 mb-1">
                                  Extraction Failed — Reprocess Available
                                </div>
                                <div className="text-xs text-slate-300 font-mono leading-relaxed break-words">
                                  {job.error || job.result?.error || 'The extraction workflow returned no structured data.'}
                                </div>
                                <div className="text-[10px] text-slate-500 mt-1.5 font-medium">
                                  Retry attempts: <span className="text-slate-300">{job.retry_count || 0}</span>
                                  {' · '}Use the amber reprocess action; do not upload the PDF again.
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </TableScroller>
            ) : (
              <EmptyState>
                <div className="w-20 h-20 rounded-full bg-slate-900 flex items-center justify-center mx-auto mb-4 border border-white/5">
                  <Bot size={40} className="text-slate-700" />
                </div>
                <p className="text-slate-500 font-medium">No active extraction jobs.</p>
                <p className="text-[10px] text-slate-600 uppercase tracking-widest mt-1">Upload a document to trigger the AI pipeline</p>
              </EmptyState>
            )}
          </Card>
        </div>

        {relatedPackagesEnabled && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <CardTitle style={{ margin: 0 }}>
              <Building2 size={20} className="text-blue-400" />
              Related Financing Packages
            </CardTitle>
            <AxiomButton type="button" $variant="secondary" onClick={fetchDocumentPackages}>
              <RefreshCw className={cn('w-4 h-4', packageBusy && 'animate-spin')} />
              Refresh Queue
            </AxiomButton>
          </div>

          <div className="grid min-h-[420px] grid-cols-1 gap-5 xl:grid-cols-[320px_minmax(0,1fr)]">
            <div className="border-r-0 border-slate-800 pr-0 xl:border-r xl:pr-5">
              <div className="space-y-2">
                {packageQueueError && (
                  <div role="alert" className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">
                    {packageQueueError}
                  </div>
                )}
                {documentPackages.map((pkg) => (
                  <button
                    key={pkg.packageId}
                    type="button"
                    onClick={() => openPackageWorkspace(pkg.packageId, pkg.currentResolutionVersion)}
                    className={`w-full rounded-md border p-3 text-left transition-colors ${
                      packageWorkspace?.packageId === pkg.packageId
                        ? 'border-blue-500/60 bg-blue-500/10'
                        : 'border-slate-800 bg-slate-950/35 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="min-w-0 break-words text-sm font-semibold text-slate-100">{pkg.title}</span>
                      <span className="shrink-0 text-[10px] font-bold uppercase text-blue-300">{pkg.state.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="mt-2 flex gap-3 text-xs text-slate-500">
                      <span>{pkg.documentCount} docs</span>
                      <span>{pkg.purpose.replace(/_/g, ' ')}</span>
                    </div>
                  </button>
                ))}
                {!packageQueueError && documentPackages.length === 0 && (
                  <div className="py-12 text-center text-sm text-slate-500">No related packages in the queue.</div>
                )}
              </div>
            </div>

            {!packageWorkspace ? (
              <div className="grid place-items-center text-sm text-slate-500">
                Select a package to review its evidence and proposed financing structure.
              </div>
            ) : (
              <div className="min-w-0 space-y-5">
                <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-800 pb-4">
                  <div className="min-w-0">
                    <div className="break-words text-lg font-semibold text-white">{packageWorkspace.title}</div>
                    <div className="mt-1 font-mono text-xs text-slate-500">{packageWorkspace.packageId}</div>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-400">
                      <span>Purpose: {packageWorkspace.purpose.replace(/_/g, ' ')}</span>
                      <span>Owner: {packageWorkspace.preparer}</span>
                      <span>Version: {packageWorkspace.currentResolutionVersion || 'Not resolved'}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    {['DRAFT', 'UPLOADING', 'EXTRACTING', 'PARTIAL_EXTRACTION', 'CHANGES_REQUESTED'].includes(packageWorkspace.state) && (
                      <AxiomButton
                        type="button"
                        $variant="secondary"
                        onClick={resolvePackage}
                        disabled={packageBusy || packageWorkspace.members.some(member =>
                          !['COMPLETED', 'FAILED'].includes(member.currentExtractionStatus)
                        )}
                      >
                        <Zap className="w-4 h-4" /> Resolve
                      </AxiomButton>
                    )}
                    {packageResolution?.status === 'NEEDS_REVIEW' && (
                      <AxiomButton type="button" $variant="primary" onClick={submitPackage} disabled={packageBusy}>
                        <Send className="w-4 h-4" /> Submit
                      </AxiomButton>
                    )}
                    {packageResolution?.status === 'SUBMITTED' && (
                      <AxiomButton type="button" $variant="primary" onClick={approvePackage} disabled={packageBusy}>
                        <Shield className="w-4 h-4" /> Checker Approve
                      </AxiomButton>
                    )}
                    {['APPROVED', 'PROMOTION_FAILED'].includes(packageResolution?.status || '') && (
                      <>
                        <AxiomButton type="button" $variant="secondary" onClick={startPackageCorrection} disabled={packageBusy}>
                          <Edit3 className="w-4 h-4" /> New Version
                        </AxiomButton>
                        <AxiomButton type="button" $variant="primary" onClick={promotePackage} disabled={packageBusy}>
                          <CheckCircle className="w-4 h-4" /> Promote References
                        </AxiomButton>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <div className="mb-2 text-xs font-bold uppercase text-slate-400">Document rail</div>
                  <div className="overflow-x-auto rounded-md border border-slate-800">
                    <table className="w-full table-fixed text-left text-xs">
                      <colgroup><col className="w-[28%]" /><col className="w-[21%]" /><col className="w-[14%]" /><col className="w-[14%]" /><col className="w-[15%]" /><col className="w-[8%]" /></colgroup>
                      <thead className="bg-slate-950/60 text-slate-500"><tr>
                        <th className="p-2">Document</th><th>Role</th><th>Execution</th><th>Completeness</th><th>Cluster</th><th>Hash</th>
                      </tr></thead>
                      <tbody>
                        {packageWorkspace.members.map((member) => (
                          <tr key={member.jobId} className="border-t border-slate-800 text-slate-300">
                            <td className="break-words p-2 font-medium" title={member.documentName}>{member.documentName}</td>
                            <td className="pr-2">
                              <select
                                value={member.approvedRole || member.proposedRole || 'REFERENCE'}
                                onChange={(event) => savePackageDecision([], [], [{
                                  jobId: member.jobId,
                                  approvedRole: event.target.value,
                                  reason: 'Document role reviewed'
                                }])}
                                disabled={packageBusy || !packageResolution || !['NEEDS_REVIEW', 'CHANGES_REQUESTED'].includes(packageResolution.status)}
                                className="w-full rounded border border-slate-700 bg-slate-950 px-1.5 py-1 text-[11px] text-slate-200 disabled:opacity-70"
                              >
                                <option value="EXECUTED_CREDIT_AGREEMENT">Executed agreement</option>
                                <option value="AMENDMENT">Amendment</option>
                                <option value="FEE_LETTER">Fee letter</option>
                                <option value="DRAW_NOTICE">Draw notice</option>
                                <option value="PAYDOWN_NOTICE">Paydown notice</option>
                                <option value="RATE_RESET_NOTICE">Rate reset</option>
                                <option value="COMPLIANCE_CERTIFICATE">Compliance certificate</option>
                                <option value="REFERENCE">Reference</option>
                              </select>
                            </td>
                            <td className="pr-2">
                              <select
                                value={member.executionStatus}
                                onChange={(event) => savePackageDecision([], [], [{
                                  jobId: member.jobId,
                                  executionStatus: event.target.value,
                                  reason: 'Execution status reviewed'
                                }])}
                                disabled={packageBusy || !packageResolution || !['NEEDS_REVIEW', 'CHANGES_REQUESTED'].includes(packageResolution.status)}
                                className="w-full rounded border border-slate-700 bg-slate-950 px-1.5 py-1 text-[11px] text-slate-200 disabled:opacity-70"
                              >
                                <option value="UNKNOWN">Unknown</option><option value="DRAFT">Draft</option><option value="EXECUTED">Executed</option>
                              </select>
                            </td>
                            <td className="pr-2">
                              <select
                                value={member.completenessStatus}
                                onChange={(event) => savePackageDecision([], [], [{
                                  jobId: member.jobId,
                                  completenessStatus: event.target.value,
                                  reason: 'Completeness reviewed'
                                }])}
                                disabled={packageBusy || !packageResolution || !['NEEDS_REVIEW', 'CHANGES_REQUESTED'].includes(packageResolution.status)}
                                className="w-full rounded border border-slate-700 bg-slate-950 px-1.5 py-1 text-[11px] text-slate-200 disabled:opacity-70"
                              >
                                <option value="UNKNOWN">Unknown</option><option value="COMPLETE">Complete</option><option value="PARTIAL">Partial</option><option value="EXCERPT">Excerpt</option>
                              </select>
                            </td>
                            <td className="truncate pr-2 font-mono" title={member.clusterKey}>{member.clusterKey || '-'}</td>
                            <td className="font-mono" title={member.contentHash}>{member.contentHash.slice(0, 8)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {packageResolution && (
                  <>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                      {[
                        ['References', packageResolution.dryRun.referenceCreates || 0],
                        ['Facilities', packageResolution.dryRun.facilityCreates || 0],
                        ['Blocking conflicts', packageResolution.conflicts.filter(conflict => conflict.blocking && !conflict.resolutionDecision).length],
                        ['Immediate positions', packageResolution.dryRun.immediatePositionImpact || 0]
                      ].map(([label, value]) => (
                        <div key={String(label)} className="rounded-md border border-slate-800 bg-slate-950/35 p-3">
                          <div className="text-[10px] font-bold uppercase text-slate-500">{label}</div>
                          <div className="mt-1 text-lg font-semibold text-slate-100">{value}</div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <div className="mb-2 text-xs font-bold uppercase text-slate-400">Arrangement and facility proposals</div>
                      <div className="space-y-2">
                        {packageResolution.entities.map((entity) => (
                          <div key={entity.proposalKey} className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 py-2">
                            <div className="min-w-0">
                              <div className="break-words text-sm font-medium text-slate-200">
                                {entity.entityType === 'ARRANGEMENT'
                                  ? entity.proposedPayload.arrangementName
                                  : entity.proposedPayload.loanName || entity.proposedPayload.facilityCode}
                              </div>
                              <div className="mt-1 text-xs text-slate-500">
                                {entity.entityType} · {entity.decision.replace(/_/g, ' ')}
                                {entity.confidenceScore != null && entity.evidenceFactors?.length
                                  ? ` · ${Math.round(entity.confidenceScore * 100)}% from ${entity.evidenceFactors.map(factor => factor.factor.replace(/_/g, ' ')).join(', ')}`
                                  : ' · Human review required'}
                              </div>
                            </div>
                            {entity.blockingReview && !entity.reviewerDecision ? (
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => savePackageDecision([{ proposalKey: entity.proposalKey, decision: 'APPROVE', reason: 'Evidence and target scope reviewed' }])}
                                  className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300"
                                >Approve</button>
                                <button
                                  type="button"
                                  onClick={() => savePackageDecision([{ proposalKey: entity.proposalKey, decision: 'REJECT', reason: 'Proposal rejected during structure review' }])}
                                  className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-300"
                                >Reject</button>
                              </div>
                            ) : (
                              <span className="text-xs font-bold uppercase text-slate-400">{entity.reviewerDecision || entity.decision}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 text-xs font-bold uppercase text-slate-400">Relationship and applicability proposals</div>
                      <div className="space-y-2">
                        {packageResolution.relationships.map((relationship) => {
                          const source = packageWorkspace.members.find(member => member.jobId === relationship.jobId);
                          const evidence = relationship.evidenceFactors?.map(factor => factor.factor.replace(/_/g, ' ')).join(', ');
                          return (
                            <div key={relationship.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 py-2">
                              <div className="min-w-0">
                                <div className="break-words text-sm font-medium text-slate-200">
                                  {relationship.relationshipType.replace(/_/g, ' ')}
                                  {source ? ` · ${source.documentName}` : ''}
                                </div>
                                <div className="mt-1 break-words text-xs text-slate-500">
                                  {relationship.fromProposalKey || '-'} → {relationship.toProposalKey || '-'}
                                  {relationship.confidenceScore != null && evidence
                                    ? ` · ${Math.round(relationship.confidenceScore * 100)}% from ${evidence}`
                                    : ''}
                                </div>
                              </div>
                              {relationship.blockingReview && !relationship.reviewerDecision ? (
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => savePackageDecision([], [], [], [{
                                      relationshipId: relationship.id,
                                      decision: 'APPROVE',
                                      reason: 'Relationship scope and evidence reviewed'
                                    }])}
                                    className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300"
                                  >Approve</button>
                                  <button
                                    type="button"
                                    onClick={() => savePackageDecision([], [], [], [{
                                      relationshipId: relationship.id,
                                      decision: 'REJECT',
                                      reason: 'Relationship proposal rejected during review'
                                    }])}
                                    className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-300"
                                  >Reject</button>
                                </div>
                              ) : (
                                <span className="text-xs font-bold uppercase text-slate-400">
                                  {relationship.reviewerDecision || 'Deterministic'}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 text-xs font-bold uppercase text-slate-400">Conflicts and reconciliation</div>
                      {packageResolution.conflicts.length === 0 ? (
                        <div className="border-l-2 border-emerald-500 py-2 pl-3 text-sm text-slate-300">No resolver conflicts.</div>
                      ) : packageResolution.conflicts.map((conflict) => (
                        <div key={conflict.id} className="mb-2 flex flex-wrap items-start justify-between gap-3 border-l-2 border-red-500 bg-red-500/[0.04] p-3">
                          <div className="min-w-0">
                            <div className="text-xs font-bold uppercase text-red-300">{conflict.conflictClass.replace(/_/g, ' ')}</div>
                            <div className="mt-1 break-words text-sm text-slate-300">{conflict.description}</div>
                            {conflict.resolutionReason && <div className="mt-1 text-xs text-slate-500">{conflict.resolutionReason}</div>}
                          </div>
                          {!conflict.resolutionDecision && (
                            <div className="flex flex-wrap justify-end gap-2">
                              {(conflict.involvedAssertionIds || []).map((assertionId) => {
                                const assertion = packageResolution.assertions.find(item => item.id === assertionId);
                                const source = packageWorkspace.members.find(member => member.jobId === assertion?.jobId);
                                return (
                                  <button
                                    key={assertionId}
                                    type="button"
                                    title={source?.documentName || `Assertion ${assertionId}`}
                                    onClick={async () => {
                                      const reason = await askReason('Why is this source authoritative?',
                                        'Recorded with the conflict decision.', 'Select as authority');
                                      if (reason) savePackageDecision([], [{
                                        conflictId: conflict.id,
                                        decision: 'SELECT_AUTHORITY',
                                        selectedAuthorityId: assertionId,
                                        reason: reason.trim()
                                      }]);
                                    }}
                                    className="max-w-48 truncate rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-300"
                                  >Use {source?.documentName || assertion?.assertionType || assertionId}</button>
                                );
                              })}
                              {(conflict.involvedAssertionIds || []).length === 0 && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const reason = await askReason('Why can this evidence be excluded?',
                                      'Recorded with the conflict decision.', 'Exclude');
                                    if (reason) savePackageDecision([], [{
                                      conflictId: conflict.id,
                                      decision: 'EXCLUDE_DOCUMENT',
                                      reason: reason.trim()
                                    }]);
                                  }}
                                  className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-300"
                                >Exclude evidence</button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div>
                      <div className="mb-2 text-xs font-bold uppercase text-slate-400">Evidence matrix</div>
                      <div className="max-h-72 overflow-auto rounded-md border border-slate-800">
                        <table className="w-full table-fixed text-left text-xs">
                          <colgroup><col className="w-[24%]" /><col className="w-[28%]" /><col className="w-[16%]" /><col className="w-[20%]" /><col className="w-[12%]" /></colgroup>
                          <thead className="sticky top-0 bg-slate-950 text-slate-500"><tr>
                            <th className="p-2">Field</th><th>Proposed value</th><th>Scope</th><th>Document</th><th>Authority</th>
                          </tr></thead>
                          <tbody>
                            {packageResolution.assertions.map((assertion) => (
                              <tr key={assertion.id} className="border-t border-slate-800 text-slate-300">
                                <td className="break-words p-2 font-medium">{assertion.assertionType.replace(/_/g, ' ')}</td>
                                <td className="break-words pr-2 font-mono text-[11px]">
                                  {typeof assertion.proposedValue === 'object'
                                    ? JSON.stringify(assertion.proposedValue)
                                    : String(assertion.proposedValue ?? '-')}
                                </td>
                                <td>{assertion.targetScope || '-'}</td>
                                <td className="truncate pr-2" title={packageWorkspace.members.find(member => member.jobId === assertion.jobId)?.documentName}>
                                  {packageWorkspace.members.find(member => member.jobId === assertion.jobId)?.documentName || assertion.jobId.slice(0, 8)}
                                </td>
                                <td>{assertion.authorityRank ?? '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="border-t border-slate-800 pt-3 text-xs text-slate-400">
                      {packageResolution.dryRun.message || 'No immediate trade, position, lot, or cash impact.'}
                      {packageResolution.promotionManifestHash && (
                        <span className="ml-3 font-mono">Manifest {packageResolution.promotionManifestHash.slice(0, 12)}</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Card>
        )}
      </div>

      {/* Raw Job Details Modal */}
      {selectedJob && (
        <Modal onClick={() => setSelectedJob(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Raw Job Details</ModalTitle>
              <CloseButton onClick={() => setSelectedJob(null)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>
            <CodeBlock>
              {JSON.stringify(selectedJob, null, 2)}
            </CodeBlock>
          </ModalContent>
        </Modal>
      )}

      {portfolioPick && (
        <PortfolioPickerDialog
          docType={portfolioPick.docType}
          documentName={portfolioPick.documentName}
          portfolios={portfolios}
          onResolve={choice => { portfolioPick.resolve(choice); setPortfolioPick(null); }}
        />
      )}

      {reasonAsk && (
        <ReasonDialog
          title={reasonAsk.title}
          description={reasonAsk.description}
          confirmLabel={reasonAsk.confirmLabel}
          onResolve={reason => { reasonAsk.resolve(reason); setReasonAsk(null); }}
        />
      )}

      {llDealImportResult && (
        <Modal onClick={() => setLlDealImportResult(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 920 }}>
            <ModalHeader>
              <div>
                <ModalTitle>Leveraged Loan Deal Defined</ModalTitle>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 4 }}>
                  {llDealImportResult.dealCode || llDealImportResult.dealName || `Deal #${llDealImportResult.dealId}`}
                </div>
              </div>
              <CloseButton onClick={() => setLlDealImportResult(null)}><X size={20} /></CloseButton>
            </ModalHeader>
            <ApprovalBanner status="APPROVED">
              <div>
                <strong style={{ color: '#86efac' }}>Reference data posted</strong>
                <div style={{ color: '#cbd5e1', fontSize: '0.78rem', marginTop: 3 }}>
                  No trade, portfolio allocation, position, lot or cash was created.
                </div>
              </div>
              <CheckCircle size={20} color="#22c55e" />
            </ApprovalBanner>
            <div style={{ overflowX: 'auto', border: '1px solid #334155', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead><tr style={{ color: '#94a3b8', textAlign: 'left' }}>
                  <th style={{ padding: 9 }}>Tranche</th><th>Type</th><th>CUSIP</th><th>State</th><th>Actions</th>
                </tr></thead>
                <tbody>{(llDealImportResult.tranches || []).map(tranche => (
                  <tr key={tranche.loanRecordId} style={{ borderTop: '1px solid #1e293b', color: '#e2e8f0' }}>
                    <td style={{ padding: 9, fontWeight: 700 }}>{tranche.trancheCode || tranche.loanId}</td>
                    <td>{String(tranche.facilityType || '').replace(/_/g, ' ')}</td>
                    <td>{tranche.cusip || '-'}</td>
                    <td>{tranche.facilityState || '-'}</td>
                    <td style={{ padding: 6 }}><div style={{ display: 'flex', gap: 6 }}>
                      <CloseButton title="Open Loan 360" onClick={() => navigate(loan360Path('LEVERAGED_LOAN', tranche.loanRecordId))}><BookOpen size={16} /></CloseButton>
                      {tranche.cusip && <CloseButton title="Open Security Master" onClick={() => navigate(securityMasterPath(tranche.cusip!))}><Database size={16} /></CloseButton>}
                      <CloseButton title="Create acquisition ticket" onClick={() => navigate(leveragedLoanTicketPath(tranche.cusip))}><Ticket size={16} /></CloseButton>
                    </div></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <ButtonSecondary onClick={() => setLlDealImportResult(null)}>Close</ButtonSecondary>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Extracted Loan Data Modal */}
      {extractedDataModal && (
        <Modal onClick={closeExtractedModal}>
          <LargeModal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalTitle>{(() => {
                  const dt = extractedDataModal.document_type || '';
                  if (dt === 'REAL_ESTATE_ACQUISITION') return 'Extracted Real Estate Acquisition Data';
                  if (dt === 'RE_APPRAISAL') return 'Extracted Property Appraisal Data';
                  if (dt === 'REAL_ESTATE_LEASE') return 'Extracted Lease Data';
                  if (dt === 'REAL_ESTATE_DISPOSITION') return 'Extracted Disposition Data';
                  if (dt === 'RE_LOAN_AGREEMENT') return 'Extracted CRE Loan Agreement Data';
                  if (dt === 'RE_LOAN_PAYDOWN') return 'Extracted CRE Loan Paydown Data';
                  if (dt === 'RE_LOAN_RATE_RESET') return 'Extracted CRE Loan Rate-Reset Data';
                  if (dt === 'RE_LOAN_AMENDMENT') return 'Extracted CRE Loan Amendment Data';
                  if (dt === 'RE_LOAN_PAYOFF') return 'Extracted CRE Loan Payoff Data';
                  if (dt === 'RE_LOAN_CONSTRUCTION_DRAW') return 'Extracted CRE Construction Draw Data';
                  if (dt === 'CLO_OFFERING_MEMO' || dt === 'CLO_OFFERING_CIRCULAR') return 'Extracted CLO Equity Offering Data';
                  if (dt === 'CLO_INDENTURE') return 'Extracted CLO Indenture Data';
                  if (dt === 'CLO_TRUSTEE_REPORT' || dt === 'CLO_MONTHLY_REPORT') return 'Extracted CLO Trustee Report Data';
                  if (dt === 'ABF_CREDIT_AGREEMENT') return 'Extracted ABF Credit Agreement Data';
                  if (dt === 'ABF_AMENDMENT') return 'Extracted ABF Amendment Data';
                  if (dt === 'ABF_DRAWDOWN_NOTICE') return 'Extracted ABF Drawdown Data';
                  if (dt === 'ABF_BORROWING_BASE_CERTIFICATE') return 'Extracted ABF Borrowing-Base Data';
                  if (dt === 'ABF_SERVICER_REPORT') return 'Extracted ABF Servicer Report Data';
                  if (dt === 'ABF_REPAYMENT_NOTICE') return 'Extracted ABF Repayment Data';
                  if (dt === 'ABF_COMPLIANCE_CERTIFICATE') return 'Extracted ABF Compliance Certificate';
                  if (dt === 'ABF_PAYING_AGENT_REPORT') return "Extracted Paying Agent's Statement";
                  return 'Extracted Loan Data';
                })()}</ModalTitle>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                  {extractedDataModal.document_name}
                </div>
              </div>
              {REVIEWABLE_DOCUMENT_TYPES.has(extractedDataModal.document_type || '') && (
                <button
                  onClick={() => navigate(agreementReviewPath(extractedDataModal.job_id))}
                  className="ml-auto mr-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-sky-500/10 text-sky-300 hover:bg-sky-500/20 border border-sky-500/30 text-sm font-semibold transition-all"
                >
                  <FileSearch size={14} /> Review against the document
                </button>
              )}
              <CloseButton onClick={closeExtractedModal}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            {/* Decision Banner */}
            <ApprovalBanner status={extractedDataModal.approval_status}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <DecisionBadge decision={extractedDataModal.manager_decision}>
                  {extractedDataModal.manager_decision === 'APPROVE_FOR_EXPORT' && <CheckCircle size={16} />}
                  {extractedDataModal.manager_decision === 'NEEDS_HUMAN_REVIEW' && <AlertTriangle size={16} />}
                  {extractedDataModal.manager_decision === 'REJECT' && <XCircle size={16} />}
                  {extractedDataModal.manager_decision.replace(/_/g, ' ')}
                </DecisionBadge>
                {(() => {
                  const checks = extractedDataModal.document_type === 'ABF_PAYING_AGENT_REPORT'
                    ? payingAgentStatementChecks(extractedDataModal.extracted_data) : null;
                  if (checks) {
                    const passed = checks.filter(c => c.passed).length;
                    return (
                      <span style={{ fontSize: '0.875rem', color: passed === checks.length ? '#4ade80' : '#fbbf24' }}>
                        Statement checks: <strong>{passed} of {checks.length}</strong> passed
                      </span>
                    );
                  }
                  return extractedDataModal.data_quality_score ? (
                    <span style={{ fontSize: '0.875rem', color: '#e2e8f0' }}>
                      DQ Score: <strong>{extractedDataModal.data_quality_score}%</strong>
                    </span>
                  ) : null;
                })()}
              </div>
              <DecisionBadge decision={extractedDataModal.approval_status}>
                {extractedDataModal.approval_status.replace(/_/g, ' ')}
              </DecisionBadge>
            </ApprovalBanner>
            <TemplateReadBanner validation={extractedDataModal.template_validation} />
            {extractedDataModal.booking_precheck && (
              <DataSection style={{ margin: '0 1.25rem 1rem', borderColor: 'rgba(245, 158, 11, 0.45)' }}>
                <SectionTitle><AlertTriangle size={16} /> OMS Booking Precheck</SectionTitle>
                <DataRow><DataLabel>Status</DataLabel><DataValue>{extractedDataModal.booking_precheck.status || 'PENDING_REVIEW'}</DataValue></DataRow>
                <DataRow><DataLabel>IBOR Ready</DataLabel><DataValue>{extractedDataModal.booking_precheck.ibor_ready ? 'Yes' : 'No — approval and mapping controls pending'}</DataValue></DataRow>
                <DataRow><DataLabel>Buyer Mapping</DataLabel><DataValue>{extractedDataModal.booking_precheck.buyer_mapping_status || 'NOT_EVALUATED'}</DataValue></DataRow>
                <CovenantDetail style={{ marginTop: 8 }}>{extractedDataModal.booking_precheck.message}</CovenantDetail>
              </DataSection>
            )}

            {/* Tabs */}
            <TabContainer>
              <Tab active={modalTab === 'data'} onClick={() => setModalTab('data')}>
                Extracted Data
              </Tab>
              {(extractedDataModal.linked_loan_record_id || extractedDataModal.linked_commitment_id) && (
                <Tab
                  active={modalTab === 'workspace'}
                  onClick={() => {
                    setModalTab('workspace');
                    if (extractedDataModal.linked_loan_record_id) {
                      fetchFacilityWorkspace(extractedDataModal.linked_loan_record_id);
                    }
                    if (extractedDataModal.linked_commitment_id) {
                      fetchCommitmentWorkspace(extractedDataModal.linked_commitment_id);
                    }
                  }}
                >
                  {extractedDataModal.linked_loan_record_id ? 'Facility Workspace' : 'Commitment Workspace'}
                </Tab>
              )}
              <Tab active={modalTab === 'raw'} onClick={() => setModalTab('raw')}>
                Raw Output
              </Tab>
            </TabContainer>

            {/* Tab Content */}
            {modalTab === 'data' ? (
              (() => {
                const data = extractDraftData ?? extractedDataModal.extracted_data;
                const edit = extractEditMode && extractedDataModal.approval_status === 'PENDING_REVIEW';
                const reDocTypes = ['REAL_ESTATE_ACQUISITION', 'RE_APPRAISAL', 'REAL_ESTATE_LEASE', 'REAL_ESTATE_DISPOSITION'];
                const isReDoc = reDocTypes.includes(extractedDataModal.document_type || '');
                // PE-BUG-1: PE crews emit a flat fund/commitment-shaped JSON;
                // render a PE-aware schema instead of falling through to the
                // loan layout (which shows "Borrower / Loan Identifiers").
                const peDocTypes = ['PE_SUBSCRIPTION_AGREEMENT', 'PE_NAV', 'PE_VALUATION', 'CAPITAL_CALL', 'DISTRIBUTION_NOTICE', 'FUND_PROSPECTUS'];
                const isPeDoc = peDocTypes.includes(extractedDataModal.document_type || '');
                // RE-DEBT: CRE mortgage docs emit a flat property+loan-shaped
                // JSON (`extracted_re_debt_data`). Render a CRE-mortgage
                // schema (Property / Borrower & Sponsor / Loan Economics /
                // Interest Rate / Maturity / Covenants / event deltas)
                // instead of falling through to the bilateral PC layout.
                const reDebtDocTypes = ['RE_LOAN_AGREEMENT', 'RE_LOAN_PAYDOWN', 'RE_LOAN_RATE_RESET', 'RE_LOAN_AMENDMENT', 'RE_LOAN_PAYOFF', 'RE_LOAN_CONSTRUCTION_DRAW'];
                const isReDebtDoc = reDebtDocTypes.includes(extractedDataModal.document_type || '');
                // V342 CLO: render deal/tranche/equity-economics schema instead
                // of the loan-shaped layout (which shows "Borrower / Loan
                // Identifiers" — none of which a CLO offering memo carries).
                const cloDocTypes = ['CLO_OFFERING_MEMO', 'CLO_OFFERING_CIRCULAR', 'CLO_INDENTURE', 'CLO_TRUSTEE_REPORT', 'CLO_MONTHLY_REPORT'];
                const isCloDoc = cloDocTypes.includes(extractedDataModal.document_type || '');
                const abfDocTypes = ['ABF_CREDIT_AGREEMENT', 'ABF_AMENDMENT', 'ABF_DRAWDOWN_NOTICE', 'ABF_REPAYMENT_NOTICE', 'ABF_BORROWING_BASE_CERTIFICATE', 'ABF_SERVICER_REPORT', 'ABF_COMPLIANCE_CERTIFICATE', 'ABF_PAYING_AGENT_REPORT'];
                const isAbfDoc = abfDocTypes.includes(extractedDataModal.document_type || '');
                const docType = extractedDataModal.document_type || '';
                const fmtMoney = (v: any) => (v == null || v === '' ? '-' : '$' + Number(v).toLocaleString());
                const fmtPct = (v: any) => (v == null || v === '' ? '-' : Number(v).toFixed(2) + '%');
                const fmtNum = (v: any) => (v == null || v === '' ? '-' : Number(v).toLocaleString());
                const iv = extractedDataModal.interest_verification;
                const verificationFailed = iv?.status === 'FAIL';
                const fmtVerifyMoney = (v: any) => (v == null || v === '' ? '-' : '$' + Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));
                const loanNoticeDocTypes = ['LOAN_NOTICE', 'PAYDOWN_NOTICE', 'DRAWDOWN_NOTICE', 'LSTA_DRAWDOWN_NOTICE', 'LSTA_PAYDOWN_NOTICE', 'LSTA_RATE_RESET', 'AGENT_BANK_NOTICE'];
                const isLoanNoticeDoc = loanNoticeDocTypes.includes(docType);
                const modalAny = extractedDataModal as any;
                const parseRawJsonPayload = (value: any): any => {
                  if (!value) return null;
                  if (typeof value === 'object') return value;
                  if (typeof value !== 'string') return null;
                  const trimmed = value.trim();
                  const fenced = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
                  const body = fenced ? fenced[1] : trimmed;
                  try {
                    return JSON.parse(body);
                  } catch {
                    return null;
                  }
                };
                const rawPayload =
                  parseRawJsonPayload(modalAny?.result?.result?.raw_output)
                  || parseRawJsonPayload(modalAny?.result?.raw_output)
                  || parseRawJsonPayload(modalAny?.result_json?.result?.raw_output)
                  || parseRawJsonPayload(modalAny?.raw_output);
                const nestedResultLoanData =
                  modalAny?.result?.result?.extracted_loan_data
                  || modalAny?.result?.extracted_loan_data
                  || modalAny?.result_json?.result?.extracted_loan_data
                  || modalAny?.raw_output?.extracted_loan_data
                  || rawPayload?.extracted_loan_data
                  || rawPayload?.result?.extracted_loan_data
                  || rawPayload?.result?.result?.extracted_loan_data;
                const bookingPayload = modalAny?.booking_payload || modalAny?.bookingPayload || {};
                const dataAny = data as any;
                const firstResolved = (...values: any[]) => values.find(value => value !== undefined && value !== null && value !== '');
                const principalDeltaAmount = (() => {
                  const eventType = String(data.notice_event?.event_type || nestedResultLoanData?.notice_event?.event_type || '').toUpperCase();
                  if (!eventType.includes('PAYDOWN')) return undefined;
                  const original = firstResolved(data.loan_terms?.original_principal, nestedResultLoanData?.loan_terms?.original_principal);
                  const current = firstResolved(data.loan_terms?.current_principal, nestedResultLoanData?.loan_terms?.current_principal);
                  const originalNum = Number(original);
                  const currentNum = Number(current);
                  if (!Number.isFinite(originalNum) || !Number.isFinite(currentNum)) return undefined;
                  const delta = Math.abs(originalNum - currentNum);
                  return delta > 0 ? delta : undefined;
                })();
                const noticeEventAmount =
                  firstResolved(
                    data.notice_event?.event_amount,
                    dataAny?.prepayment_details?.total_payment_due,
                    dataAny?.event_amount,
                    dataAny?.amount,
                    dataAny?.paydown_amount,
                    dataAny?.draw_amount,
                    nestedResultLoanData?.notice_event?.event_amount,
                    nestedResultLoanData?.prepayment_details?.total_payment_due,
                    rawPayload?.extracted_loan_data?.notice_event?.event_amount,
                    rawPayload?.extracted_loan_data?.prepayment_details?.total_payment_due,
                    bookingPayload.amount,
                    bookingPayload.facility_amount,
                    bookingPayload.paydown_amount,
                    bookingPayload.event_amount,
                    principalDeltaAmount,
                  );
                const noticeEffectiveDate =
                  data.notice_event?.effective_date ||
                  data.key_dates?.notice_effective_date;
                return (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {verificationFailed && (
                  <div style={{
                    background: 'rgba(220, 38, 38, 0.10)',
                    border: '1px solid rgba(220, 38, 38, 0.45)',
                    borderRadius: '8px',
                    padding: '0.9rem 1rem',
                    marginBottom: '1rem',
                    color: '#fecaca'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <AlertTriangle size={16} style={{ color: '#f87171' }} />
                      <strong style={{ color: '#fca5a5', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Verification Discrepancy — Review Required
                      </strong>
                    </div>
                    <div style={{ fontSize: '0.825rem', lineHeight: 1.5 }}>
                      The parser-extracted interest amount does not reconcile with the
                      deterministic re-computation (principal × all-in × days / day-count-base).
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginTop: '10px', fontSize: '0.8rem' }}>
                      <div>
                        <div style={{ color: '#fca5a5', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.04em' }}>Stated (Notice)</div>
                        <div style={{ fontWeight: 600, color: '#fef2f2' }}>{fmtVerifyMoney(iv?.stated_interest_amount)}</div>
                      </div>
                      <div>
                        <div style={{ color: '#fca5a5', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.04em' }}>Computed</div>
                        <div style={{ fontWeight: 600, color: '#fef2f2' }}>{fmtVerifyMoney(iv?.computed_interest_amount)}</div>
                      </div>
                      <div>
                        <div style={{ color: '#fca5a5', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.04em' }}>Δ (bps)</div>
                        <div style={{ fontWeight: 600, color: '#fef2f2' }}>{iv?.delta_bps != null ? Number(iv.delta_bps).toFixed(1) : '-'}</div>
                      </div>
                      <div>
                        <div style={{ color: '#fca5a5', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.04em' }}>Tolerance</div>
                        <div style={{ fontWeight: 600, color: '#fef2f2' }}>{iv?.tolerance_bps != null ? Number(iv.tolerance_bps).toFixed(1) + ' bps' : '-'}</div>
                      </div>
                    </div>
                    {iv?.reason && (
                      <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#fda4af', fontStyle: 'italic' }}>
                        {iv.reason}
                      </div>
                    )}
                    <div style={{ marginTop: '10px', fontSize: '0.78rem', color: '#fecaca' }}>
                      <strong>Resolve:</strong> Click <em>Edit before approve</em> to correct the
                      parser-extracted numbers, or click <em>Override &amp; Post</em> below to accept the
                      discrepancy with a justification reason (audit-stamped).
                    </div>
                  </div>
                )}
                {extractedDataModal.approval_status === 'PENDING_REVIEW' && String(extractedDataModal.document_type || '').startsWith('ABF_') && (
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '12px' }}>
                    ABF extractions are not edited here. An agreement's terms are confirmed clause by clause in the
                    contract review; a notice or certificate is corrected on the facility, where the change is checked
                    against the agreement and approved by a second person.
                  </div>
                )}
                {extractedDataModal.approval_status === 'PENDING_REVIEW' && !String(extractedDataModal.document_type || '').startsWith('ABF_') && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <ButtonOutline type="button" onClick={() => setExtractEditMode((m) => !m)}>
                      <Edit3 size={14} />
                      {extractEditMode ? 'Done editing' : 'Edit before approve'}
                    </ButtonOutline>
                    {extractEditMode && extractDraftData && JSON.stringify(extractDraftData) !== extractDraftBaselineJson && (
                      <span style={{ fontSize: '0.75rem', color: '#fbbf24' }}>
                        Edits will be saved with approval.
                      </span>
                    )}
                  </div>
                )}
                {data._extraction_note && (
                  <div style={{
                    background: 'rgba(234, 179, 8, 0.15)',
                    border: '1px solid rgba(234, 179, 8, 0.3)',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    marginBottom: '1rem',
                    color: '#eab308',
                    fontSize: '0.875rem'
                  }}>
                    <AlertTriangle size={16} style={{ display: 'inline', marginRight: '8px' }} />
                    {data._extraction_note}
                  </div>
                )}

                {isReDoc && (() => {
                  // RE crews emit a flat property-shaped object; cast to any
                  // so the loan-typed `data` can read RE field names.
                  const re = data as any;
                  return (
                  <>
                    <DataGrid>
                      {/* Property */}
                      <DataSection>
                        <SectionTitle>
                          <Building2 size={16} />
                          Property
                        </SectionTitle>
                        <DataRow><DataLabel>Property Name</DataLabel><DataValue>{re.property_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Address</DataLabel><DataValue>{re.property_address || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Type</DataLabel><DataValue>{re.property_type || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Owner of Record</DataLabel><DataValue>{re.owner_of_record || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Asset Manager</DataLabel><DataValue>{re.asset_manager || '-'}</DataValue></DataRow>
                      </DataSection>

                      {/* Acquisition Economics — hidden for lease docs */}
                      {docType !== 'REAL_ESTATE_LEASE' && (
                        <DataSection>
                          <SectionTitle>
                            <DollarSign size={16} />
                            {docType === 'REAL_ESTATE_DISPOSITION' ? 'Disposition Economics' : 'Acquisition Economics'}
                          </SectionTitle>
                          {docType === 'REAL_ESTATE_DISPOSITION' ? (
                            <>
                              <DataRow><DataLabel>Sale Price</DataLabel><DataValue>{fmtMoney(re.sale_price)}</DataValue></DataRow>
                              <DataRow><DataLabel>Sale Date</DataLabel><DataValue>{re.sale_date || '-'}</DataValue></DataRow>
                              <DataRow><DataLabel>Buyer</DataLabel><DataValue>{re.buyer || '-'}</DataValue></DataRow>
                              <DataRow><DataLabel>Broker</DataLabel><DataValue>{re.broker || '-'}</DataValue></DataRow>
                              <DataRow><DataLabel>Closing Date</DataLabel><DataValue>{re.closing_date || '-'}</DataValue></DataRow>
                            </>
                          ) : (
                            <>
                              <DataRow><DataLabel>Purchase Price</DataLabel><DataValue>{fmtMoney(re.purchase_price)}</DataValue></DataRow>
                              <DataRow><DataLabel>Equity Invested</DataLabel><DataValue>{fmtMoney(re.equity_invested)}</DataValue></DataRow>
                              <DataRow><DataLabel>Total Debt</DataLabel><DataValue>{fmtMoney(re.total_debt)}</DataValue></DataRow>
                              <DataRow><DataLabel>LTV</DataLabel><DataValue>{fmtPct(re.ltv)}</DataValue></DataRow>
                              <DataRow><DataLabel>Appraised Value</DataLabel><DataValue>{fmtMoney(re.appraised_value)}</DataValue></DataRow>
                              <DataRow><DataLabel>Acquisition Date</DataLabel><DataValue>{re.acquisition_date || '-'}</DataValue></DataRow>
                              <DataRow><DataLabel>Ownership %</DataLabel><DataValue>{fmtPct(re.ownership_pct)}</DataValue></DataRow>
                            </>
                          )}
                        </DataSection>
                      )}

                      {/* Income & Returns — relevant to acquisition + appraisal */}
                      {(docType === 'REAL_ESTATE_ACQUISITION' || docType === 'RE_APPRAISAL') && (
                        <DataSection>
                          <SectionTitle>
                            <TrendingUp size={16} />
                            Income & Returns
                          </SectionTitle>
                          <DataRow><DataLabel>Net Operating Income</DataLabel><DataValue>{fmtMoney(re.net_operating_income)}</DataValue></DataRow>
                          <DataRow><DataLabel>Cap Rate</DataLabel><DataValue>{fmtPct(re.cap_rate)}</DataValue></DataRow>
                          <DataRow><DataLabel>DSCR</DataLabel><DataValue>{re.dscr != null ? Number(re.dscr).toFixed(2) + 'x' : '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Occupancy</DataLabel><DataValue>{fmtPct(re.occupancy_rate)}</DataValue></DataRow>
                          <DataRow><DataLabel>Expected Hold (yrs)</DataLabel><DataValue>{re.expected_hold_years ?? '-'}</DataValue></DataRow>
                        </DataSection>
                      )}

                      {/* Lease Terms — lease docs only */}
                      {docType === 'REAL_ESTATE_LEASE' && (
                        <DataSection>
                          <SectionTitle>
                            <FileText size={16} />
                            Lease Terms
                          </SectionTitle>
                          <DataRow><DataLabel>Tenant</DataLabel><DataValue>{re.tenant_name || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Lease Type</DataLabel><DataValue>{re.lease_type || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Annual Base Rent</DataLabel><DataValue>{fmtMoney(re.annual_base_rent)}</DataValue></DataRow>
                          <DataRow><DataLabel>Rentable SqFt</DataLabel><DataValue>{fmtNum(re.rentable_sqft)}</DataValue></DataRow>
                          <DataRow><DataLabel>Start Date</DataLabel><DataValue>{re.lease_start_date || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>End Date</DataLabel><DataValue>{re.lease_end_date || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Annual Escalation</DataLabel><DataValue>{fmtPct(re.escalation_pct)}</DataValue></DataRow>
                        </DataSection>
                      )}

                      {/* Appraisal — appraisal docs only */}
                      {docType === 'RE_APPRAISAL' && (
                        <DataSection>
                          <SectionTitle>
                            <FileCheck size={16} />
                            Appraisal
                          </SectionTitle>
                          <DataRow><DataLabel>Appraised Value</DataLabel><DataValue>{fmtMoney(re.appraised_value)}</DataValue></DataRow>
                          <DataRow><DataLabel>Appraisal Date</DataLabel><DataValue>{re.appraisal_date || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Appraisal Firm</DataLabel><DataValue>{re.appraisal_firm || '-'}</DataValue></DataRow>
                        </DataSection>
                      )}
                    </DataGrid>
                  </>
                  );
                })()}

                {isPeDoc && (() => {
                  // PE crews emit a flat fund/commitment-shaped object.
                  const pe = data as any;
                  return (
                  <>
                    <DataGrid>
                      <DataSection>
                        <SectionTitle>
                          <Building2 size={16} />
                          Fund
                        </SectionTitle>
                        <DataRow><DataLabel>Fund Name</DataLabel><DataValue>{pe.fund_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>General Partner</DataLabel><DataValue>{pe.general_partner || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Vintage Year</DataLabel><DataValue>{pe.vintage_year ?? '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Strategy</DataLabel><DataValue>{pe.strategy || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Fund Structure</DataLabel><DataValue>{pe.fund_structure || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Fund ID</DataLabel><DataValue>{pe.fund_id || '-'}</DataValue></DataRow>
                      </DataSection>

                      {(docType === 'PE_SUBSCRIPTION_AGREEMENT' || docType === 'FUND_PROSPECTUS') && (
                        <DataSection>
                          <SectionTitle>
                            <DollarSign size={16} />
                            Commitment Economics
                          </SectionTitle>
                          <DataRow><DataLabel>Commitment Amount</DataLabel><DataValue>{fmtMoney(pe.commitment_amount)}</DataValue></DataRow>
                          <DataRow><DataLabel>Called At Acquisition</DataLabel><DataValue>{fmtMoney(pe.called_at_acquisition)}</DataValue></DataRow>
                          <DataRow><DataLabel>Mgmt Fee Rate</DataLabel><DataValue>{fmtPct(pe.management_fee_rate)}</DataValue></DataRow>
                          <DataRow><DataLabel>Carried Interest</DataLabel><DataValue>{fmtPct(pe.carried_interest_rate)}</DataValue></DataRow>
                          <DataRow><DataLabel>Preferred Return</DataLabel><DataValue>{fmtPct(pe.preferred_return_rate)}</DataValue></DataRow>
                          <DataRow><DataLabel>Subscription Date</DataLabel><DataValue>{pe.subscription_date || '-'}</DataValue></DataRow>
                        </DataSection>
                      )}

                      {docType === 'CAPITAL_CALL' && (
                        <DataSection>
                          <SectionTitle>
                            <Zap size={16} />
                            Capital Call
                          </SectionTitle>
                          <DataRow><DataLabel>Investor Amount</DataLabel><DataValue>{fmtMoney(pe.investor_amount)}</DataValue></DataRow>
                          <DataRow><DataLabel>Total Amount</DataLabel><DataValue>{fmtMoney(pe.total_amount)}</DataValue></DataRow>
                          <DataRow><DataLabel>Notice Date</DataLabel><DataValue>{pe.notice_date || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Due Date</DataLabel><DataValue>{pe.due_date || pe.payment_date || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Mgmt Fee</DataLabel><DataValue>{fmtMoney(pe.management_fee)}</DataValue></DataRow>
                          <DataRow><DataLabel>Fund Expenses</DataLabel><DataValue>{fmtMoney(pe.expenses)}</DataValue></DataRow>
                          <DataRow><DataLabel>Wire Bank</DataLabel><DataValue>{pe.wire_bank_name || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Wire ABA</DataLabel><DataValue>{pe.wire_aba_routing || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Wire Account</DataLabel><DataValue>{pe.wire_account_number || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Wire Reference</DataLabel><DataValue>{pe.wire_reference || '-'}</DataValue></DataRow>
                        </DataSection>
                      )}

                      {docType === 'DISTRIBUTION_NOTICE' && (
                        <DataSection>
                          <SectionTitle>
                            <TrendingUp size={16} />
                            Distribution
                          </SectionTitle>
                          <DataRow><DataLabel>Investor Amount</DataLabel><DataValue>{fmtMoney(pe.investor_amount)}</DataValue></DataRow>
                          <DataRow><DataLabel>Total Amount</DataLabel><DataValue>{fmtMoney(pe.total_amount)}</DataValue></DataRow>
                          <DataRow><DataLabel>Return of Capital</DataLabel><DataValue>{fmtMoney(pe.return_of_capital)}</DataValue></DataRow>
                          <DataRow><DataLabel>Capital Gains</DataLabel><DataValue>{fmtMoney(pe.capital_gains)}</DataValue></DataRow>
                          <DataRow><DataLabel>Income</DataLabel><DataValue>{fmtMoney(pe.income)}</DataValue></DataRow>
                          <DataRow><DataLabel>Notice Date</DataLabel><DataValue>{pe.notice_date || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Payment Date</DataLabel><DataValue>{pe.payment_date || '-'}</DataValue></DataRow>
                        </DataSection>
                      )}

                      {(docType === 'PE_NAV' || docType === 'PE_VALUATION') && (
                        <DataSection>
                          <SectionTitle>
                            <FileCheck size={16} />
                            NAV / Valuation
                          </SectionTitle>
                          <DataRow><DataLabel>Reported NAV</DataLabel><DataValue>{fmtMoney(pe.reported_nav)}</DataValue></DataRow>
                          <DataRow><DataLabel>NAV As Of</DataLabel><DataValue>{pe.nav_as_of_date || pe.valuation_date || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>NAV Per Unit</DataLabel><DataValue>{fmtMoney(pe.nav_per_unit)}</DataValue></DataRow>
                          <DataRow><DataLabel>Called To Date</DataLabel><DataValue>{fmtMoney(pe.called_to_date)}</DataValue></DataRow>
                          <DataRow><DataLabel>Distributed To Date</DataLabel><DataValue>{fmtMoney(pe.distributed_to_date)}</DataValue></DataRow>
                        </DataSection>
                      )}
                    </DataGrid>
                  </>
                  );
                })()}

                {isReDebtDoc && (() => {
                  // CRE mortgage crews emit a flat object with both property
                  // and loan fields under `extracted_re_debt_data`.
                  const re = data as any;
                  const isAgreement = docType === 'RE_LOAN_AGREEMENT';
                  const isPaydown = docType === 'RE_LOAN_PAYDOWN';
                  const isRateReset = docType === 'RE_LOAN_RATE_RESET';
                  const isAmendment = docType === 'RE_LOAN_AMENDMENT';
                  const isPayoff = docType === 'RE_LOAN_PAYOFF';
                  const isConstructionDraw = docType === 'RE_LOAN_CONSTRUCTION_DRAW';
                  return (
                  <>
                    <DataGrid>
                      {/* Property */}
                      <DataSection>
                        <SectionTitle>
                          <Building2 size={16} />
                          Property
                        </SectionTitle>
                        <DataRow><DataLabel>Property Name</DataLabel><DataValue>{re.property_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Address</DataLabel><DataValue>{re.property_address || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Type</DataLabel><DataValue>{re.property_type || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Square Footage</DataLabel><DataValue>{fmtNum(re.square_footage)}</DataValue></DataRow>
                        <DataRow><DataLabel>Year Built</DataLabel><DataValue>{re.year_built ?? '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Appraised Value</DataLabel><DataValue>{fmtMoney(re.appraised_value)}</DataValue></DataRow>
                      </DataSection>

                      {/* Borrower & Sponsor */}
                      <DataSection>
                        <SectionTitle>
                          <FileText size={16} />
                          Borrower &amp; Sponsor
                        </SectionTitle>
                        <DataRow><DataLabel>Borrower</DataLabel><DataValue>{re.borrower_legal_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Sponsor</DataLabel><DataValue>{re.sponsor || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Guarantor</DataLabel><DataValue>{re.guarantor || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Servicer</DataLabel><DataValue>{re.servicer || '-'}</DataValue></DataRow>
                      </DataSection>

                      {/* Loan Identifiers */}
                      <DataSection>
                        <SectionTitle>
                          <FileCheck size={16} />
                          Loan Identifiers
                        </SectionTitle>
                        <DataRow><DataLabel>Loan Name</DataLabel><DataValue>{re.loan_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Loan Number</DataLabel><DataValue>{re.loan_number || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Loan Type</DataLabel><DataValue>{re.loan_type || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>CUSIP</DataLabel><DataValue>{re.cusip || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Currency</DataLabel><DataValue>{re.currency || '-'}</DataValue></DataRow>
                      </DataSection>

                      {/* Loan Economics */}
                      <DataSection>
                        <SectionTitle>
                          <DollarSign size={16} />
                          Loan Economics
                        </SectionTitle>
                        <DataRow><DataLabel>Principal Amount</DataLabel><DataValue>{fmtMoney(re.principal_amount)}</DataValue></DataRow>
                        <DataRow><DataLabel>Total Commitment</DataLabel><DataValue>{fmtMoney(re.total_commitment)}</DataValue></DataRow>
                        <DataRow><DataLabel>Funded Amount</DataLabel><DataValue>{fmtMoney(re.funded_amount)}</DataValue></DataRow>
                        <DataRow><DataLabel>Unfunded Commitment</DataLabel><DataValue>{fmtMoney(re.unfunded_commitment)}</DataValue></DataRow>
                      </DataSection>

                      {/* Interest Rate */}
                      <DataSection>
                        <SectionTitle>
                          <TrendingUp size={16} />
                          Interest Rate
                        </SectionTitle>
                        <DataRow><DataLabel>Rate Type</DataLabel><DataValue>{re.rate_type || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Index</DataLabel><DataValue>{re.index_rate || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Spread (bps)</DataLabel><DataValue>{re.spread_bps != null ? Number(re.spread_bps).toFixed(0) : '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Floor</DataLabel><DataValue>{fmtPct(re.floor_pct)}</DataValue></DataRow>
                        <DataRow><DataLabel>All-In Rate</DataLabel><DataValue>{fmtPct(re.all_in_rate)}</DataValue></DataRow>
                      </DataSection>

                      {/* Maturity & Term */}
                      <DataSection>
                        <SectionTitle>
                          <Calendar size={16} />
                          Maturity &amp; Term
                        </SectionTitle>
                        <DataRow><DataLabel>Origination Date</DataLabel><DataValue>{re.origination_date || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>First Payment Date</DataLabel><DataValue>{re.first_payment_date || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Maturity Date</DataLabel><DataValue>{re.maturity_date || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Extension Options</DataLabel><DataValue>{re.extension_options || '-'}</DataValue></DataRow>
                      </DataSection>

                      {/* Covenants */}
                      <DataSection>
                        <SectionTitle>
                          <Shield size={16} />
                          Covenants
                        </SectionTitle>
                        <DataRow><DataLabel>Max LTV</DataLabel><DataValue>{re.max_ltv_covenant != null ? Number(re.max_ltv_covenant).toFixed(2) + '%' : '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Min DSCR</DataLabel><DataValue>{re.min_dscr_covenant != null ? Number(re.min_dscr_covenant).toFixed(2) + 'x' : '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Min Debt Yield</DataLabel><DataValue>{re.debt_yield_covenant != null ? Number(re.debt_yield_covenant).toFixed(2) + '%' : '-'}</DataValue></DataRow>
                      </DataSection>

                      {/* V299 — typed classification discriminators (origination only) */}
                      {isAgreement && (
                        <DataSection>
                          <SectionTitle>
                            <Shield size={16} />
                            Classification
                          </SectionTitle>
                          <DataRow><DataLabel>Instrument Subtype</DataLabel><DataValue>{re.instrument_subtype ? String(re.instrument_subtype).replace(/_/g, ' ') : '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Collateral Type</DataLabel><DataValue>{re.collateral_type ? String(re.collateral_type).replace(/_/g, ' ') : '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>GAAP Classification</DataLabel><DataValue>{re.gaap_classification ? String(re.gaap_classification).replace(/_/g, ' ') : '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Pay Mechanic</DataLabel><DataValue>{re.pay_mechanic ? String(re.pay_mechanic).replace(/_/g, ' ') : '-'}</DataValue></DataRow>
                        </DataSection>
                      )}

                      {/* Event-specific deltas */}
                      {!isAgreement && (
                        <DataSection>
                          <SectionTitle>
                            <Zap size={16} />
                            Event Details
                          </SectionTitle>
                          <DataRow><DataLabel>Event Type</DataLabel><DataValue>{re.event_type || (isPaydown ? 'PAYDOWN' : isRateReset ? 'RATE_RESET' : isAmendment ? 'AMENDMENT' : isPayoff ? 'PAYOFF' : isConstructionDraw ? 'CONSTRUCTION_DRAW' : '-')}</DataValue></DataRow>
                          <DataRow><DataLabel>Effective Date</DataLabel><DataValue>{re.effective_date || '-'}</DataValue></DataRow>
                          {isPaydown && (
                            <DataRow><DataLabel>Paydown Amount</DataLabel><DataValue>{fmtMoney(re.paydown_amount)}</DataValue></DataRow>
                          )}
                          {isRateReset && (
                            <>
                              <DataRow><DataLabel>New Index Rate</DataLabel><DataValue>{re.new_index_rate || '-'}</DataValue></DataRow>
                              <DataRow><DataLabel>New Spread (bps)</DataLabel><DataValue>{re.new_spread_bps != null ? Number(re.new_spread_bps).toFixed(0) : '-'}</DataValue></DataRow>
                              <DataRow><DataLabel>New All-In Rate</DataLabel><DataValue>{fmtPct(re.new_all_in_rate)}</DataValue></DataRow>
                            </>
                          )}
                          {isAmendment && (
                            <>
                              <DataRow><DataLabel>New Maturity Date</DataLabel><DataValue>{re.new_maturity_date || '-'}</DataValue></DataRow>
                              <DataRow><DataLabel>New Max LTV</DataLabel><DataValue>{re.new_max_ltv_covenant != null ? Number(re.new_max_ltv_covenant).toFixed(2) + '%' : '-'}</DataValue></DataRow>
                              <DataRow><DataLabel>New Min DSCR</DataLabel><DataValue>{re.new_min_dscr_covenant != null ? Number(re.new_min_dscr_covenant).toFixed(2) + 'x' : '-'}</DataValue></DataRow>
                              <DataRow><DataLabel>New Extension Options</DataLabel><DataValue>{re.new_extension_options || '-'}</DataValue></DataRow>
                            </>
                          )}
                          {isPayoff && (
                            <DataRow><DataLabel>Payoff Amount</DataLabel><DataValue>{fmtMoney(re.payoff_amount)}</DataValue></DataRow>
                          )}
                          {isConstructionDraw && (
                            <>
                              <DataRow><DataLabel>Draw Amount</DataLabel><DataValue>{fmtMoney(re.draw_amount)}</DataValue></DataRow>
                              <DataRow><DataLabel>Draw Number</DataLabel><DataValue>{re.draw_number ?? '-'}</DataValue></DataRow>
                              <DataRow><DataLabel>Budget Category</DataLabel><DataValue>{re.budget_category || '-'}</DataValue></DataRow>
                              <DataRow><DataLabel>Completion %</DataLabel><DataValue>{re.completion_pct != null ? Number(re.completion_pct).toFixed(1) + '%' : '-'}</DataValue></DataRow>
                              <DataRow><DataLabel>Inspector</DataLabel><DataValue>{re.inspector || '-'}</DataValue></DataRow>
                            </>
                          )}
                        </DataSection>
                      )}
                    </DataGrid>
                  </>
                  );
                })()}

                {isCloDoc && (() => {
                  // V342 CLO: crew emits a flat deal+tranche+economics object
                  // under `extracted_clo_data`. Render deal identity, sizes &
                  // dates, collateral metrics, equity tranche, equity
                  // economics, and the investor's subscription in sections
                  // sized for the institutional review modal.
                  const clo = data as any;
                  const isOfferingMemo = docType === 'CLO_OFFERING_MEMO' || docType === 'CLO_OFFERING_CIRCULAR';
                  return (
                  <>
                    <DataGrid>
                      <DataSection>
                        <SectionTitle>
                          <Building2 size={16} />
                          Deal Identity
                        </SectionTitle>
                        <DataRow><DataLabel>Deal Name</DataLabel><DataValue>{clo.deal_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Deal Ticker</DataLabel><DataValue>{clo.deal_ticker || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Deal Type</DataLabel><DataValue>{clo.deal_type || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Collateral Manager</DataLabel><DataValue>{clo.manager_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Trustee</DataLabel><DataValue>{clo.trustee_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>CUSIP Base</DataLabel><DataValue>{clo.cusip_base || '-'}</DataValue></DataRow>
                      </DataSection>

                      <DataSection>
                        <SectionTitle>
                          <FileCheck size={16} />
                          Sizes &amp; Key Dates
                        </SectionTitle>
                        <DataRow><DataLabel>Target Par</DataLabel><DataValue>{fmtMoney(clo.target_par)}</DataValue></DataRow>
                        <DataRow><DataLabel>Pricing Date</DataLabel><DataValue>{clo.pricing_date || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Closing Date</DataLabel><DataValue>{clo.closing_date || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Effective Date</DataLabel><DataValue>{clo.effective_date || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Reinvestment End</DataLabel><DataValue>{clo.reinvestment_end_date || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Stated Maturity</DataLabel><DataValue>{clo.stated_maturity_date || '-'}</DataValue></DataRow>
                      </DataSection>

                      <DataSection>
                        <SectionTitle>
                          <TrendingUp size={16} />
                          Collateral Metrics
                        </SectionTitle>
                        <DataRow><DataLabel>WARF</DataLabel><DataValue>{fmtNum(clo.warf)}</DataValue></DataRow>
                        <DataRow><DataLabel>WAS (bps)</DataLabel><DataValue>{fmtNum(clo.was_bps)}</DataValue></DataRow>
                        <DataRow><DataLabel>WAL (yrs)</DataLabel><DataValue>{clo.wal_years != null ? Number(clo.wal_years).toFixed(1) : '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Diversity Score</DataLabel><DataValue>{fmtNum(clo.diversity_score)}</DataValue></DataRow>
                        <DataRow><DataLabel>CCC Concentration</DataLabel><DataValue>{fmtPct(clo.ccc_pct)}</DataValue></DataRow>
                        <DataRow><DataLabel>Defaulted</DataLabel><DataValue>{fmtPct(clo.defaulted_pct)}</DataValue></DataRow>
                      </DataSection>

                      <DataSection>
                        <SectionTitle>
                          <DollarSign size={16} />
                          Equity Tranche
                        </SectionTitle>
                        <DataRow><DataLabel>Tranche Name</DataLabel><DataValue>{clo.tranche_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>CUSIP</DataLabel><DataValue>{clo.tranche_cusip || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>ISIN</DataLabel><DataValue>{clo.tranche_isin || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Payment Priority</DataLabel><DataValue>{fmtNum(clo.payment_priority)}</DataValue></DataRow>
                        <DataRow><DataLabel>Coupon Type</DataLabel><DataValue>{clo.coupon_type || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Original Balance</DataLabel><DataValue>{fmtMoney(clo.original_balance)}</DataValue></DataRow>
                        <DataRow><DataLabel>Attach / Detach</DataLabel><DataValue>{(clo.attachment_point_pct != null || clo.detachment_point_pct != null) ? `${fmtPct(clo.attachment_point_pct)} / ${fmtPct(clo.detachment_point_pct)}` : '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Ratings (Mdy/S&amp;P/Fitch)</DataLabel><DataValue>{[clo.moodys_rating, clo.sp_rating, clo.fitch_rating].filter(Boolean).join(' / ') || '-'}</DataValue></DataRow>
                      </DataSection>

                      <DataSection>
                        <SectionTitle>
                          <Zap size={16} />
                          Equity Economics
                        </SectionTitle>
                        <DataRow><DataLabel>Mgmt Fee (bps)</DataLabel><DataValue>{fmtNum(clo.management_fee_bps)}</DataValue></DataRow>
                        <DataRow><DataLabel>Hurdle</DataLabel><DataValue>{fmtPct(clo.incentive_hurdle_pct)}</DataValue></DataRow>
                        <DataRow><DataLabel>GP Split</DataLabel><DataValue>{fmtPct(clo.gp_split_pct)}</DataValue></DataRow>
                        <DataRow><DataLabel>Target Equity IRR</DataLabel><DataValue>{fmtPct(clo.target_equity_irr)}</DataValue></DataRow>
                      </DataSection>

                      {isOfferingMemo && (
                        <DataSection>
                          <SectionTitle>
                            <CheckCircle size={16} />
                            Investment
                          </SectionTitle>
                          <DataRow><DataLabel>Investment Amount</DataLabel><DataValue>{fmtMoney(clo.investment_amount)}</DataValue></DataRow>
                          <DataRow><DataLabel>Price (% of Par)</DataLabel><DataValue>{clo.price_pct_of_par != null ? Number(clo.price_pct_of_par).toFixed(2) + '%' : '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Fund Identifier</DataLabel><DataValue>{clo.fund_identifier || '-'}</DataValue></DataRow>
                        </DataSection>
                      )}
                    </DataGrid>
                  </>
                  );
                })()}

                {isAbfDoc && (() => {
                  const abf = data as any;
                  const isAmendmentDoc = docType === 'ABF_AMENDMENT';
                  // N-S6-010: extractor placeholders are not values. On an amendment an unstated term inherits.
                  const PLACEHOLDERS = ['NOT EXTRACTED', 'NOT STATED IN AGREEMENT', 'NOT STATED', 'N/A'];
                  const stated = (value: unknown) => value != null && value !== '' && !PLACEHOLDERS.includes(String(value).trim().toUpperCase());
                  const shown = (value: unknown) => stated(value) ? String(value)
                    : isAmendmentDoc ? <span style={{ color: '#94a3b8', fontWeight: 400 }}>— not stated (inherits)</span> : '-';
                  const loc = (row: any) => [row?.source_page ? `p. ${row.source_page}` : null, row?.source_clause || row?.source_section || null].filter(Boolean).join(' · ') || '-';
                  const words = (value: unknown) => value == null || value === '' ? '-' : String(value).replace(/_/g, ' ').toLowerCase();
                  const nodes: any[] = Array.isArray(abf.waterfall_nodes) ? [...abf.waterfall_nodes].sort((a, b) => String(a.mode).localeCompare(String(b.mode)) || Number(a.priority_order) - Number(b.priority_order)) : [];
                  const classes: any[] = Array.isArray(abf.lender_classes) ? abf.lender_classes : [];
                  const consequences: any[] = Array.isArray(abf.consequences) ? abf.consequences : [];
                  const calendar: any = abf.payment_calendar && typeof abf.payment_calendar === 'object' && Object.keys(abf.payment_calendar).length ? abf.payment_calendar : null;
                  const accounts: any[] = Array.isArray(abf.accounts) ? abf.accounts : [];
                  const cell: React.CSSProperties = { padding: '0.35rem 0.5rem', borderBottom: '1px solid rgba(148,163,184,0.12)', verticalAlign: 'top', fontSize: '0.78rem', color: '#e2e8f0' };
                  const head: React.CSSProperties = { ...cell, color: '#94a3b8', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left' };
                  const src: React.CSSProperties = { ...cell, color: '#94a3b8', fontSize: '0.72rem' };
                  const recordNote = (
                    <div style={{ margin: '0 0 0.75rem', color: '#94a3b8', fontSize: '0.8rem' }}>
                      {extractedDataModal.abf_record_source === 'APPROVED'
                        ? 'Showing the approved record — what Import to OMS books.'
                        : 'Showing what Import to OMS will book from this document. Approve it, then import.'}
                    </div>
                  );
                  const page = (key: string) => {
                    const l = abf.source_lineage?.[key];
                    return l?.page ? <span style={{ color: '#64748b', fontSize: '0.7rem', marginLeft: 6 }}>p. {l.page}</span> : null;
                  };
                  const facilitySection = (
                    <DataSection>
                      <SectionTitle><Building2 size={16} />Facility</SectionTitle>
                      <DataRow><DataLabel>Facility Code</DataLabel><DataValue>{abf.facility_code || abf.facility_id || '-'}</DataValue></DataRow>
                      <DataRow><DataLabel>Facility Name</DataLabel><DataValue>{abf.facility_name || '-'}</DataValue></DataRow>
                      <DataRow><DataLabel>Borrower</DataLabel><DataValue>{shown(abf.borrower_name || abf.obligor_name)}</DataValue></DataRow>
                      <DataRow><DataLabel>Currency</DataLabel><DataValue>{abf.currency || 'USD'}</DataValue></DataRow>
                    </DataSection>
                  );
                  const fmtCents = (v: any) => (v == null || v === '' ? '-' : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

                  if (docType === 'ABF_DRAWDOWN_NOTICE' || docType === 'ABF_REPAYMENT_NOTICE') {
                    const isDraw = docType === 'ABF_DRAWDOWN_NOTICE';
                    const amount = isDraw
                      ? (abf.requested_draw_amount ?? abf.draw_amount ?? abf.amount)
                      : (abf.repayment_amount ?? abf.prepayment_amount ?? abf.payoff_amount ?? abf.amount);
                    const valueDate = isDraw
                      ? (abf.funding_date || abf.draw_date || abf.effective_date)
                      : (abf.value_date || abf.payment_date || abf.effective_date);
                    return (
                      <>
                      {recordNote}
                      <DataGrid>
                        {facilitySection}
                        <DataSection>
                          <SectionTitle><DollarSign size={16} />{isDraw ? 'Draw request' : 'Repayment'}</SectionTitle>
                          <DataRow><DataLabel>{isDraw ? 'Amount requested' : 'Amount'}</DataLabel><DataValue>{fmtCents(amount)}</DataValue></DataRow>
                          {!isDraw && <DataRow><DataLabel>Kind</DataLabel><DataValue>{words(abf.repayment_kind)}</DataValue></DataRow>}
                          <DataRow><DataLabel>{isDraw ? 'Funding date' : 'Value date'}</DataLabel><DataValue>{valueDate || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Notice date</DataLabel><DataValue>{abf.notice_date || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Drawn before</DataLabel><DataValue>{fmtCents(abf.prior_drawn_amount)}</DataValue></DataRow>
                          <DataRow><DataLabel>Drawn after</DataLabel><DataValue>{fmtCents(abf.pro_forma_drawn_amount)}</DataValue></DataRow>
                          <DataRow><DataLabel>Commitment</DataLabel><DataValue>{fmtMoney(abf.total_commitment)}</DataValue></DataRow>
                        </DataSection>
                        {isDraw && (
                          <DataSection>
                            <SectionTitle><FileCheck size={16} />Wire instructions (as stated)</SectionTitle>
                            <DataRow><DataLabel>Bank</DataLabel><DataValue>{abf.wire_bank_name || '-'}</DataValue></DataRow>
                            <DataRow><DataLabel>Account</DataLabel><DataValue>{abf.wire_account_number || '-'}</DataValue></DataRow>
                            <DataRow><DataLabel>Routing</DataLabel><DataValue>{abf.wire_aba_routing || '-'}</DataValue></DataRow>
                            <DataRow><DataLabel>Reference</DataLabel><DataValue>{abf.wire_reference || '-'}</DataValue></DataRow>
                            <DataRow><DataLabel>Signed by</DataLabel><DataValue>{abf.borrower_officer || '-'}</DataValue></DataRow>
                          </DataSection>
                        )}
                      </DataGrid>
                      <div style={{ marginTop: '0.75rem', color: '#94a3b8', fontSize: '0.78rem' }}>
                        Import stages a funding request. Nothing moves until a checker approves it against the approved borrowing base and operations records the wire.
                      </div>
                      </>
                    );
                  }

                  if (docType === 'ABF_COMPLIANCE_CERTIFICATE') {
                    const covenants: any[] = Array.isArray(abf.covenants) ? abf.covenants : [];
                    return (
                      <>
                      {recordNote}
                      <DataGrid>
                        {facilitySection}
                        <DataSection>
                          <SectionTitle><FileCheck size={16} />Certificate</SectionTitle>
                          <DataRow><DataLabel>As of</DataLabel><DataValue>{abf.as_of_date || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Certified by</DataLabel><DataValue>{abf.certified_by || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Reference</DataLabel><DataValue>{abf.certificate_reference || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Borrower's note</DataLabel><DataValue>{abf.notes || '-'}</DataValue></DataRow>
                        </DataSection>
                      </DataGrid>
                      <div style={{ marginTop: '1rem' }}>
                        <SectionTitle><FileCheck size={16} />Covenant figures reported by the borrower</SectionTitle>
                        {covenants.length === 0
                          ? <div style={{ color: '#fca5a5', fontSize: '0.8rem' }}>No covenant lines were read from this certificate. Import would be refused.</div>
                          : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead><tr><th style={head}>Covenant</th><th style={{ ...head, textAlign: 'right' }}>Reported</th><th style={head}>Where</th></tr></thead>
                              <tbody>
                                {covenants.map((c, i) => (
                                  <tr key={i}>
                                    <td style={cell}>{words(c.covenant_key || c.covenant || c.name)}</td>
                                    <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.reported_value == null ? '-' : Number(c.reported_value).toLocaleString()}</td>
                                    <td style={src}>{loc(c)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        <div style={{ marginTop: '0.5rem', color: '#94a3b8', fontSize: '0.78rem' }}>
                          These are the borrower's own figures. On import each is compared with the covenant AxiomFlow computed from the released tape; the computed value governs.
                        </div>
                      </div>
                      </>
                    );
                  }

                  if (docType === 'ABF_PAYING_AGENT_REPORT') {
                    const lines: any[] = Array.isArray(abf.lines) ? [...abf.lines].sort((a, b) => Number(a.priority) - Number(b.priority)) : [];
                    const lineTotal = lines.reduce((sum, l) => sum + Number(l.amount || 0), 0);
                    const available = abf.available_collections == null ? null : Number(abf.available_collections);
                    const foots = available != null && Math.abs(lineTotal - available) < 0.005;
                    const conflicts: any[] = Array.isArray(abf.statement_conflicts) ? abf.statement_conflicts : [];
                    return (
                      <>
                      {recordNote}
                      <DataGrid>
                        {facilitySection}
                        <DataSection>
                          <SectionTitle><DollarSign size={16} />Distribution statement</SectionTitle>
                          <DataRow><DataLabel>Paying agent</DataLabel><DataValue>{abf.paying_agent || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Collection period</DataLabel><DataValue>{abf.collection_period || '-'}{page('collection_period')}</DataValue></DataRow>
                          <DataRow><DataLabel>Payment date</DataLabel><DataValue>{abf.payment_date || '-'}{page('payment_date')}</DataValue></DataRow>
                          <DataRow><DataLabel>Available collections</DataLabel><DataValue>{fmtCents(abf.available_collections)}{page('available_collections')}</DataValue></DataRow>
                          <DataRow><DataLabel>Distributed to the lender</DataLabel><DataValue>{fmtCents(abf.distribution_total)}{page('distribution_total')}</DataValue></DataRow>
                          <DataRow><DataLabel>of which principal / interest</DataLabel><DataValue>{fmtCents(abf.lender_principal)} / {fmtCents(abf.lender_interest)}</DataValue></DataRow>
                          <DataRow><DataLabel>Read from</DataLabel><DataValue>{abf.statement_source === 'PRINTED_STATEMENT' ? 'The printed statement' : 'Extraction (no printed table found)'}</DataValue></DataRow>
                        </DataSection>
                      </DataGrid>
                      <div style={{ marginTop: '1rem' }}>
                        <SectionTitle><FileCheck size={16} />Priority of payments as the agent applied it</SectionTitle>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead><tr><th style={head}>Priority</th><th style={head}>Line</th><th style={{ ...head, textAlign: 'right' }}>Amount</th><th style={head}>Where</th></tr></thead>
                          <tbody>
                            {lines.map((l, i) => (
                              <tr key={i}>
                                <td style={cell}>{l.priority ?? '-'}</td>
                                <td style={cell}>{l.label || '-'}</td>
                                <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtCents(l.amount)}</td>
                                <td style={src}>{l.source_page ? `p. ${l.source_page}` : '-'}</td>
                              </tr>
                            ))}
                            <tr>
                              <td style={cell} />
                              <td style={{ ...cell, fontWeight: 700 }}>Total</td>
                              <td style={{ ...cell, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtCents(lineTotal)}</td>
                              <td style={{ ...src, color: foots ? '#4ade80' : '#fbbf24' }}>
                                {available == null ? '' : foots ? 'foots to available collections' : `differs from available collections by ${fmtCents(lineTotal - available)}`}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        {conflicts.length > 0 && (
                          <div style={{ marginTop: '0.5rem', color: '#fbbf24', fontSize: '0.78rem' }}>
                            The printed statement and its appendix disagree on {conflicts.map(c => words(c.field)).join(', ')}. The printed figures are used.
                          </div>
                        )}
                        {(payingAgentStatementChecks(abf) || []).length > 0 && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.78rem' }}>
                            {(payingAgentStatementChecks(abf) || []).map(c => (
                              <div key={c.label} style={{ color: c.passed ? '#4ade80' : '#fbbf24' }}>{c.passed ? '✓' : '✗'} {c.label}</div>
                            ))}
                          </div>
                        )}
                        <div style={{ marginTop: '0.5rem', color: '#94a3b8', fontSize: '0.78rem' }}>
                          Import records the agent's statement on the collection period. Collections → Close out then compares it with the agreement's own waterfall, node by node.
                        </div>
                      </div>
                      </>
                    );
                  }

                  return (
                    <>
                    <div style={{ margin: '0 0 0.75rem', color: '#94a3b8', fontSize: '0.8rem' }}>
                      {extractedDataModal.abf_record_source === 'APPROVED'
                        ? 'Showing the approved record — what Import to OMS books.'
                        : 'Showing the extraction — approve it to fix the record Import to OMS books.'}
                    </div>
                    {abfReviewVersionId && (
                      <ContractReviewHandoff>
                        <div>
                          <strong>This agreement has a contract version to review</strong>
                          <span>
                            Terms, rules and covenants are confirmed against their clauses on the
                            facility, where a second person activates them. This view is the
                            extraction record.
                          </span>
                        </div>
                        <button type="button" onClick={() => {
                          setExtractedDataModal(null);
                          navigate(`/portfolio-management/credit/abf/onboarding/versions/${abfReviewVersionId}`);
                        }}>Open contract review</button>
                      </ContractReviewHandoff>
                    )}
                    <DataGrid>
                      <DataSection>
                        <SectionTitle>
                          <Building2 size={16} />
                          Facility
                        </SectionTitle>
                        <DataRow><DataLabel>Facility Code</DataLabel><DataValue>{abf.facility_code || abf.facility_id || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Facility Name</DataLabel><DataValue>{abf.facility_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Structure</DataLabel><DataValue>{abf.abf_structure || abf.facility_type || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Collateral Type</DataLabel><DataValue>{abf.collateral_type || abf.pool_type || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Fund Identifier</DataLabel><DataValue>{abf.fund_identifier || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Currency</DataLabel><DataValue>{abf.currency || abf.facility_currency || '-'}</DataValue></DataRow>
                      </DataSection>

                      <DataSection>
                        <SectionTitle>
                          <FileCheck size={16} />
                          Parties
                        </SectionTitle>
                        <DataRow><DataLabel>Obligor / Borrower</DataLabel><DataValue>{shown(abf.borrower_name || abf.obligor_name)}</DataValue></DataRow>
                        <DataRow><DataLabel>Seller</DataLabel><DataValue>{abf.seller_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Sponsor</DataLabel><DataValue>{abf.sponsor_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Originator</DataLabel><DataValue>{abf.originator_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Servicer</DataLabel><DataValue>{abf.servicer_name || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Admin Agent</DataLabel><DataValue>{abf.admin_agent_name || '-'}</DataValue></DataRow>
                      </DataSection>

                      <DataSection>
                        <SectionTitle>
                          <DollarSign size={16} />
                          Borrowing Base
                        </SectionTitle>
                        <DataRow><DataLabel>Total Commitment</DataLabel><DataValue>{fmtMoney(abf.total_commitment || abf.commitment_amount)}</DataValue></DataRow>
                        <DataRow><DataLabel>Drawn Amount</DataLabel><DataValue>{fmtMoney(abf.drawn_amount || abf.initial_drawn_amount)}</DataValue></DataRow>
                        <DataRow><DataLabel>Collateral Balance</DataLabel><DataValue>{fmtMoney(abf.collateral_balance)}</DataValue></DataRow>
                        <DataRow><DataLabel>Eligible Collateral</DataLabel><DataValue>{fmtMoney(abf.eligible_collateral_amount)}</DataValue></DataRow>
                        <DataRow><DataLabel>Ineligible Collateral</DataLabel><DataValue>{fmtMoney(abf.ineligible_collateral_amount)}</DataValue></DataRow>
                        <DataRow><DataLabel>Borrowing Base</DataLabel><DataValue>{fmtMoney(abf.borrowing_base)}</DataValue></DataRow>
                        <DataRow><DataLabel>Advance Rate</DataLabel><DataValue>{fmtPct(abf.advance_rate_pct)}</DataValue></DataRow>
                        <DataRow><DataLabel>Reserve</DataLabel><DataValue>{fmtMoney(abf.reserve_amount)}</DataValue></DataRow>
                      </DataSection>

                      <DataSection>
                        <SectionTitle>
                          <Percent size={16} />
                          Pricing & Dates
                        </SectionTitle>
                        <DataRow><DataLabel>Index</DataLabel><DataValue>{abf.financing_index || abf.base_rate || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Spread</DataLabel><DataValue>{abf.spread_bps ?? abf.margin_bps ?? '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Rate Floor</DataLabel><DataValue>{fmtPct(abf.rate_floor_pct || abf.floor_pct)}</DataValue></DataRow>
                        <DataRow><DataLabel>All-in Rate</DataLabel><DataValue>{fmtPct(abf.all_in_rate_pct)}</DataValue></DataRow>
                        <DataRow><DataLabel>Unused Fee</DataLabel><DataValue>{abf.unused_fee_bps != null ? `${abf.unused_fee_bps} bps` : '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Closing Date</DataLabel><DataValue>{abf.closing_date || abf.effective_date || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Maturity Date</DataLabel><DataValue>{abf.maturity_date || '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>Next BBC Date</DataLabel><DataValue>{abf.next_borrowing_base_date || '-'}</DataValue></DataRow>
                      </DataSection>

                      <DataSection>
                        <SectionTitle>
                          <Activity size={16} />
                          Pool Surveillance
                        </SectionTitle>
                        <DataRow><DataLabel>Collateral Assets</DataLabel><DataValue>{fmtNum(abf.asset_count || abf.num_collateral_assets)}</DataValue></DataRow>
                        <DataRow><DataLabel>Weighted Avg Yield</DataLabel><DataValue>{fmtPct(abf.weighted_avg_yield_pct)}</DataValue></DataRow>
                        <DataRow><DataLabel>Weighted Avg Life</DataLabel><DataValue>{abf.weighted_avg_life_years != null ? `${Number(abf.weighted_avg_life_years).toFixed(2)} yrs` : '-'}</DataValue></DataRow>
                        <DataRow><DataLabel>30+ Delinquency</DataLabel><DataValue>{fmtPct(abf.delinquency_30_pct || abf.delinquency_pct)}</DataValue></DataRow>
                        <DataRow><DataLabel>Default Rate</DataLabel><DataValue>{fmtPct(abf.default_rate_pct)}</DataValue></DataRow>
                        <DataRow><DataLabel>Net Loss Rate</DataLabel><DataValue>{fmtPct(abf.net_loss_rate_pct)}</DataValue></DataRow>
                        <DataRow><DataLabel>Excess Spread</DataLabel><DataValue>{fmtPct(abf.excess_spread_pct)}</DataValue></DataRow>
                        <DataRow><DataLabel>Mark</DataLabel><DataValue>{fmtPct(abf.mark_pct)}</DataValue></DataRow>
                      </DataSection>

                      {/* F-REG-034: negotiated deal terms must be visible to the
                          reviewer — these arrays drive the facility's eligibility
                          rules and covenant thresholds at booking. */}
                      {Array.isArray(abf.eligibility_criteria) && abf.eligibility_criteria.length > 0 && (
                        <DataSection>
                          <SectionTitle>
                            <FileCheck size={16} />
                            Eligibility Criteria (governs Borrowing Base)
                          </SectionTitle>
                          {abf.eligibility_criteria.map((r: any, i: number) => (
                            <DataRow key={`elig-${i}`}>
                              <DataLabel>{r.rule || r.field || `Criterion ${i + 1}`}</DataLabel>
                              <DataValue>{r.threshold || '-'}{r.field ? `  (${r.field})` : ''}</DataValue>
                            </DataRow>
                          ))}
                        </DataSection>
                      )}
                      {Array.isArray(abf.covenants) && abf.covenants.length > 0 && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <SectionTitle>
                            <Shield size={16} />
                            Covenants &amp; Performance Triggers
                          </SectionTitle>
                          {abf.covenants.map((c: any, i: number) => {
                            const threshold = c.threshold || '-';
                            const category = c.category || c.covenant_type
                              || (/warning|trigger/i.test(String(threshold)) ? 'Performance trigger' : 'Financial covenant');
                            return (
                              <CovenantCard key={`cov-${i}`} status={c.status || 'UNKNOWN'}>
                                <CovenantName>{c.test || c.covenant_name || `Covenant ${i + 1}`}</CovenantName>
                                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                  <CovenantDetail>
                                    Threshold: <strong>{threshold}</strong>
                                  </CovenantDetail>
                                  <CovenantDetail>Type: {category}</CovenantDetail>
                                  {(c.testing_frequency || c.test_frequency || c.measurement_frequency) && (
                                    <CovenantDetail>
                                      Test: {c.testing_frequency || c.test_frequency || c.measurement_frequency}
                                    </CovenantDetail>
                                  )}
                                  {c.measurement_period && <CovenantDetail>Period: {c.measurement_period}</CovenantDetail>}
                                  <CovenantDetail style={{ color: '#64748b', fontStyle: 'italic' }}>
                                    Live metrics available after first test period
                                  </CovenantDetail>
                                </div>
                                {(c.source_clause || c.page_reference || c.source_reference) && (
                                  <CovenantDetail style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #1e293b' }}>
                                    Source: {c.source_clause || c.page_reference || c.source_reference}
                                  </CovenantDetail>
                                )}
                              </CovenantCard>
                            );
                          })}
                        </div>
                      )}
                      {docType === 'ABF_CREDIT_AGREEMENT' && !(abf.eligibility_criteria?.length) && !(abf.covenants?.length) && (
                        <DataSection>
                          <SectionTitle>
                            <Activity size={16} />
                            Deal Terms
                          </SectionTitle>
                          <DataRow>
                            <DataLabel>Eligibility / Covenants</DataLabel>
                            <DataValue>
                              None extracted. Activation requires at least one eligibility rule
                              cited to a clause, so these are entered in contract review — the
                              platform never supplies a default as contract authority.
                            </DataValue>
                          </DataRow>
                        </DataSection>
                      )}
                      {/* F-S6-025: the schedules the reviewer is approving, each row with its page and clause. */}
                      {nodes.length > 0 && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <SectionTitle><Activity size={16} />Payment waterfall · {nodes.length} line{nodes.length === 1 ? '' : 's'}</SectionTitle>
                          <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr><th style={head}>Applies</th><th style={head}>Order</th><th style={head}>Line</th><th style={head}>Pays</th><th style={head}>Rate / cap</th><th style={head}>Lender class</th><th style={head}>Pays to</th><th style={head}>Source</th></tr></thead>
                            <tbody>{nodes.map((n, i) => <tr key={`wf-${i}`}>
                              <td style={cell}>{n.mode === 'BOTH' ? 'Both' : n.mode === 'PRE_TRIGGER' ? 'Normal' : n.mode === 'POST_TRIGGER' ? 'After trigger' : words(n.mode)}</td>
                              <td style={cell}>{n.priority_order ?? '-'}</td>
                              <td style={cell}><strong>{n.node_name || '-'}</strong></td>
                              <td style={cell}>{words(n.payment_type)}</td>
                              <td style={cell}>{n.rate_bps != null ? `${n.rate_bps} bps` : n.cap_amount != null ? `cap ${fmtMoney(n.cap_amount)}` : n.fixed_amount != null ? fmtMoney(n.fixed_amount) : '-'}</td>
                              <td style={cell}>{n.lender_class_key || '-'}</td>
                              <td style={cell}>{n.destination_account_key || '-'}</td>
                              <td style={src}>{loc(n)}</td>
                            </tr>)}</tbody>
                          </table></div>
                        </div>
                      )}
                      {classes.length > 0 && (
                        <DataSection>
                          <SectionTitle><Building2 size={16} />Lender classes</SectionTitle>
                          {classes.map((c, i) => <React.Fragment key={`cls-${i}`}>
                            <DataRow><DataLabel>{c.class_key || `Class ${i + 1}`}</DataLabel><DataValue>{c.class_name || '-'}</DataValue></DataRow>
                            <DataRow><DataLabel>Seniority · commitment</DataLabel><DataValue>{words(c.seniority)} · {fmtMoney(c.commitment_amount)}</DataValue></DataRow>
                            <DataRow><DataLabel>Pricing</DataLabel><DataValue>{c.rate_index || '-'}{c.spread_bps != null ? ` + ${c.spread_bps} bps` : ''}</DataValue></DataRow>
                            <DataRow><DataLabel>Source</DataLabel><DataValue style={{ fontWeight: 400, color: '#94a3b8' }}>{loc(c)}</DataValue></DataRow>
                          </React.Fragment>)}
                        </DataSection>
                      )}
                      {consequences.length > 0 && (
                        <DataSection>
                          <SectionTitle><Shield size={16} />Consequences</SectionTitle>
                          {consequences.map((c, i) => <React.Fragment key={`csq-${i}`}>
                            <DataRow><DataLabel>{c.consequence_key || `Consequence ${i + 1}`}</DataLabel><DataValue>{c.consequence_type === 'DRAW_BLOCK' ? 'Blocks further draws' : c.consequence_type === 'WATERFALL_MODE' ? 'Switches the waterfall to post-trigger' : words(c.consequence_type)}</DataValue></DataRow>
                            <DataRow><DataLabel>Acts on</DataLabel><DataValue>{words(c.target_type)}{c.target_key ? ` · ${c.target_key}` : ''}</DataValue></DataRow>
                            <DataRow><DataLabel>Triggered by</DataLabel><DataValue>{c.covenant_key || '-'}</DataValue></DataRow>
                            <DataRow><DataLabel>Source</DataLabel><DataValue style={{ fontWeight: 400, color: '#94a3b8' }}>{loc(c)}</DataValue></DataRow>
                          </React.Fragment>)}
                        </DataSection>
                      )}
                      {calendar && (
                        <DataSection>
                          <SectionTitle><Activity size={16} />Payment calendar</SectionTitle>
                          <DataRow><DataLabel>Determination day</DataLabel><DataValue>{calendar.determination_day ?? '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Payment day</DataLabel><DataValue>{calendar.payment_day ?? '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>If not a business day</DataLabel><DataValue>{words(calendar.business_day_convention)}</DataValue></DataRow>
                          <DataRow><DataLabel>Holidays</DataLabel><DataValue>{calendar.holiday_calendar === 'US_FEDERAL' ? 'U.S. federal' : words(calendar.holiday_calendar)}</DataValue></DataRow>
                          <DataRow><DataLabel>First period starts</DataLabel><DataValue>{calendar.first_period_start || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Source</DataLabel><DataValue style={{ fontWeight: 400, color: '#94a3b8' }}>{loc(calendar)}</DataValue></DataRow>
                        </DataSection>
                      )}
                      {accounts.length > 0 && (
                        <DataSection>
                          <SectionTitle><Building2 size={16} />Accounts</SectionTitle>
                          {accounts.map((a, i) => <React.Fragment key={`acc-${i}`}>
                            <DataRow><DataLabel>{a.account_key || `Account ${i + 1}`}</DataLabel><DataValue>{words(a.account_type)} · {a.bank_name || '-'}{a.masked_account_number ? ` · ${a.masked_account_number}` : ''}</DataValue></DataRow>
                            <DataRow><DataLabel>Source</DataLabel><DataValue style={{ fontWeight: 400, color: '#94a3b8' }}>{loc(a)}</DataValue></DataRow>
                          </React.Fragment>)}
                        </DataSection>
                      )}
                    </DataGrid>
                    </>
                  );
                })()}

                {!isReDoc && !isPeDoc && !isReDebtDoc && !isCloDoc && !isAbfDoc && (<>
                <DataGrid>
                  {/* Borrower Section */}
                  <DataSection>
                    <SectionTitle>
                      <Building2 size={16} />
                      Borrower
                    </SectionTitle>
                    <DataRow>
                      <DataLabel>Legal Name</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.borrower?.legal_name ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.borrower = { ...d.borrower, legal_name: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.borrower?.legal_name || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Jurisdiction</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.borrower?.jurisdiction ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.borrower = { ...d.borrower, jurisdiction: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.borrower?.jurisdiction || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Industry</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.borrower?.industry_sector ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.borrower = { ...d.borrower, industry_sector: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.borrower?.industry_sector || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Credit Rating</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.borrower?.credit_rating ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.borrower = { ...d.borrower, credit_rating: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.borrower?.credit_rating || '-'}</DataValue>
                      )}
                    </DataRow>
                  </DataSection>

                  {/* Loan Identifiers */}
                  <DataSection>
                    <SectionTitle>
                      <FileText size={16} />
                      Loan Identifiers
                    </SectionTitle>
                    <DataRow>
                      <DataLabel>Loan ID</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.loan_identifiers?.loan_id ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.loan_identifiers = { ...d.loan_identifiers, loan_id: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.loan_identifiers?.loan_id || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Facility Name</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.loan_identifiers?.facility_name ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.loan_identifiers = { ...d.loan_identifiers, facility_name: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.loan_identifiers?.facility_name || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>CUSIP</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.loan_identifiers?.cusip ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.loan_identifiers = { ...d.loan_identifiers, cusip: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.loan_identifiers?.cusip || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>ISIN</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.loan_identifiers?.isin ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.loan_identifiers = { ...d.loan_identifiers, isin: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.loan_identifiers?.isin || '-'}</DataValue>
                      )}
                    </DataRow>
                  </DataSection>

                  {(!!data.notice_event || isLoanNoticeDoc) && (
                    <DataSection>
                      <SectionTitle>
                        <Zap size={16} />
                        Notice Event
                      </SectionTitle>
                      <DataRow>
                        <DataLabel>Event Type</DataLabel>
                        {edit ? (
                          <EditableInput
                            value={data.notice_event?.event_type ?? ''}
                            onChange={(e) =>
                              updateExtractDraft((d) => {
                                d.notice_event = { ...d.notice_event, event_type: e.target.value };
                              })
                            }
                          />
                        ) : (
                          <DataValueHighlight>{data.notice_event?.event_type || '-'}</DataValueHighlight>
                        )}
                      </DataRow>
                      <DataRow>
                        <DataLabel>Event Amount</DataLabel>
                        {edit ? (
                          <EditableInput
                            value={data.notice_event?.event_amount != null ? String(data.notice_event.event_amount) : ''}
                            onChange={(e) =>
                              updateExtractDraft((d) => {
                                d.notice_event = {
                                  ...d.notice_event,
                                  event_amount: parseOptionalNumber(e.target.value)
                                };
                              })
                            }
                          />
                        ) : (
                          <DataValueHighlight>{formatCurrency(noticeEventAmount)}</DataValueHighlight>
                        )}
                      </DataRow>
                      <DataRow>
                        <DataLabel>Effective Date</DataLabel>
                        {edit ? (
                          <EditableInput
                            value={data.notice_event?.effective_date ?? ''}
                            onChange={(e) =>
                              updateExtractDraft((d) => {
                                d.notice_event = { ...d.notice_event, effective_date: e.target.value };
                              })
                            }
                          />
                        ) : (
                          <DataValue>{noticeEffectiveDate || '-'}</DataValue>
                        )}
                      </DataRow>
                      <DataRow>
                        <DataLabel>Notice Date</DataLabel>
                        {edit ? (
                          <EditableInput
                            value={data.notice_event?.notice_date ?? ''}
                            onChange={(e) =>
                              updateExtractDraft((d) => {
                                d.notice_event = { ...d.notice_event, notice_date: e.target.value };
                              })
                            }
                          />
                        ) : (
                          <DataValue>{data.notice_event?.notice_date || '-'}</DataValue>
                        )}
                      </DataRow>
                      <DataRow>
                        <DataLabel>Rationale</DataLabel>
                        {edit ? (
                          <EditableInput
                            value={data.notice_event?.rationale ?? ''}
                            onChange={(e) =>
                              updateExtractDraft((d) => {
                                d.notice_event = { ...d.notice_event, rationale: e.target.value };
                              })
                            }
                          />
                        ) : (
                          <DataValue>{data.notice_event?.rationale || '-'}</DataValue>
                        )}
                      </DataRow>
                    </DataSection>
                  )}

                  {(extractedDataModal.document_type === 'CAPITAL_CALL' || extractedDataModal.document_type === 'DISTRIBUTION_NOTICE') && (
                    <DataSection>
                      <SectionTitle>
                        <DollarSign size={16} />
                        Capital Activity
                      </SectionTitle>
                      <DataRow>
                        <DataLabel>Activity Type</DataLabel>
                        {edit ? (
                          <EditableInput
                            value={data.capital_activity?.activity_type ?? ''}
                            onChange={(e) =>
                              updateExtractDraft((d) => {
                                d.capital_activity = { ...d.capital_activity, activity_type: e.target.value };
                              })
                            }
                          />
                        ) : (
                          <DataValueHighlight>
                            {data.capital_activity?.activity_type || extractedDataModal.document_type || '-'}
                          </DataValueHighlight>
                        )}
                      </DataRow>
                      <DataRow>
                        <DataLabel>Fund Name</DataLabel>
                        {edit ? (
                          <EditableInput
                            value={data.capital_activity?.fund_name ?? ''}
                            onChange={(e) =>
                              updateExtractDraft((d) => {
                                d.capital_activity = { ...d.capital_activity, fund_name: e.target.value };
                              })
                            }
                          />
                        ) : (
                          <DataValue>{data.capital_activity?.fund_name || '-'}</DataValue>
                        )}
                      </DataRow>
                      <DataRow>
                        <DataLabel>Investor Amount</DataLabel>
                        {edit ? (
                          <EditableInput
                            value={data.capital_activity?.investor_amount != null ? String(data.capital_activity.investor_amount) : ''}
                            onChange={(e) =>
                              updateExtractDraft((d) => {
                                d.capital_activity = {
                                  ...d.capital_activity,
                                  investor_amount: parseOptionalNumber(e.target.value)
                                };
                              })
                            }
                          />
                        ) : (
                          <DataValue>{formatCurrency(data.capital_activity?.investor_amount)}</DataValue>
                        )}
                      </DataRow>
                      <DataRow>
                        <DataLabel>Due Date</DataLabel>
                        {edit ? (
                          <EditableInput
                            value={data.capital_activity?.due_date ?? ''}
                            onChange={(e) =>
                              updateExtractDraft((d) => {
                                d.capital_activity = { ...d.capital_activity, due_date: e.target.value };
                              })
                            }
                          />
                        ) : (
                          <DataValue>{data.capital_activity?.due_date || '-'}</DataValue>
                        )}
                      </DataRow>
                      <DataRow>
                        <DataLabel>Payment Date</DataLabel>
                        {edit ? (
                          <EditableInput
                            value={data.capital_activity?.payment_date ?? ''}
                            onChange={(e) =>
                              updateExtractDraft((d) => {
                                d.capital_activity = { ...d.capital_activity, payment_date: e.target.value };
                              })
                            }
                          />
                        ) : (
                          <DataValue>{data.capital_activity?.payment_date || '-'}</DataValue>
                        )}
                      </DataRow>
                      <DataRow>
                        <DataLabel>Commitment / Unfunded</DataLabel>
                        {edit ? (
                          <>
                            <EditableInput
                              style={{ width: '26%', minWidth: '72px' }}
                              placeholder="Commitment"
                              value={data.capital_activity?.commitment_amount != null ? String(data.capital_activity.commitment_amount) : ''}
                              onChange={(e) =>
                                updateExtractDraft((d) => {
                                  d.capital_activity = {
                                    ...d.capital_activity,
                                    commitment_amount: parseOptionalNumber(e.target.value)
                                  };
                                })
                              }
                            />
                            <EditableInput
                              style={{ width: '26%', minWidth: '72px' }}
                              placeholder="Unfunded"
                              value={data.capital_activity?.unfunded_commitment != null ? String(data.capital_activity.unfunded_commitment) : ''}
                              onChange={(e) =>
                                updateExtractDraft((d) => {
                                  d.capital_activity = {
                                    ...d.capital_activity,
                                    unfunded_commitment: parseOptionalNumber(e.target.value)
                                  };
                                })
                              }
                            />
                          </>
                        ) : (
                          <DataValue>
                            {`${formatCurrency(data.capital_activity?.commitment_amount)} / ${formatCurrency(data.capital_activity?.unfunded_commitment)}`}
                          </DataValue>
                        )}
                      </DataRow>
                      {extractedDataModal.document_type === 'DISTRIBUTION_NOTICE' && (
                        <DataRow>
                          <DataLabel>ROC / Gain / Carry</DataLabel>
                          {edit ? (
                            <>
                              <EditableInput
                                style={{ width: '26%', minWidth: '64px' }}
                                placeholder="ROC"
                                value={data.capital_activity?.return_of_capital != null ? String(data.capital_activity.return_of_capital) : ''}
                                onChange={(e) =>
                                  updateExtractDraft((d) => {
                                    d.capital_activity = {
                                      ...d.capital_activity,
                                      return_of_capital: parseOptionalNumber(e.target.value)
                                    };
                                  })
                                }
                              />
                              <EditableInput
                                style={{ width: '26%', minWidth: '64px' }}
                                placeholder="Gain"
                                value={data.capital_activity?.realized_gain != null ? String(data.capital_activity.realized_gain) : ''}
                                onChange={(e) =>
                                  updateExtractDraft((d) => {
                                    d.capital_activity = {
                                      ...d.capital_activity,
                                      realized_gain: parseOptionalNumber(e.target.value)
                                    };
                                  })
                                }
                              />
                              <EditableInput
                                style={{ width: '26%', minWidth: '64px' }}
                                placeholder="Carry"
                                value={data.capital_activity?.carried_interest != null ? String(data.capital_activity.carried_interest) : ''}
                                onChange={(e) =>
                                  updateExtractDraft((d) => {
                                    d.capital_activity = {
                                      ...d.capital_activity,
                                      carried_interest: parseOptionalNumber(e.target.value)
                                    };
                                  })
                                }
                              />
                            </>
                          ) : (
                            <DataValue>
                              {`${formatCurrency(data.capital_activity?.return_of_capital)} / ${formatCurrency(data.capital_activity?.realized_gain)} / ${formatCurrency(data.capital_activity?.carried_interest)}`}
                            </DataValue>
                          )}
                        </DataRow>
                      )}
                      <DataRow>
                        <DataLabel>Waterfall Tier</DataLabel>
                        {edit ? (
                          <EditableInput
                            value={data.capital_activity?.waterfall_tier ?? ''}
                            onChange={(e) =>
                              updateExtractDraft((d) => {
                                d.capital_activity = { ...d.capital_activity, waterfall_tier: e.target.value };
                              })
                            }
                          />
                        ) : (
                          <DataValue>{data.capital_activity?.waterfall_tier || '-'}</DataValue>
                        )}
                      </DataRow>
                    </DataSection>
                  )}

                  {/* Loan Terms */}
                  <DataSection>
                    <SectionTitle>
                      <DollarSign size={16} />
                      {isLoanNoticeDoc ? 'Facility Terms' : 'Loan Terms'}
                    </SectionTitle>
                    {isLoanNoticeDoc && (
                      <DataRow>
                        <DataLabel>Notice Event Amount</DataLabel>
                        <DataValueHighlight>{formatCurrency(noticeEventAmount)}</DataValueHighlight>
                      </DataRow>
                    )}
                    <DataRow>
                      <DataLabel>{isLoanNoticeDoc ? 'Stated Current / Post-Event Principal' : 'Current Principal'}</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.loan_terms?.current_principal != null ? String(data.loan_terms.current_principal) : ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.loan_terms = { ...d.loan_terms, current_principal: parseOptionalNumber(e.target.value) };
                            })
                          }
                        />
                      ) : (
                        <DataValueHighlight>
                          {formatCurrency(data.loan_terms?.current_principal)}
                        </DataValueHighlight>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Original Principal</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.loan_terms?.original_principal != null ? String(data.loan_terms.original_principal) : ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.loan_terms = { ...d.loan_terms, original_principal: parseOptionalNumber(e.target.value) };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{formatCurrency(data.loan_terms?.original_principal)}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Currency</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.loan_terms?.currency ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.loan_terms = { ...d.loan_terms, currency: e.target.value || 'USD' };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.loan_terms?.currency || 'USD'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Loan Status</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.loan_terms?.loan_status ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.loan_terms = { ...d.loan_terms, loan_status: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.loan_terms?.loan_status || '-'}</DataValue>
                      )}
                    </DataRow>
                  </DataSection>

                  {/* Interest Rate */}
                  <DataSection>
                    <SectionTitle>
                      <Percent size={16} />
                      Interest Rate
                    </SectionTitle>
                    <DataRow>
                      <DataLabel>All-In Rate (%)</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.interest_rate?.all_in_rate_pct != null ? String(data.interest_rate.all_in_rate_pct) : ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.interest_rate = { ...d.interest_rate, all_in_rate_pct: parseOptionalNumber(e.target.value) };
                            })
                          }
                        />
                      ) : (
                        <DataValueHighlight>
                          {formatPercent(data.interest_rate?.all_in_rate_pct)}
                        </DataValueHighlight>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Base Type</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.interest_rate?.base_type ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.interest_rate = { ...d.interest_rate, base_type: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.interest_rate?.base_type || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Base Rate (%)</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.interest_rate?.base_rate != null ? String(data.interest_rate.base_rate) : ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.interest_rate = { ...d.interest_rate, base_rate: parseOptionalNumber(e.target.value) };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{formatPercent(data.interest_rate?.base_rate)}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Spread (bps)</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.interest_rate?.spread_bps != null ? String(data.interest_rate.spread_bps) : ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.interest_rate = { ...d.interest_rate, spread_bps: parseOptionalNumber(e.target.value) };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.interest_rate?.spread_bps ? `${data.interest_rate.spread_bps} bps` : '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>CSA (bps)</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.interest_rate?.csa_bps != null ? String(data.interest_rate.csa_bps) : ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.interest_rate = { ...d.interest_rate, csa_bps: parseOptionalNumber(e.target.value) };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.interest_rate?.csa_bps != null ? `${data.interest_rate.csa_bps} bps` : '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Floor (%)</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.interest_rate?.floor_pct != null ? String(data.interest_rate.floor_pct) : ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.interest_rate = { ...d.interest_rate, floor_pct: parseOptionalNumber(e.target.value) };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{formatPercent(data.interest_rate?.floor_pct)}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Step-Down Spread (bps)</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.interest_rate?.step_down_spread_bps != null ? String(data.interest_rate.step_down_spread_bps) : ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.interest_rate = { ...d.interest_rate, step_down_spread_bps: parseOptionalNumber(e.target.value) };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.interest_rate?.step_down_spread_bps != null ? `${data.interest_rate.step_down_spread_bps} bps` : '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Step-Down Trigger</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.interest_rate?.step_down_trigger_leverage != null ? String(data.interest_rate.step_down_trigger_leverage) : ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.interest_rate = { ...d.interest_rate, step_down_trigger_leverage: parseOptionalNumber(e.target.value) };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.interest_rate?.step_down_trigger_leverage != null ? `< ${data.interest_rate.step_down_trigger_leverage.toFixed(2)}x leverage` : '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>OID Price</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.interest_rate?.oid_price != null ? String(data.interest_rate.oid_price) : ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.interest_rate = { ...d.interest_rate, oid_price: parseOptionalNumber(e.target.value) };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.interest_rate?.oid_price != null ? data.interest_rate.oid_price.toFixed(2) : '-'}</DataValue>
                      )}
                    </DataRow>
                  </DataSection>

                  {/* Ratings & Sponsor */}
                  <DataSection>
                    <SectionTitle>
                      <Shield size={16} />
                      Ratings & Sponsor
                    </SectionTitle>
                    <DataRow>
                      <DataLabel>S&P</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.borrower?.sp_rating ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.borrower = { ...d.borrower, sp_rating: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.borrower?.sp_rating || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Moody's</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.borrower?.moodys_rating ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.borrower = { ...d.borrower, moodys_rating: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.borrower?.moodys_rating || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Fitch</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.borrower?.fitch_rating ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.borrower = { ...d.borrower, fitch_rating: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.borrower?.fitch_rating || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Sector</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.borrower?.industry_sector ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.borrower = { ...d.borrower, industry_sector: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.borrower?.industry_sector || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Sub-Industry</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.borrower?.sub_industry ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.borrower = { ...d.borrower, sub_industry: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.borrower?.sub_industry || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Sponsor</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.sponsor_info?.sponsor_name ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.sponsor_info = { ...d.sponsor_info, sponsor_name: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.sponsor_info?.sponsor_name || '-'}</DataValue>
                      )}
                    </DataRow>
                  </DataSection>

                  {/* Payment Terms */}
                  <DataSection>
                    <SectionTitle>
                      <Calendar size={16} />
                      Payment Terms
                    </SectionTitle>
                    <DataRow>
                      <DataLabel>Payment Type</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.payment_terms?.payment_type ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.payment_terms = { ...d.payment_terms, payment_type: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.payment_terms?.payment_type || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Frequency</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.payment_terms?.payment_frequency ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.payment_terms = { ...d.payment_terms, payment_frequency: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.payment_terms?.payment_frequency || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Day Count</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.payment_terms?.day_count_convention ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.payment_terms = { ...d.payment_terms, day_count_convention: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.payment_terms?.day_count_convention || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Next Payment</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.payment_terms?.next_payment_date ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.payment_terms = { ...d.payment_terms, next_payment_date: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.payment_terms?.next_payment_date || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Next Amount</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.payment_terms?.next_payment_amount != null ? String(data.payment_terms.next_payment_amount) : ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.payment_terms = {
                                ...d.payment_terms,
                                next_payment_amount: parseOptionalNumber(e.target.value)
                              };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{formatCurrency(data.payment_terms?.next_payment_amount)}</DataValue>
                      )}
                    </DataRow>
                  </DataSection>

                  {/* Key Dates */}
                  <DataSection>
                    <SectionTitle>
                      <Calendar size={16} />
                      Key Dates
                    </SectionTitle>
                    <DataRow>
                      <DataLabel>Document Date</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.key_dates?.document_date ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.key_dates = { ...d.key_dates, document_date: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.key_dates?.document_date || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Origination</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.loan_terms?.origination_date ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.loan_terms = { ...d.loan_terms, origination_date: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.loan_terms?.origination_date || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Maturity</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.loan_terms?.maturity_date ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.loan_terms = { ...d.loan_terms, maturity_date: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.loan_terms?.maturity_date || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Interest Period Start</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.key_dates?.interest_period_start ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.key_dates = { ...d.key_dates, interest_period_start: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.key_dates?.interest_period_start || '-'}</DataValue>
                      )}
                    </DataRow>
                    <DataRow>
                      <DataLabel>Interest Period End</DataLabel>
                      {edit ? (
                        <EditableInput
                          value={data.key_dates?.interest_period_end ?? ''}
                          onChange={(e) =>
                            updateExtractDraft((d) => {
                              d.key_dates = { ...d.key_dates, interest_period_end: e.target.value };
                            })
                          }
                        />
                      ) : (
                        <DataValue>{data.key_dates?.interest_period_end || '-'}</DataValue>
                      )}
                    </DataRow>
                  </DataSection>
                </DataGrid>

                {/* LL deal facilities: one reviewed row per legally distinct tranche. */}
                {((data.facilities && data.facilities.length > 0)
                  || extractedDataModal.document_type === 'SYNDICATED_TERM_SHEET') && (
                  <div style={{ marginTop: '1rem' }}>
                    <SectionTitle><Building2 size={16} /> Deal Facilities / Tranches</SectionTitle>
                    <DataSection style={{ marginBottom: '0.75rem' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0 1rem' }}>
                        <DataRow><DataLabel>Deal Code</DataLabel>{edit
                          ? <EditableInput value={data.deal_code || ''} onChange={(e) => updateExtractDraft(draft => { draft.deal_code = e.target.value || undefined; })} />
                          : <DataValue>{data.deal_code || '-'}</DataValue>}</DataRow>
                        <DataRow><DataLabel>Deal Name</DataLabel>{edit
                          ? <EditableInput value={data.deal_name || ''} onChange={(e) => updateExtractDraft(draft => { draft.deal_name = e.target.value || undefined; })} />
                          : <DataValue>{data.deal_name || '-'}</DataValue>}</DataRow>
                        <DataRow><DataLabel>Governing Law</DataLabel>{edit
                          ? <EditableInput value={data.governing_law || ''} onChange={(e) => updateExtractDraft(draft => { draft.governing_law = e.target.value || undefined; })} />
                          : <DataValue>{data.governing_law || '-'}</DataValue>}</DataRow>
                        <DataRow>
                          <DataLabel>Cov-Lite Package</DataLabel>
                          {edit ? (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#e2e8f0', fontSize: '0.8rem' }}>
                              <input type="checkbox" checked={Boolean(data.cov_lite_acknowledged)} onChange={(e) => updateExtractDraft(draft => { draft.cov_lite_acknowledged = e.target.checked; })} />
                              No maintenance covenant package
                            </label>
                          ) : <DataValue>{data.cov_lite_acknowledged ? 'Explicitly acknowledged' : 'No'}</DataValue>}
                        </DataRow>
                      </div>
                    </DataSection>
                    {(!data.facilities || data.facilities.length === 0) && (
                      <div style={{ color: '#fbbf24', fontSize: '0.82rem', marginBottom: 10 }}>
                        No facility rows were extracted. Add and review every tranche before approval.
                      </div>
                    )}
                    {(data.facilities || []).map((facility, idx) => {
                      const updateFacility = (field: string, value: string | number | undefined) =>
                        updateExtractDraft((draft) => {
                          const rows = [...(draft.facilities || [])];
                          rows[idx] = { ...rows[idx], [field]: value };
                          draft.facilities = rows;
                        });
                      const textField = (label: string, field: keyof typeof facility, type = 'text') => (
                        <DataRow>
                          <DataLabel>{label}</DataLabel>
                          {edit ? (
                            <EditableInput
                              type={type}
                              value={facility[field] == null ? '' : String(facility[field])}
                              onChange={(e) => updateFacility(field, type === 'number' && e.target.value !== '' ? Number(e.target.value) : e.target.value || undefined)}
                            />
                          ) : <DataValue>{facility[field] == null || facility[field] === '' ? '-' : String(facility[field])}</DataValue>}
                        </DataRow>
                      );
                      return (
                        <DataSection key={`${facility.tranche_code || 'tranche'}-${idx}`} style={{ marginBottom: '0.75rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 6 }}>
                            <strong style={{ color: '#f8fafc' }}>{facility.tranche_code || `Facility ${idx + 1}`}</strong>
                            {edit && (
                              <CloseButton title="Remove facility" onClick={() => updateExtractDraft((draft) => {
                                draft.facilities = (draft.facilities || []).filter((_, rowIndex) => rowIndex !== idx);
                              })}><Trash2 size={16} /></CloseButton>
                            )}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0 1rem' }}>
                            {textField('Tranche Code', 'tranche_code')}
                            {textField('Facility Type', 'facility_type')}
                            {textField('Facility Name', 'facility_name')}
                            {textField('Loan ID', 'loan_id')}
                            {textField('CUSIP', 'cusip')}
                            {textField('ISIN', 'isin')}
                            {textField('LSTA ID', 'lsta_id')}
                            {textField('LoanX ID', 'loanx_id')}
                            {textField('WSO Loan ID', 'wso_loan_id')}
                            {textField('Original Facility Size', 'original_size', 'number')}
                            {textField('Currency', 'currency')}
                            {textField('Base Index', 'base_rate')}
                            {textField('Spread (bps)', 'spread_bps', 'number')}
                            {textField('CSA (bps)', 'csa_bps', 'number')}
                            {textField('Floor (%)', 'floor_rate', 'number')}
                            {textField('Floor Basis', 'floor_basis')}
                            {textField('OID / Issue Price', 'oid_price', 'number')}
                            {textField('Commitment Fee (bps)', 'commitment_fee_bps', 'number')}
                            {textField('Ticking Fee (bps)', 'ticking_fee_bps', 'number')}
                            {textField('Closing Date', 'closing_date', 'date')}
                            {textField('Maturity Date', 'maturity_date', 'date')}
                            {textField('Availability End', 'availability_end_date', 'date')}
                            {textField('Day Count', 'day_count_convention')}
                            {textField('Payment Frequency', 'payment_frequency')}
                            {textField('Annual Amortization (%)', 'annual_amortization_pct', 'number')}
                            {textField('Quarterly Amortization (%)', 'quarterly_amortization_pct', 'number')}
                            {textField('Amortization Starts', 'amortization_start_date', 'date')}
                            {textField('Amortization Frequency', 'amortization_frequency')}
                            <DataRow><DataLabel>Balloon at Maturity</DataLabel><DataValue>{facility.balloon_at_maturity ? 'Yes' : 'No'}</DataValue></DataRow>
                            {textField('Page / Section', 'page_reference')}
                          </div>
                          {facility.evidence_text && <CovenantDetail style={{ marginTop: 8 }}>Evidence: {facility.evidence_text}</CovenantDetail>}
                        </DataSection>
                      );
                    })}
                    {edit && (
                      <ButtonSecondary onClick={() => updateExtractDraft((draft) => {
                        draft.facilities = [...(draft.facilities || []), {
                          tranche_code: '', facility_type: 'TERM_LOAN_B', currency: 'USD',
                          floor_basis: 'ADJUSTED_INDEX',
                        }];
                      })}><Plus size={15} /> Add Facility</ButtonSecondary>
                    )}
                  </div>
                )}

                {/* Financial Covenants */}
                {((data.financial_covenants && data.financial_covenants.length > 0)
                  || (edit && extractedDataModal.document_type === 'SYNDICATED_TERM_SHEET')) && (
                  <div style={{ marginTop: '1rem' }}>
                    <SectionTitle>
                      <Shield size={16} />
                      Financial Covenants
                    </SectionTitle>
                    {edit ? (
                      <>
                        {(data.financial_covenants || []).map((covenant, idx) => {
                          const updateCovenant = (field: string, value: string | number | undefined) =>
                            updateExtractDraft(draft => {
                              const rows = [...(draft.financial_covenants || [])];
                              rows[idx] = { ...rows[idx], [field]: value };
                              draft.financial_covenants = rows;
                            });
                          return (
                            <DataSection key={`covenant-edit-${idx}`} style={{ marginBottom: '0.75rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 6 }}>
                                <strong style={{ color: '#f8fafc' }}>{covenant.covenant_name || `Covenant ${idx + 1}`}</strong>
                                <CloseButton title="Remove covenant" onClick={() => updateExtractDraft(draft => {
                                  draft.financial_covenants = (draft.financial_covenants || []).filter((_, rowIndex) => rowIndex !== idx);
                                })}><Trash2 size={16} /></CloseButton>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0 1rem' }}>
                                <DataRow><DataLabel>Name</DataLabel><EditableInput value={covenant.covenant_name || ''} onChange={e => updateCovenant('covenant_name', e.target.value || undefined)} /></DataRow>
                                <DataRow><DataLabel>Type</DataLabel><EditableInput value={covenant.covenant_type || ''} onChange={e => updateCovenant('covenant_type', e.target.value || undefined)} placeholder="FINANCIAL / NEGATIVE" /></DataRow>
                                <DataRow><DataLabel>Category</DataLabel><EditableInput value={covenant.covenant_category || ''} onChange={e => updateCovenant('covenant_category', e.target.value || undefined)} placeholder="LEVERAGE / COVERAGE" /></DataRow>
                                <DataRow><DataLabel>Metric</DataLabel><EditableInput value={covenant.metric_type || ''} onChange={e => updateCovenant('metric_type', e.target.value || undefined)} placeholder="TOTAL_LEVERAGE" /></DataRow>
                                <DataRow><DataLabel>Direction</DataLabel><EditableInput value={covenant.covenant_direction || ''} onChange={e => updateCovenant('covenant_direction', e.target.value || undefined)} placeholder="MAX / MIN" /></DataRow>
                                <DataRow><DataLabel>Threshold</DataLabel><EditableInput type="number" value={covenant.threshold_value == null ? '' : String(covenant.threshold_value)} onChange={e => updateCovenant('threshold_value', e.target.value === '' ? undefined : Number(e.target.value))} /></DataRow>
                                <DataRow><DataLabel>Frequency</DataLabel><EditableInput value={covenant.measurement_frequency || covenant.testing_frequency || covenant.test_frequency || ''} onChange={e => updateCovenant('measurement_frequency', e.target.value || undefined)} placeholder="QUARTERLY" /></DataRow>
                                <DataRow><DataLabel>Measurement Period</DataLabel><EditableInput value={covenant.measurement_period || ''} onChange={e => updateCovenant('measurement_period', e.target.value || undefined)} placeholder="LTM" /></DataRow>
                              </div>
                            </DataSection>
                          );
                        })}
                        <ButtonSecondary onClick={() => updateExtractDraft(draft => {
                          draft.cov_lite_acknowledged = false;
                          draft.financial_covenants = [...(draft.financial_covenants || []), {
                            covenant_name: '', covenant_type: 'FINANCIAL', covenant_category: 'LEVERAGE',
                            metric_type: 'TOTAL_LEVERAGE', covenant_direction: 'MAX',
                            measurement_frequency: 'QUARTERLY', measurement_period: 'LTM',
                          }];
                        })}><Plus size={15} /> Add Covenant</ButtonSecondary>
                      </>
                    ) : (data.financial_covenants || []).map((covenant, idx) => {
                      const threshold = covenant.threshold_value || covenant.threshold || '-';
                      const status = covenant.current_compliance_status || covenant.status || null;
                      const testFreq = covenant.testing_frequency || covenant.test_frequency || null;
                      const currentVal = covenant.current_value != null ? Number(covenant.current_value).toFixed(2) : null;
                      // Live metrics (current value, compliance status) are not available at origination.
                      // Only show them when the extraction actually returned a value.
                      const hasLiveData = currentVal !== null || status !== null;
                      return (
                      <CovenantCard key={idx} status={status || 'UNKNOWN'}>
                        <CovenantName>{covenant.covenant_name || 'Unknown Covenant'}</CovenantName>
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                          <CovenantDetail>
                            Threshold: <strong>{threshold}</strong>
                          </CovenantDetail>
                          {hasLiveData ? (
                            <>
                              <CovenantDetail>
                                Current: <strong>{currentVal ?? '-'}</strong>
                              </CovenantDetail>
                              <CovenantDetail>
                                Status: <strong style={{
                                  color: status === 'COMPLIANT' ? '#22c55e' :
                                         status === 'BREACH' ? '#ef4444' : '#eab308'
                                }}>{status ?? '-'}</strong>
                              </CovenantDetail>
                            </>
                          ) : (
                            <CovenantDetail style={{ color: '#64748b', fontStyle: 'italic' }}>
                              Live metrics available after first test period
                            </CovenantDetail>
                          )}
                          {testFreq && (
                            <CovenantDetail>Test: {testFreq}</CovenantDetail>
                          )}
                          {covenant.measurement_period && (
                            <CovenantDetail>Period: {covenant.measurement_period}</CovenantDetail>
                          )}
                        </div>
                        {covenant.step_schedule && covenant.step_schedule.length > 0 && (
                          <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #1e293b' }}>
                            <CovenantDetail style={{ color: '#94a3b8', marginBottom: '0.25rem' }}>
                              <strong>Step-Down Schedule:</strong>
                            </CovenantDetail>
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                              {covenant.step_schedule.map((step, sIdx) => (
                                <span key={sIdx} style={{ color: '#cbd5e1' }}>
                                  {step.date ?? '-'} → <strong>{step.value != null ? Number(step.value).toFixed(2) : '-'}</strong>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {covenant.has_equity_cure && (
                          <CovenantDetail style={{ marginTop: '0.5rem', color: '#60a5fa' }}>
                            Equity cure: up to {covenant.max_equity_cures ?? '?'} cures
                            {covenant.equity_cure_amount_cap != null
                              ? ` (cap ${formatCurrency(covenant.equity_cure_amount_cap)})`
                              : ''}
                          </CovenantDetail>
                        )}
                        {covenant.cure_period_days != null && (
                          <CovenantDetail style={{ marginTop: '0.5rem', color: '#94a3b8' }}>
                            Contractual cure period: {covenant.cure_period_days} days
                          </CovenantDetail>
                        )}
                      </CovenantCard>
                      );
                    })}
                  </div>
                )}

                {extractedDataModal.document_type === 'LSTA_TRADE_CONFIRM' && data.trade_settlement_details && (() => {
                  const trade = data.trade_settlement_details;
                  return (
                    <div style={{ marginTop: '1rem' }}>
                      <SectionTitle><DollarSign size={16} /> Trade Settlement Economics</SectionTitle>
                      <DataSection>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0 1rem' }}>
                          <DataRow><DataLabel>Buyer / Assignee</DataLabel><DataValue>{trade.assignee || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Seller / Assignor</DataLabel><DataValue>{trade.assignor || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Trade Type</DataLabel><DataValue>{trade.trade_type || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Par Amount</DataLabel><DataValue>{fmtMoney(trade.par_amount)}</DataValue></DataRow>
                          <DataRow><DataLabel>Transfer Price</DataLabel><DataValue>{trade.transfer_price == null ? '-' : `${trade.transfer_price}%`}</DataValue></DataRow>
                          <DataRow><DataLabel>Trade Date</DataLabel><DataValue>{trade.trade_date || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Settlement Date</DataLabel><DataValue>{trade.settlement_date || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Settlement Convention</DataLabel><DataValue>{trade.settlement_convention || '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Principal Consideration</DataLabel><DataValue>{fmtMoney(trade.principal_consideration)}</DataValue></DataRow>
                          <DataRow><DataLabel>Accrued Interest</DataLabel><DataValue>{fmtMoney(trade.accrued_interest)}</DataValue></DataRow>
                          <DataRow><DataLabel>Accrued Days</DataLabel><DataValue>{trade.accrued_days ?? '-'}</DataValue></DataRow>
                          <DataRow><DataLabel>Assignment Fee</DataLabel><DataValue>{fmtMoney(trade.assignment_fee)}</DataValue></DataRow>
                          <DataRow><DataLabel>Total Settlement</DataLabel><DataValue>{fmtMoney(trade.total_settlement_amount)}</DataValue></DataRow>
                        </div>
                        {trade.lsta_standard_terms && <CovenantDetail style={{ marginTop: 8 }}>{trade.lsta_standard_terms}</CovenantDetail>}
                      </DataSection>
                    </div>
                  );
                })()}

                {/* Lender Register — syndicate composition (admin agent + lenders) */}
                {data.lender_register && data.lender_register.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <SectionTitle>
                      <Building2 size={16} />
                      Lender Register / Syndicate
                    </SectionTitle>
                    <div style={{
                      border: '1px solid #1e293b',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      background: '#0f172a',
                    }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1.2fr 1fr 0.8fr',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: '#1e293b',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>
                        <div>Lender</div>
                        <div>Role</div>
                        <div style={{ textAlign: 'right' }}>Commitment</div>
                        <div style={{ textAlign: 'right' }}>Pro-Rata</div>
                      </div>
                      {data.lender_register.map((lender, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1.2fr 1fr 0.8fr',
                            gap: '0.5rem',
                            padding: '0.5rem 0.75rem',
                            borderTop: idx > 0 ? '1px solid #1e293b' : 'none',
                            fontSize: '0.85rem',
                            color: '#e2e8f0',
                          }}
                        >
                          <div style={{ fontWeight: 600 }}>{lender.lender_name || '-'}</div>
                          <div style={{
                            color: lender.lender_role === 'ADMIN_AGENT' ? '#facc15' :
                                   lender.lender_role === 'LEAD_ARRANGER' ? '#60a5fa' :
                                   '#94a3b8',
                          }}>
                            {lender.lender_role || 'LENDER'}
                          </div>
                          <div style={{ textAlign: 'right' }}>{formatCurrency(lender.commitment_amount)}</div>
                          <div style={{ textAlign: 'right' }}>
                            {lender.pro_rata_pct != null ? `${Number(lender.pro_rata_pct).toFixed(2)}%` : '-'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* PIK Toggle */}
                {data.pik_toggle?.has_pik_option && (
                  <DataSection style={{ marginTop: '1rem' }}>
                    <SectionTitle>
                      <TrendingUp size={16} />
                      PIK Toggle
                    </SectionTitle>
                    <DataRow>
                      <DataLabel>PIK Option</DataLabel>
                      <DataValue>{data.pik_toggle.has_pik_option ? 'Yes' : 'No'}</DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>Current Election</DataLabel>
                      <DataValue>{data.pik_toggle.current_election || '-'}</DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>Cash %</DataLabel>
                      <DataValue>{data.pik_toggle.cash_percentage || '-'}%</DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>PIK %</DataLabel>
                      <DataValue>{data.pik_toggle.pik_percentage || '-'}%</DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>PIK Premium</DataLabel>
                      <DataValue>{data.pik_toggle.pik_premium_bps || '-'} bps</DataValue>
                    </DataRow>
                  </DataSection>
                )}
                </>)}
              </div>
                );
              })()
            ) : modalTab === 'workspace' ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {extractedDataModal.linked_loan_record_id ? (
                  facilityWorkspaceLoading ? (
                    <div className="py-8 text-sm text-slate-400">Loading linked facility workspace...</div>
                  ) : facilityWorkspace ? (
                    <div className="space-y-4">
                      <DataSection>
                        <SectionTitle>
                          <Building2 size={16} />
                          Linked Facility
                        </SectionTitle>
                        <DataRow>
                          <DataLabel>Loan ID</DataLabel>
                          <DataValue>{facilityWorkspace.loanId}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Borrower</DataLabel>
                          <DataValue>{facilityWorkspace.borrowerName}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Security</DataLabel>
                          <DataValue>{facilityWorkspace.securityName || '-'}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Facility Capacity (Reference)</DataLabel>
                          <DataValue>{facilityWorkspace.currentCapacity == null
                            ? 'Awaiting evidence'
                            : formatCurrency(facilityWorkspace.currentCapacity)}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Global Outstanding / Availability (Reference)</DataLabel>
                          <DataValue>{`${facilityWorkspace.globalOutstanding == null
                            ? 'Awaiting evidence'
                            : formatCurrency(facilityWorkspace.globalOutstanding)} / ${facilityWorkspace.globalAvailability == null
                            ? 'Awaiting evidence'
                            : formatCurrency(facilityWorkspace.globalAvailability)}`}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Portfolio Drawn / Unfunded (IBOR)</DataLabel>
                          <DataValue>{`${formatCurrency(facilityWorkspace.totalDrawnAcrossPortfolios)} / ${formatCurrency(facilityWorkspace.totalUnfundedAcrossPortfolios)}`}</DataValue>
                        </DataRow>
                      </DataSection>

                      <DataSection>
                        <SectionTitle>
                          <Activity size={16} />
                          Recent Applied Events
                        </SectionTitle>
                        {facilityWorkspace.recentEvents.length > 0 ? facilityWorkspace.recentEvents.map((event) => (
                          <DataRow key={event.eventId}>
                            <DataLabel>{`${event.eventType} · ${event.eventDate}`}</DataLabel>
                            <DataValue>{`${formatCurrency(event.eventAmount)} · ${event.status}`}</DataValue>
                          </DataRow>
                        )) : (
                          <DataRow>
                            <DataLabel>No events</DataLabel>
                            <DataValue>-</DataValue>
                          </DataRow>
                        )}
                      </DataSection>
                    </div>
                  ) : (
                    <div className="py-8 text-sm text-slate-500">No linked facility workspace is available for this document yet.</div>
                  )
                ) : extractedDataModal.linked_commitment_id ? (
                  commitmentWorkspaceLoading ? (
                    <div className="py-8 text-sm text-slate-400">Loading linked commitment workspace...</div>
                  ) : commitmentWorkspace ? (
                    <div className="space-y-4">
                      <DataSection>
                        <SectionTitle>
                          <Building2 size={16} />
                          Linked Commitment
                        </SectionTitle>
                        <DataRow>
                          <DataLabel>Fund</DataLabel>
                          <DataValue>{commitmentWorkspace.fundName || '-'}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Portfolio</DataLabel>
                          <DataValue>{commitmentWorkspace.portfolioCode || commitmentWorkspace.portfolioName || '-'}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Commitment</DataLabel>
                          <DataValue>{formatCurrency(commitmentWorkspace.commitmentAmount)}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Called / Unfunded</DataLabel>
                          <DataValue>{`${formatCurrency(commitmentWorkspace.calledAmount)} / ${formatCurrency(commitmentWorkspace.unfundedCommitment)}`}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Distributed / Recallable</DataLabel>
                          <DataValue>{`${formatCurrency(commitmentWorkspace.distributedAmount)} / ${formatCurrency(commitmentWorkspace.recallableAmount)}`}</DataValue>
                        </DataRow>
                      </DataSection>

                      <DataSection>
                        <SectionTitle>
                          <Activity size={16} />
                          Recent Commitment Events
                        </SectionTitle>
                        {commitmentWorkspace.recentEvents.length > 0 ? commitmentWorkspace.recentEvents.map((event) => (
                          <DataRow key={event.eventId}>
                            <DataLabel>{`${event.eventType} · ${event.eventDate || '-'}`}</DataLabel>
                            <DataValue>{`${formatCurrency(event.eventAmount)} · ${event.status}`}</DataValue>
                          </DataRow>
                        )) : (
                          <DataRow>
                            <DataLabel>No events</DataLabel>
                            <DataValue>-</DataValue>
                          </DataRow>
                        )}
                      </DataSection>
                    </div>
                  ) : (
                    <div className="py-8 text-sm text-slate-500">No linked commitment workspace is available for this document yet.</div>
                  )
                ) : (
                  <div className="py-8 text-sm text-slate-500">No linked workspace is available for this document yet.</div>
                )}
              </div>
            ) : (
              <CodeBlock style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {extractedDataModal.raw_output || 'No raw output available'}
              </CodeBlock>
            )}

            {/* Approval Actions */}
            {extractedDataModal.approval_status === 'PENDING_REVIEW' && (
              <div style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '1rem' }}>
                <FormGroup>
                  <Label>
                    {extractedDataModal.interest_verification?.status === 'FAIL'
                      ? 'Justification (required for Override & Post; recommended for rejection)'
                      : 'Comments (required for rejection)'}
                  </Label>
                  <TextArea
                    value={approvalComments}
                    onChange={(e) => setApprovalComments(e.target.value)}
                    placeholder={extractedDataModal.interest_verification?.status === 'FAIL'
                      ? 'Why is the discrepancy acceptable? (e.g. "Agent bank confirmed rounding to nearest dollar; mismatch < $50; accepted per credit policy")'
                      : 'Add any comments or notes about this review...'}
                  />
                </FormGroup>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <ButtonDanger onClick={handleReject} disabled={isApproving}>
                    <Ban size={16} />
                    Reject
                  </ButtonDanger>
                  {extractedDataModal.interest_verification?.status === 'FAIL' ? (
                    <ButtonPrimary
                      onClick={() => handleApprove(true)}
                      disabled={isApproving || !approvalComments.trim()}
                      title={!approvalComments.trim()
                        ? 'Enter a justification reason above before overriding'
                        : 'Accept the verification discrepancy with a stamped audit reason and book the payment'}
                      style={{ background: '#b91c1c', borderColor: '#dc2626' }}
                    >
                      <AlertTriangle size={16} />
                      Override &amp; Post
                    </ButtonPrimary>
                  ) : (
                    <ButtonPrimary data-testid="doc-job-approve" onClick={() => handleApprove(false)} disabled={isApproving}>
                      <Check size={16} />
                      Approve
                    </ButtonPrimary>
                  )}
                </div>
              </div>
            )}

            {/* Export Actions (for approved data) */}
            {extractedDataModal.approval_status === 'APPROVED' && (
              <div style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '1rem' }}>
                {['ABF_CREDIT_AGREEMENT', 'ABF_AGREEMENT', 'ABF_AMENDMENT'].includes(extractedDataModal.document_type || '') ? (
                  // N-S6-010: an ABF contract document books through Import to OMS; there is no export step.
                  <div style={{ color: '#94a3b8', fontSize: '0.8rem', textAlign: 'right' }}>
                    Approved. Next: close this and click Import to OMS in the pipeline row to stage the contract version.
                  </div>
                ) : (
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <ButtonSecondary onClick={handleExport} disabled={isApproving}>
                    <Send size={16} />
                    Export to Aladdin & Snowflake
                  </ButtonSecondary>
                </div>
                )}
              </div>
            )}
          </LargeModal>
        </Modal>
      )}
    </Container>
  );
};

export default DocumentProcessingScreen;
